// ── CCSC ASSISTED DESIGN — "Design it for me" ───────────────────────────────
//
// After the shopper captures their lake, this offers to design the sign for
// them: a few short questions, three finished drafts with prices, then -- if
// they want one -- their property marker, placed by tapping the sign, then a
// single "Here's your design" screen (size included) they can edit from. The existing builder is still underneath -- every edit
// opens the real control for that one thing and comes straight back -- and it
// is still the whole experience for anyone who taps "I'll design it myself".
//
// ⚠️ REVERSIBLE BY CONSTRUCTION
//   - Everything new lives in THIS file. lake-capture.js has one hook into it
//     (bakeRotationAndProceed) and does nothing differently if this file is
//     absent or switched off.
//   - Off unless LAKE_CAPTURE_CONFIG.assistedDesign === true, or the browser
//     session opted in with ?lc_assist=1 (?lc_assist=0 opts back out). That lets
//     the flow be tried on the live site before any customer sees it.
//   - The AI is a further switch on the Apps Script side (DESIGN_AI_ENABLED),
//     and when it is off, slow or failing, the drafts come from the rules below
//     without the shopper seeing any difference.
//
// ⚠️ THE CUSTOMER COMES FIRST. Nothing here may stop someone ordering. Every
//   entry point is wrapped; if anything throws, the shopper lands in the normal
//   builder exactly where they would have been.
//
// The three pieces:
//   CORE   pure decisions (answers -> three drafts, size advice, colour words,
//          lake-name guess). No DOM, no globals -- tested offline.
//   ENGINE applies a draft to the real builder state through lake-capture.js's
//          own functions (applyTheme, selectSize, computeNameplate...).
//   UI     the screens.
// ────────────────────────────────────────────────────────────────────────────

(function(){
'use strict';

// ════════════════════════════════════════════════════════════════════════════
// CORE — pure
// ════════════════════════════════════════════════════════════════════════════

// The 14 looks shipped in presets/ (themeOnly). All are two-layer, which is the
// only construction the assisted path sells (owner's decision, 1 Oct 2026).
// `families` is how a shopper's "what kind of look" maps onto them; the first
// family listed is the look's home.
const LOOKS = {
  'cabin':      { name:'Cabin',      style:'raised', sign:'sand',    panel:'bronze', desc:'Warm sand on bronze — a classic cabin feel', families:['rustic','classic'] },
  'pinewood':   { name:'Pinewood',   style:'cutout', sign:'sand',    panel:'green',  desc:'Sand and forest green — woodsy and calm', families:['rustic'] },
  'driftwood':  { name:'Driftwood',  style:'cutout', sign:'sand',    panel:'bronze', desc:'Sand over bronze — weathered and natural', families:['rustic','coastal'] },
  'copper':     { name:'Copper',     style:'raised', sign:'bronze',  panel:'hblack', desc:'Bronze on black — rich and rustic', families:['rustic','classic'] },
  'granite':    { name:'Granite',    style:'cutout', sign:'sand',    panel:'smoke',  desc:'Sand on smoke grey — quiet and solid', families:['classic','rustic'] },
  'evergreen':  { name:'Evergreen',  style:'raised', sign:'hwhite',  panel:'green',  desc:'White on deep green — fresh and classic', families:['classic','bold'] },
  'midnight':   { name:'Midnight',   style:'raised', sign:'hwhite',  panel:'hblack', desc:'Crisp white on black — clean and bold', families:['modern','classic'] },
  'whiteout':   { name:'Whiteout',   style:'cutout', sign:'gblack',  panel:'gwhite', desc:'Black lake on gloss white — sharp and modern', families:['modern'] },
  'polished':   { name:'Polished',   style:'raised', sign:'pmetal',  panel:'gblack', desc:'Polished metal on gloss black — sleek', families:['modern'] },
  'harbor':     { name:'Harbor',     style:'cutout', sign:'gwhite',  panel:'blue',   desc:'White on blue — bright and nautical', families:['coastal','classic'] },
  'coastline':  { name:'Coastline',  style:'cutout', sign:'hwhite',  panel:'teal',   desc:'White with a teal lake — breezy and coastal', families:['coastal'] },
  'deep-water': { name:'Deep Water', style:'cutout', sign:'tsilver', panel:'blue',   desc:'Silver on blue — cool and calm', families:['coastal','modern'] },
  'ember':      { name:'Ember',      style:'cutout', sign:'hblack',  panel:'red',    desc:'Black with a red lake — bold and warm', families:['bold'] },
  'sunset':     { name:'Sunset',     style:'cutout', sign:'tgold',   panel:'orange', desc:'Gold with an orange lake — sunny and bright', families:['bold'] },
  // 8 Oct (owner): sand plate with the lake cut through to Candy Blue -- the water reads as water.
  'sandbar':    { name:'Sandbar',    style:'cutout', sign:'sand',    panel:'blue',   desc:'Sand with a blue lake — easy beach-house feel', families:['coastal','rustic'] },
  // 8 Oct (owner: "more options"). Every pair contrasts on the layer that
  // shows: raised = lake piece vs back plate; cutout = front plate vs the
  // colour through the lake. All two-layer, like every look here.
  'lighthouse': { name:'Lighthouse', style:'cutout', sign:'hwhite',  panel:'red',    desc:'White with a red lake — crisp and nautical', families:['coastal','classic'] },
  'lagoon':     { name:'Lagoon',     style:'cutout', sign:'sand',    panel:'teal',   desc:'Sand with a teal lake — warm and beachy', families:['coastal','rustic'] },
  'mariner':    { name:'Mariner',    style:'raised', sign:'gwhite',  panel:'blue',   desc:'Gloss white lake on blue — sharp and nautical', families:['coastal','classic'] },
  'flamingo':   { name:'Flamingo',   style:'cutout', sign:'hwhite',  panel:'pink',   desc:'White with a pink lake — bright and fun', families:['bold','coastal'] },
  'northwoods': { name:'Northwoods', style:'raised', sign:'bronze',  panel:'green',  desc:'Bronze lake on forest green — lodge and pines', families:['rustic'] },
  'cranberry':  { name:'Cranberry',  style:'raised', sign:'sand',    panel:'red',    desc:'Sand lake on deep red — warm cabin classic', families:['rustic','bold'] },
  'sunrise':    { name:'Sunrise',    style:'cutout', sign:'sand',    panel:'orange', desc:'Sand with an orange lake — morning on the dock', families:['rustic','bold'] },
  'spruce':     { name:'Spruce',     style:'cutout', sign:'hwhite',  panel:'green',  desc:'White with a green lake — fresh and timeless', families:['classic','rustic'] },
  'golden-hour':{ name:'Golden Hour',style:'raised', sign:'tgold',   panel:'gblack', desc:'Gold lake on gloss black — rich and refined', families:['modern','classic'] },
  'slate':      { name:'Slate',      style:'raised', sign:'hwhite',  panel:'smoke',  desc:'White lake on smoky grey — calm and modern', families:['modern','classic'] },
  'night-swim': { name:'Night Swim', style:'raised', sign:'teal',    panel:'hblack', desc:'Teal lake on hammered black — glowing and bold', families:['bold','modern'] },
  'twilight':   { name:'Twilight',   style:'raised', sign:'tsilver', panel:'purple', desc:'Silver lake on purple — dusk over the water', families:['bold'] },
  // 9 Oct (owner): 11 more, all existing finishes. `feature` is a small
  // merchandising preference read in one place (lookFit) -- Blue Reflection is
  // the featured premium look; it still has to compose well to be shown.
  'harbor-mist':      { name:'Harbor Mist',       style:'cutout', sign:'hwhite',  panel:'smoke',  desc:'White with a smoky lake — soft and coastal', families:['coastal','modern'] },
  'blue-ridge':       { name:'Blue Ridge',        style:'raised', sign:'blue',    panel:'sand',   desc:'Blue lake on sand — classic lakeside', families:['coastal','classic'] },
  'deep-current':     { name:'Deep Current',      style:'cutout', sign:'hblack',  panel:'teal',   desc:'Black with a teal lake — deep and modern', families:['modern','coastal'] },
  'silver-lake':      { name:'Silver Lake',       style:'raised', sign:'tsilver', panel:'hblack', desc:'Silver lake on hammered black — refined', families:['modern','classic'] },
  // Candy Green renders dark on Hammered Black: lake and letters read faintly
  // (9 Oct matrix). Kept for customers who choose it; scored down so it is not
  // recommended unprompted (`feature` < 0).
  'forest-retreat':   { name:'Forest Retreat',    style:'cutout', sign:'hblack',  panel:'green',  desc:'Black with a green lake — woods at night', families:['rustic','modern'], feature:-0.08 },
  'champagne-coast':  { name:'Champagne Coast',   style:'raised', sign:'tgold',   panel:'hwhite', desc:'Gold lake on white — light and elegant', families:['classic','coastal'] },
  'lakehouse-classic':{ name:'Lakehouse Classic', style:'cutout', sign:'sand',    panel:'smoke',  desc:'Sand with a smoky lake — the lake-house classic', families:['classic','rustic'] },
  'blue-steel':       { name:'Blue Steel',        style:'raised', sign:'blue',    panel:'tsilver',desc:'Blue lake on silver — cool and modern', families:['modern','coastal'] },
  'evergreen-mist':   { name:'Evergreen Mist',    style:'raised', sign:'green',   panel:'sand',   desc:'Green lake on sand — calm and woodsy', families:['rustic','classic'] },
  'copper-shore':     { name:'Copper Shore',      style:'cutout', sign:'hblack',  panel:'bronze', desc:'Black with a bronze lake — warm and bold', families:['rustic','modern'] },
  'blue-reflection':  { name:'Blue Reflection',   style:'cutout', sign:'pmetal',  panel:'blue',   desc:'Polished metal with a blue lake — our premium look', families:['coastal','modern','classic'], feature:0.04 },
};

// Ordered preference within each family -- the first is the strongest example.
const FAMILY_ORDER = {
  rustic:  ['cabin','pinewood','sandbar','northwoods','copper-shore','evergreen-mist','copper','cranberry','driftwood','forest-retreat','lakehouse-classic','sunrise','lagoon','granite','spruce'],
  modern:  ['midnight','blue-reflection','golden-hour','silver-lake','whiteout','deep-current','slate','blue-steel','polished','harbor-mist','deep-water','forest-retreat','copper-shore','night-swim'],
  coastal: ['sandbar','blue-reflection','coastline','blue-ridge','lighthouse','harbor','harbor-mist','mariner','lagoon','deep-water','champagne-coast','blue-steel','deep-current','driftwood','flamingo'],
  bold:    ['ember','twilight','sunset','night-swim','flamingo','cranberry','sunrise','evergreen'],
  classic: ['midnight','blue-reflection','harbor','lakehouse-classic','spruce','champagne-coast','evergreen','mariner','blue-ridge','golden-hour','silver-lake','copper','slate','granite','evergreen-mist','lighthouse','cabin'],
};
// "Surprise me": one from each of three different directions.
// 12 Oct (owner): "show off our best but also the breadth" -- two Layered
// Cutouts (sand/smoke, polished metal/blue) and one Layered Silhouette (white on green).
const SURPRISE = ['lakehouse-classic','blue-reflection','evergreen'];

// The 21 finishes, by id, with the words a shopper might use for each. `exact`
// is false where we are substituting (navy is not a finish we have), which is
// what triggers the small "closest we have" note.
const COLOR_WORDS = [
  { re:/\bnavy\b/i,                        id:'blue',    exact:false, said:'navy' },
  { re:/\b(cobalt|sky blue|light blue|baby blue|ocean)\b/i, id:'blue', exact:false, said:'blue' },
  { re:/\b(emerald|olive|sage)\b/i,        id:'green',   exact:false, said:'green' },
  { re:/\b(cranberry|cherry|scarlet)\b/i,  id:'red',     exact:false, said:'red' },
  { re:/\b(lavender|plum)\b/i,             id:'purple',  exact:false, said:'purple' },
  { re:/\b(rose|magenta|fuchsia)\b/i,      id:'pink',    exact:false, said:'pink' },
  { re:/\bbrown\b/i,                       id:'bronze',  exact:false, said:'brown' },
  { re:/\b(ivory|off[- ]white)\b/i,        id:'sand',    exact:false, said:'cream' },
  { re:/\b(aqua|turquoise|teal)\b/i,       id:'teal',    exact:null },
  { re:/\b(royal blue|blue)\b/i,           id:'blue',    exact:true },
  { re:/\b(forest|hunter|green)\b/i,       id:'green',   exact:null },
  { re:/\blime\b/i,                        id:'lime',    exact:true },
  { re:/\b(red|crimson)\b/i,               id:'red',     exact:null },
  { re:/\b(burgundy|maroon)\b/i,           id:'red',     exact:false, said:'burgundy' },
  { re:/\borange\b/i,                      id:'orange',  exact:true },
  { re:/\byellow\b/i,                      id:'yellow',  exact:true },
  { re:/\b(purple|violet)\b/i,             id:'purple',  exact:null },
  { re:/\bpink\b/i,                        id:'pink',    exact:true },
  { re:/\b(gr[ae]y|charcoal|smoke)\b/i,    id:'smoke',   exact:null },
  { re:/\b(silver|steel)\b/i,              id:'tsilver', exact:null },
  { re:/\b(chrome|polished|metal)\b/i,     id:'pmetal',  exact:null },
  { re:/\bgold\b/i,                        id:'tgold',   exact:true },
  { re:/\b(copper|bronze|rust)\b/i,        id:'bronze',  exact:null },
  { re:/\b(tan|beige|sand|cream|khaki)\b/i,id:'sand',    exact:null },
  { re:/\bwhite\b/i,                       id:'hwhite',  exact:true },
  { re:/\bblack\b/i,                       id:'hblack',  exact:true },
];
const FINISH_NAMES = {
  red:'Candy Red', orange:'Candy Orange', yellow:'Candy Yellow', lime:'Candy Lime', green:'Candy Green',
  teal:'Candy Teal', blue:'Candy Blue', purple:'Candy Purple', pink:'Candy Pink', cgold:'Candy Gold',
  smoke:'Candy Smoke', bronze:'Textured Bronze', tgold:'Textured Gold', tsilver:'Textured Silver',
  sand:'Sand', hblack:'Hammered Black', hwhite:'Hammered White', gblack:'Gloss Black', gwhite:'Gloss White',
  pmetal:'Polished Metal',
};
const LIGHT = new Set(['hwhite','gwhite','sand','tsilver','pmetal','yellow','tgold','cgold']);

const ICONS = ['house','cabin','star','heart','flag','fish','compass','tree','duck','moose','bear','bigfoot','anchor','maple','arrow'];
const SIZES = [24, 28, 32, 36];

// What each look family means, in a customer's words (the look cards).
// `swatches`: the range a style covers, shown on its card (finish ids), so a
// card hints at all of its looks, not just one.
const FAMILY_INFO = {
  rustic:  { name:'Rustic',          desc:'Sand, bronze, forest green and cranberry', lead:'cabin',     swatches:['sand','bronze','green','red'] },
  modern:  { name:'Modern',          desc:'Black, white, gold and grey',            lead:'midnight',  swatches:['hwhite','gblack','tgold','smoke'] },
  coastal: { name:'Coastal',         desc:'Sand, whites, blues and teals',          lead:'sandbar',   swatches:['sand','blue','teal','hwhite'] },
  bold:    { name:'Bold & colorful', desc:'Reds, oranges, purples and pinks',       lead:'ember',     swatches:['red','orange','purple','pink'] },
  classic: { name:'Classic',         desc:'Timeless white, black, blue and green',  lead:'harbor',    swatches:['hwhite','hblack','blue','green'] },
};
// One short label per size, read on the card itself.
const SIZE_LABEL = { 24:'Smaller wall / entryway', 28:'Most popular', 32:'Large wall', 36:'Statement piece' };

// Plain-English size help. Longest side, in inches.
const SIZE_INFO = {
  24: 'Good for an entryway, a smaller wall or above a side table — roughly the size of a carry-on suitcase.',
  28: 'A versatile medium size that suits most lake-house walls.',
  32: 'More of a focal point for a room.',
  36: 'A large statement piece for a prominent wall.',
};

/** Colours a shopper named in their notes, in the order they named them. */
function colorsFromText(text){
  const t = String(text || '');
  const found = [];
  for(const w of COLOR_WORDS){
    const m = t.match(w.re);
    if(!m) continue;
    if(found.some(f => f.id === w.id)) continue;
    // Words like "blue" also match inside "navy blue"; the navy rule ran first.
    found.push({ id:w.id, at:m.index, substituted: w.exact === false, said: w.said || m[0].toLowerCase() });
  }
  return found.sort((a,b) => a.at - b.at);
}

/**
 * How they want the words, read from their own notes -- so "text at the bottom,
 * large, as a nameplate" counts even when the AI is off or slow. Only what was
 * actually said comes back; nothing is guessed.
 */
function layoutFromText(text){
  const t = String(text || '').toLowerCase();
  const out = {};
  const words = '(text|words?|letters?|lettering|names?|lines?|writing|title)';
  if(/\b(bottom|below|beneath|underneath|under)\b/.test(t) && !/\b(top|above)\b/.test(t)) out.placement = 'bottom';
  else if(/\b(top|above)\b/.test(t) && !/\b(bottom|below|beneath|underneath)\b/.test(t)) out.placement = 'top';
  const big = '(large|larger|big|bigger|huge|bold|prominent|stand out)';
  const small = '(small|smaller|subtle|little|tiny)';
  const near = (a, b) => new RegExp(`\\b${a}\\b[^.!?]{0,30}\\b${b}\\b|\\b${b}\\b[^.!?]{0,30}\\b${a}\\b`).test(t);
  if(near(big, words)) out.textSize = 'large';
  else if(near(small, words)) out.textSize = 'small';
  if(/\bname ?plates?\b|\bplaques?\b|\bbanners?\b/.test(t)) out.textStyle = 'nameplate';
  else if(/\bcut(-| )?(out|through)?\s+(letters|lettering|text|words)\b|\bstencil(led)?\b/.test(t)) out.textStyle = 'cut';
  return out;
}

/**
 * Is the map search a lake name we can offer back? "Lake Norman, NC" -> yes;
 * "123 Main St" / "35.5, -80.9" / "near grandma's" -> no.
 */
function lakeNameFromSearch(raw){
  const s = String(raw || '').trim();
  if(!s || s.length > 60) return null;
  if(/^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(s)) return null;           // coordinates
  if(/^\d+\s+\w+/.test(s)) return null;                                    // street address
  if(/\b(st|street|rd|road|ave|avenue|dr|drive|ln|lane|hwy|highway|blvd|ct|court)\b\.?/i.test(s)) return null;
  const head = s.split(',')[0].trim();
  const water = /\b(lake|lac|pond|reservoir|bay|sound|river|creek|lagoon|harbou?r|inlet|loch)\b/i;
  if(!water.test(head)) return null;
  // Title-case it, the way it would read on a sign; keep short words lower.
  return head.toLowerCase().replace(/\b([a-z])([a-z']*)/g, (m, a, b, i) =>
    (i > 0 && /^(of|the|and|on|in|de|la)$/.test(m)) ? m : a.toUpperCase() + b);
}

/**
 * The words to offer when the lake has no known name (13 Oct, owner: "anything
 * searched appears"): the search text with a trailing state/province stripped,
 * the part before a comma, title-cased. Nothing with a digit (an address,
 * coordinates) and no street words -- the owner's rule: never print an address.
 */
function searchWords(){
  let s = String(window._lcSearchText || '').replace(/\s+/g, ' ').trim();
  if(!s || /\d/.test(s)) return null;
  if(/\b(st|street|rd|road|ave|avenue|dr|drive|ln|lane|hwy|highway|blvd|ct|court)\b\.?/i.test(s)) return null;
  s = s.split(',')[0].trim();
  try { if(typeof window._lcStripRegion === 'function'){ const t = window._lcStripRegion(s); if(t) s = t.trim(); } } catch(e){}
  if(!s || s.length > 32) return null;
  return s.toLowerCase().replace(/\b([a-z])([a-z']*)/g, (m, a, b, i) =>
    (i > 0 && /^(of|the|and|on|in|de|la)$/.test(m)) ? m : a.toUpperCase() + b);
}

/** Size advice from what the sign is for. Advice only -- the shopper chooses. */
function recommendSize(purpose, hint){
  if(hint && SIZES.includes(Number(hint.size))){
    return { inches:Number(hint.size), why: String(hint.why || SIZE_INFO[hint.size]).slice(0,160) };
  }
  switch(purpose){
    case 'gift':     return { inches:24, why:'A popular gift size — easy to hang almost anywhere.' };
    case 'cabin':    return { inches:24, why:'Cabin walls tend to be busy; this size fits without crowding.' };
    case 'business': return { inches:32, why:'Large enough to read across a room or a storefront.' };
    case 'rental':   return { inches:28, why:'Big enough to be the photo guests take, without overwhelming a wall.' };
    default:         return { inches:28, why:'Our most popular size for a lake-house wall.' };
  }
}

/** The words that go on the sign, from the interview fields. */
function linesFromBrief(brief){
  const t = (brief && brief.text) || {};
  const wants = new Set((brief && brief.wants) || []);
  const out = [];
  const clean = (s) => String(s || '').replace(/\s+/g,' ').trim().slice(0, 32);
  if(wants.has('family') && clean(t.family)) out.push(clean(t.family));
  if(wants.has('lake') && clean(t.lake)) out.push(clean(t.lake));
  if(wants.has('year') && clean(t.year)){
    const y = clean(t.year);
    out.push(/^\d{4}$/.test(y) ? 'EST. ' + y : y);
  }
  if(wants.has('other') && clean(t.other)) out.push(clean(t.other));
  return out.slice(0, 3);   // three lines is already a lot on a 24" sign
}

/**
 * Three drafts from one brief: three art directions on the same content.
 *
 * Content is fixed -- every draft carries every word they asked for. What
 * varies is the look (construction + finishes), the font that comes with it,
 * and where the words sit. Size is not varied here; it is the shopper's call,
 * asked next.
 *
 * `hints` is the AI's reading of the brief, already validated by the caller.
 * It may name looks, colours, a placement and extra notes; anything missing
 * falls back to the rules, so `hints` can be null.
 */
function briefToDrafts(brief, hints, shape){
  brief = brief || {};
  hints = hints || {};
  const notes = [];

  // Which looks. AI picks first (if any), then the family's own order, then
  // anything not yet used -- always three, never a repeat.
  const family = FAMILY_ORDER[brief.look] ? brief.look : null;
  let pool = family ? FAMILY_ORDER[family].slice() : surpriseFor(shape);
  // A business sign leans clean and readable whatever else they said.
  if(brief.purpose === 'business' && !family) pool = ['midnight','harbor','whiteout'];
  const picks = [];
  const add = (slug) => { if(LOOKS[slug] && !picks.includes(slug) && picks.length < 3) picks.push(slug); };
  (Array.isArray(hints.looks) ? hints.looks : []).forEach(add);
  pool.forEach(add);
  Object.keys(LOOKS).forEach(add);

  // Prefer a set that does not look like three copies of one sign: if all three
  // share a construction, swap the last for the best of the other kind.
  if(picks.length === 3 && picks.every(s => LOOKS[s].style === LOOKS[picks[0]].style) && !(hints.looks && hints.looks.length >= 3)
     && !(shape && shape.island && LOOKS[picks[0]].style === 'raised')){
    const other = (family ? FAMILY_ORDER[family] : Object.keys(LOOKS))
      .concat(Object.keys(LOOKS))
      .find(s => LOOKS[s].style !== LOOKS[picks[0]].style && !picks.includes(s));
    if(other) picks[2] = other;
  }

  // Colours they asked for, from the AI or from their own words.
  let colors = null;
  if(hints.colors && (hints.colors.sign || hints.colors.panel)){
    colors = { sign: FINISH_NAMES[hints.colors.sign] ? hints.colors.sign : null,
               panel: FINISH_NAMES[hints.colors.panel] ? hints.colors.panel : null };
  } else {
    const said = colorsFromText(brief.notes);
    if(said.length){
      // The lighter one usually reads best as the lake/front, the other behind.
      const a = said[0], b = said[1];
      let sign = a.id, panel = b ? b.id : null;
      if(b && !LIGHT.has(a.id) && LIGHT.has(b.id)){ sign = b.id; panel = a.id; }
      colors = { sign, panel };
      said.filter(c => c.substituted).forEach(c =>
        notes.push(`We used ${FINISH_NAMES[c.id]}, our closest finish to ${c.said}.`));
    }
  }

  const { lines, icons } = contentFromBrief(brief, hints);
  // A side they actually asked for goes on all three; otherwise vary it.
  const wish = wordsWish(brief, hints);
  // 8 Oct g: every draft takes Best fit (the composition scoring chooses
  // where the words go); the third used to be forced to the top, which left
  // Midnight's nameplate small on almost every lake.
  const placements = wish.placement ? [wish.placement, wish.placement, wish.placement] : ['best', 'best', 'best'];

  const drafts = picks.map((slug, i) => {
    const d = makeDraft(slug, lines, icons, placements[i] || 'best', wish);
    // The first draft is "as you described it": their colours on its look.
    if(i === 0 && colors){
      if(colors.sign) d.signColorId = colors.sign;
      if(colors.panel && colors.panel !== d.signColorId) d.panelColorId = colors.panel;
      d.custom = true;
    }
    return d;
  });

  (Array.isArray(hints.notes) ? hints.notes : []).forEach(n => {
    const s = String(n || '').trim();
    if(s && s.length <= 200 && !notes.includes(s)) notes.push(s);
  });
  return { drafts, notes: notes.slice(0, 3) };
}

/**
 * The first three looks, by the lake's shape (8 Oct g). Three directions --
 * lakeside, warm, modern -- as before; what changes is the construction:
 *  - a prominent island (Crater's Wizard Island, Flathead's Wild Horse): all
 *    Layered Silhouette, where the island reads as an opening in the raised
 *    lake. Mariner (gloss white on blue) stands in for Sandbar's lakeside slot.
 *  - branchy or river-like (Kerr, Dale Hollow, Lake Austin): the lake as a
 *    coloured window reads best, so the modern slot is Whiteout (cutout)
 *    instead of Midnight (raised), giving two cutouts and one raised.
 *  - anything else: Lakehouse Classic, Blue Reflection, Evergreen (owner, 12 Oct;
 *    was Sandbar, Cabin, Midnight). Shape no longer changes them.
 * `shape` comes from lakeShape() on the page; null = the default three.
 */
function surpriseFor(shape){
  if(shape && shape.island) return ['mariner', 'cabin', 'midnight'];
  // 13 Oct: once LookSavi's counts are in, the learned three replace the hand-picked ones.
  if(LOOK_SCORES){ const t = learnedThree(LOOK_SCORES); if(t) return t; }
  // 9 Oct: the premium slot leads with the featured look (Blue Reflection, a
  // Layered Cutout) when the lake has no island to preserve; if it composes
  // poorly on this lake, generate()'s fallback replaces it from DIRECTIONS.
  // 12 Oct (owner): every other lake opens on the same three, picked by hand.
  const three = SURPRISE.slice();
  // 9 Oct (owner): no two of the first three show the same colour in the lake
  // or share a colour finish (the hand-picked three don't; kept as a guard).
  if(three.slice(1).some(k => looksClash(k, three[0]))){
    const alt = DIRECTIONS[SLOT_DIRS[0]].find(k => LOOKS[k] && !three.includes(k) && !three.slice(1).some(o => looksClash(o, k)));
    if(alt) three[0] = alt;
  }
  return three;
}
/** The colour that shows in the lake: the back plate through a cutout, the lake piece when raised. */
function lakeHue(k){
  const L = LOOKS[k]; return L ? (L.style === 'cutout' ? L.panel : L.sign) : null;
}
// Neutral finishes (whites, sand, metals, greys, blacks) can repeat; a colour can't.
const NEUTRAL = new Set(['hwhite', 'gwhite', 'sand', 'tsilver', 'pmetal', 'smoke', 'hblack', 'gblack']);
/** Two looks are too alike for one set: same lake colour, or a colour finish in common. */
function looksClash(a, b){
  const A = LOOKS[a], B = LOOKS[b]; if(!A || !B) return false;
  if(lakeHue(a) === lakeHue(b)) return true;
  return [A.sign, A.panel].some(f => !NEUTRAL.has(f) && (f === B.sign || f === B.panel));
}
/** The featured look for a direction (highest `feature`), or null. */
function featuredLook(dir){
  const list = (DIRECTIONS[dir] || []).filter(k => LOOKS[k] && LOOKS[k].feature > 0);
  return list.sort((a, b) => LOOKS[b].feature - LOOKS[a].feature)[0] || null;
}

// ── Learned look ranking (13 Oct, owner: "a self learning system presenting
// the best styles first") ─────────────────────────────────────────────────────
// LookSavi counts, per look, the visits that SAW it and what they did with it
// (picked 1, browsed/tried 0.5, carted 3, bought 8 -- POINTS in LookSavi's
// funnel/look-rank.ts). Each look's rate = points per visit that saw it,
// shrunk toward the store-wide rate by LEARN_PRIOR imaginary visits, and the
// owner's hand-picked three start a little ahead (LEARN_HAND). So with little
// data the order stays close to the hand-picked one, and as visits come in it
// follows what people actually pick and buy.
// One random draw per look per VISIT (Thompson sampling): a look with few
// visits gets a wider draw, so a new or rarely shown look still gets shown
// sometimes and can prove itself instead of being buried for ever.
const LEARN_PRIOR = 30;
const LEARN_HAND = 1.6;
/** Plate or lake in Sand -- at most one of these in the first three (owner, 13 Oct: "all of the signs are the sand based ones"). */
function isSandy(k){ const L = LOOKS[k]; return !!L && (L.sign === 'sand' || L.panel === 'sand'); }
function gauss(rand){ const u = Math.max(1e-9, rand()), v = rand(); return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v); }
/**
 * {slug: score} for every look, or null without data. `stats` is LookSavi's
 * {looks:{slug:{shown,points}}}; `rand` a 0..1 generator (seeded per visit, so
 * one visit sees one stable order).
 */
function lookRankScores(stats, rand){
  if(!stats || !stats.looks || typeof rand !== 'function') return null;
  let P = 0, N = 0;
  for(const k of Object.keys(LOOKS)){ const s = stats.looks[k]; if(s){ P += Math.max(0, +s.points || 0); N += Math.max(0, +s.shown || 0); } }
  const mu = Math.min(3, Math.max(0.1, N ? P / N : 0.5));
  const out = {};
  for(const k of Object.keys(LOOKS)){
    const s = stats.looks[k] || {}, n = Math.max(0, +s.shown || 0), pts = Math.max(0, +s.points || 0);
    const h = (SURPRISE.includes(k) ? LEARN_HAND : 1) + Math.max(-0.1, Math.min(0.06, LOOKS[k].feature || 0));
    const mean = (pts + LEARN_PRIOR * mu * h) / (n + LEARN_PRIOR);
    out[k] = mean + gauss(rand) * Math.sqrt(Math.max(mean, 0.05) / (n + LEARN_PRIOR));
  }
  return out;
}
/**
 * The first three by learned score: best first, never two that clash
 * (looksClash), at most one Sand look, and both constructions in the set.
 */
function learnedThree(scores){
  const order = Object.keys(LOOKS).sort((a, b) => scores[b] - scores[a]);
  const three = [];
  for(const k of order){
    if(three.length === 3) break;
    if(three.some(o => looksClash(o, k))) continue;
    if(isSandy(k) && three.some(isSandy)) continue;
    if(three.length === 2 && LOOKS[three[0]].style === LOOKS[three[1]].style && LOOKS[k].style === LOOKS[three[0]].style) continue;
    three.push(k);
  }
  return three.length === 3 ? three : null;
}
/** A list in learned order, Sand looks spread out (never two in a row). */
function learnedOrder(list, scores){
  const rest = list.slice().sort((a, b) => scores[b] - scores[a]), out = [];
  while(rest.length){
    const i = out.length && isSandy(out[out.length - 1]) ? rest.findIndex(k => !isSandy(k)) : 0;
    out.push(rest.splice(i < 0 ? 0 : i, 1)[0]);
  }
  return out;
}
/** Set by the page once LookSavi's counts arrive (null = the hand-picked order). */
let LOOK_SCORES = null;
function setLookScores(v){ LOOK_SCORES = v || null; }

/**
 * Round 2 (8 Oct h): the first three are three directions, each with a short
 * list of looks to fall back on when the first one composes poorly on THIS
 * lake. Order inside a list = preference; construction alternates so a
 * fallback brings something different.
 */
// The default three's slots, in order (12 Oct): Lakehouse Classic, Blue
// Reflection, Evergreen -- each list starts with the owner's pick, then the
// looks that may stand in when it composes poorly on a lake.
const SLOT_DIRS = ['warm', 'modern', 'colour'];
const DIRECTIONS = {
  colour:   ['evergreen', 'cranberry', 'spruce', 'mariner', 'coastline', 'sandbar', 'harbor', 'sunrise'],
  lakeside: ['sandbar', 'mariner', 'blue-ridge', 'coastline', 'harbor', 'harbor-mist', 'lagoon', 'deep-water'],
  warm:     ['lakehouse-classic', 'cabin', 'pinewood', 'northwoods', 'copper-shore', 'evergreen-mist', 'driftwood', 'copper'],
  modern:   ['blue-reflection', 'midnight', 'whiteout', 'silver-lake', 'slate', 'golden-hour', 'deep-current', 'polished'],
};
/** Rough lightness of each finish (0 black .. 1 white), for contrast only. */
const FINISH_LUM = { hwhite:.92, gwhite:.95, sand:.75, tsilver:.70, pmetal:.67, yellow:.85, lime:.70, tgold:.60,
  cgold:.55, orange:.55, pink:.55, teal:.45, bronze:.45, smoke:.40, red:.35, blue:.30, green:.30, purple:.25, hblack:.10, gblack:.08 };
const NOVELTY = new Set(['pink', 'lime', 'purple', 'yellow']);
/**
 * How well a finished draft suits this lake, beyond its layout score:
 * construction fit (an island reads in Layered Silhouette; branchy lakes and
 * rivers read as a cutout window), contrast of the layer that shows, and a
 * small penalty for novelty colours in a first impression. -0.15..+0.1.
 */
function lookFit(d, shape){
  let v = 0;
  const raised = d.style === 'raised';
  if(shape && shape.island) v += raised ? 0.06 : -0.06;
  if(shape && (shape.branchy || shape.river)) v += raised ? -0.02 : 0.03;
  const a = FINISH_LUM[d.signColorId], b = FINISH_LUM[d.panelColorId];
  if(a != null && b != null) v += Math.min(0.04, Math.abs(a - b) * 0.08) - (Math.abs(a - b) < 0.2 ? 0.05 : 0);
  if(NOVELTY.has(d.signColorId) || NOVELTY.has(d.panelColorId)) v -= 0.04;
  // Merchandising: a look's `feature` (LOOKS[...].feature) lifts or lowers it,
  // bounded -- Blue Reflection +0.04 (featured), Forest Retreat -0.08 (faint).
  v += Math.max(-0.1, Math.min(0.06, (LOOKS[d.look] || {}).feature || 0));
  return v;
}
/**
 * The next look to try for a draft's direction, or null. The construction it
 * leans to: raised for an island lake; otherwise whichever the other two
 * drafts lack (both cutouts -> raised), else the opposite of the current one.
 */
function fallbackLook(slot, used, shape, current, others, set){
  const dir = SLOT_DIRS[slot]; if(!dir) return null;
  // Never too like one of the other two drafts (owner, 9 Oct: a splash of different flavours).
  const keep = set || others || [];
  const os = (others || []).map(k => (LOOKS[k] || {}).style);
  const want = (shape && shape.island) ? 'raised'
    : (os.length === 2 && os[0] === os[1]) ? (os[0] === 'raised' ? 'cutout' : 'raised')
    : (LOOKS[current] || {}).style === 'raised' ? 'cutout' : 'raised';
  const list = DIRECTIONS[dir].filter(k => LOOKS[k] && !used.includes(k) && !keep.some(o => looksClash(o, k)));
  return list.find(k => LOOKS[k].style === want) || list[0] || null;
}

/** The words and icons every draft carries, whatever its look. */
function contentFromBrief(brief, hints){
  brief = brief || {}; hints = hints || {};
  const lines = linesFromBrief(brief).concat(Array.isArray(hints.lines) ? hints.lines.map(String) : [])
    .filter((v, i, a) => v && a.indexOf(v) === i).slice(0, 3);
  const wants = new Set(brief.wants || []);
  const icons = [];
  // "Add an icon": the AI's pick if it named one, otherwise a compass.
  const aiIcons = (Array.isArray(hints.icons) ? hints.icons : []).filter(i => ICONS.includes(i) && i !== brief.marker);
  if(wants.has('compass')) icons.push('compass');
  if(wants.has('icon') && !aiIcons.length && !icons.includes('compass')) icons.push('compass');
  aiIcons.forEach(i => { if(!icons.includes(i)) icons.push(i); });
  return { lines, icons };
}
function makeDraft(slug, lines, icons, placement, wish){
  const L = LOOKS[slug];
  wish = wish || {};
  const d = { look: slug, name: L.name, style: L.style, signColorId: L.sign, panelColorId: L.panel,
              lines: lines.slice(), placement: placement || 'best', icons: icons.slice(0, 2) };
  if(wish.placement && wish.placement === d.placement) d.placementStated = true;
  if(wish.textSize) d.textSize = wish.textSize;
  if(wish.textStyle) d.textStyle = wish.textStyle;
  return d;
}
/**
 * What they asked for about the words: the AI's reading when it gave one, else
 * their own notes read directly. Each field present only when actually asked.
 */
function wordsWish(brief, hints){
  brief = brief || {}; hints = hints || {};
  const said = layoutFromText(brief.notes);
  const out = {};
  const p = (hints.textPlacement === 'top' || hints.textPlacement === 'bottom') ? hints.textPlacement : said.placement;
  if(p) out.placement = p;
  const z = (hints.textSize === 'large' || hints.textSize === 'small') ? hints.textSize : said.textSize;
  if(z) out.textSize = z;
  const st = (hints.textStyle === 'nameplate' || hints.textStyle === 'cut') ? hints.textStyle : said.textStyle;
  if(st) out.textStyle = st;
  return out;
}
/**
 * One draft for a look the shopper picked themselves ("Or choose another look"):
 * the same words and icons as the recommendations, the look's own finishes.
 * null for a look we don't have.
 */
function draftForLook(slug, brief, hints, placement){
  if(!LOOKS[slug]) return null;
  const { lines, icons } = contentFromBrief(brief, hints);
  const wish = wordsWish(brief, hints);
  return makeDraft(slug, lines, icons, wish.placement || placement || 'best', wish);
}

/** The finishes, in customer words: "Hammered White + Candy Teal". */
function finishPhrase(d){
  return `${FINISH_NAMES[d.signColorId] || d.signColorId} + ${FINISH_NAMES[d.panelColorId] || d.panelColorId}`;
}

/**
 * Accept only what the AI may decide, in the shapes the engine understands.
 * Everything else is dropped. Called on the server's reply before briefToDrafts.
 */
function sanitizeHints(h){
  if(!h || typeof h !== 'object') return null;
  const out = {};
  if(Array.isArray(h.looks)) out.looks = h.looks.map(String).filter(s => LOOKS[s]).slice(0, 3);
  if(h.colors && typeof h.colors === 'object'){
    out.colors = {};
    if(FINISH_NAMES[h.colors.sign]) out.colors.sign = h.colors.sign;
    if(FINISH_NAMES[h.colors.panel]) out.colors.panel = h.colors.panel;
  }
  if(['top','bottom','best'].includes(h.textPlacement)) out.textPlacement = h.textPlacement;
  if(['large','small'].includes(h.textSize)) out.textSize = h.textSize;          // 'normal' = no opinion
  if(['nameplate','cut'].includes(h.textStyle)) out.textStyle = h.textStyle;     // 'any' = no opinion
  if(SIZES.includes(Number(h.size))) { out.size = Number(h.size); out.why = String(h.sizeReason || '').slice(0,160); }
  if(Array.isArray(h.icons)) out.icons = h.icons.map(String).filter(i => ICONS.includes(i)).slice(0, 2);
  if(Array.isArray(h.lines)) out.lines = h.lines.map(s => String(s).replace(/\s+/g,' ').trim().slice(0,32)).filter(Boolean).slice(0, 3);
  if(Array.isArray(h.notes)) out.notes = h.notes.map(s => String(s).trim().slice(0,200)).filter(Boolean).slice(0, 3);
  return out;
}

/**
 * "What should we change?" read in the page (9 Oct d, owner: "make the lake
 * blue" and "move the text to the top" both came back "Sorry" -- the AI call
 * through Apps Script timed out). Colours (closest finish we make, on the
 * part they named), words ("Change X to Y", "Add our family name: Y",
 * anything in quotes), top/bottom, bigger/smaller, nameplate/cut letters,
 * one/two lines and the size. Returns { plan, complete } -- complete when
 * nothing meaningful is left unread, so the AI isn't needed at all.
 * cur: { style:'raised'|'cutout', sign, panel, lines }.
 */
function localEdit(text, cur){
  cur = cur || {};
  let t = String(text || '').trim();
  const plan = {};
  const used = [];                       // [start, end) spans we understood
  const mark = (m, off) => { if(m) used.push([m.index + (off || 0), m.index + (off || 0) + m[0].length]); };
  const clean = (x) => String(x || '').replace(/^[\s"“'‘:,-]+|[\s"”'’.,!]+$/g, '').replace(/\s+/g, ' ').trim();
  // Words, first, so a colour inside the new words ("Blue Heron Cottage") is not a colour change.
  let m = t.match(/\b(?:change|replace|rename)\s+(.+?)\s+(?:to|with|into)\s+(.+)$/i);
  if(m){
    const what = clean(m[1]).toLowerCase(), now = (cur.lines || []).join(' ').toLowerCase();
    const isWords = /\b(text|words?|name|title|lettering|writing)\b/.test(what) || (now && (now.indexOf(what) >= 0 || what.indexOf(now) >= 0));
    if(isWords && clean(m[2])){ plan.lines = [clean(m[2]).slice(0, 40)]; mark(m); }
  }
  if(!plan.lines){
    m = t.match(/\badd\s+(?:our|my|the)?\s*(?:family\s+)?(?:name|names|line|text|words?)\s*[:\-]?\s+(.+)$/i);
    if(m && clean(m[1])){ plan.lines = (cur.lines || []).concat([clean(m[1]).slice(0, 40)]).slice(0, 3); mark(m); }
  }
  if(!plan.lines){
    m = t.match(/["“]([^"”]{1,40})["”]/);
    if(m && /\b(say|says|read|reads|text|words?|name|put)\b/i.test(t)){ plan.lines = [clean(m[1])]; mark(m); }
  }
  // Colours, outside the words we just took.
  const rest = used.length ? t.slice(0, used[0][0]) + ' '.repeat(used[0][1] - used[0][0]) + t.slice(used[0][1]) : t;
  const cols = colorsFromText(rest);
  if(cols.length){
    const LAKE = /\b(lake|water|shoreline|river)\b/i, PLATE = /\b(plate|background|back|backing|border|frame|panel|sign|metal|base|rest)\b/i;
    // The part named in the same clause ("background black and the lake white").
    const cuts = [0]; rest.replace(/,|;|\band\b|\bwith\b|\bbut\b/gi, (mm, at) => { cuts.push(at); return mm; }); cuts.push(rest.length);
    const clauseOf = (at) => { for(let i = 0; i < cuts.length - 1; i++) if(at >= cuts[i] && at < cuts[i + 1]) return rest.slice(cuts[i], cuts[i + 1]); return rest; };
    const picked = cols.slice(0, 2).map((c, k) => {
      const cl = clauseOf(c.at); const l = LAKE.test(cl), p = PLATE.test(cl);
      return { id: c.id, part: l && !p ? 'lake' : p && !l ? 'plate' : null };
    });
    // Unnamed: one colour = the lake; two = lake then plate, unless the other was named.
    picked.forEach((pp, k) => { if(!pp.part){ const other = picked[1 - k]; pp.part = other && other.part ? (other.part === 'lake' ? 'plate' : 'lake') : (k === 0 ? 'lake' : 'plate'); } });
    // On a raised sign the lake piece is `sign`; on a cutout the colour through the lake is `panel`.
    const slot = (part) => (cur.style === 'cutout') === (part === 'lake') ? 'panel' : 'sign';
    picked.forEach(pp => { plan[slot(pp.part)] = pp.id; });
    cols.slice(0, 2).forEach(c => used.push([c.at, c.at + c.said.length]));
  }
  // Where / how big / what kind of lettering.
  const lay = layoutFromText(rest);
  if(lay.placement){ plan.placement = lay.placement; const mm = rest.match(/\b(top|bottom|above|below|beneath|underneath|under)\b/i); mark(mm); }
  if(lay.textSize){ plan.textSize = lay.textSize; const mm = rest.match(/\b(large|larger|big|bigger|huge|bold|small|smaller|subtle|little|tiny)\b/i); mark(mm); }
  if(lay.textStyle){ plan.textStyle = lay.textStyle; const mm = rest.match(/\bname ?plates?\b|\bplaques?\b|\bbanners?\b|\bstencil(led)?\b|\bcut(-| )?(out|through)?\b/i); mark(mm); }
  const lw = lineWish(rest);
  if(lw){ plan.lineMode = lw.mode; plan.lineCount = lw.count; const mm = rest.match(/\b(one|1|single|same|two|2|three|3|separate|multiple|own)\s+lines?\b|\bsplit\b|\bstack(ed)?\b|\bcombine\b/i); mark(mm); }
  m = rest.match(/\b(24|28|32|36)\s*(?:"|”|″|-?\s*in(?:ch(?:es)?)?\b)/i);
  if(m){ plan.size = Number(m[1]); mark(m); }
  // What's left that we did not read?
  let left = t.split('');
  used.forEach(([a, b]) => { for(let i = Math.max(0, a); i < Math.min(left.length, b); i++) left[i] = ' '; });
  const STOP = new Set('make made makes the a an it its it\'s to and please can you could would i we want like change my our sign be is are of on in at for so that this color colour colors colours text words word letters lettering name names move put set turn use with lake water plate background back backing border frame panel metal base rest line lines more less bit little little a lot much very just instead also too same'.split(' '));
  const residue = left.join('').toLowerCase().replace(/[^a-z ]+/g, ' ').split(/\s+/).filter(w => w && !STOP.has(w));
  const found = Object.keys(plan).length > 0;
  return { plan, complete: found && residue.length === 0, residue };
}
/**
 * One line or several (owner, 9 Oct d: "make the text 1 single line" did
 * nothing -- the layout broke long names in two whenever that made the
 * letters bigger). Read from what they typed, no AI needed:
 * {mode:'single'} | {mode:'multi', count} | null.
 */
function lineWish(text){
  const t = String(text || '').toLowerCase();
  if(/\b(one|1|single|same)\s+line\b|\bon (one|a single) line\b|\b(do ?n[o']?t|no)\s+(split|break|stack)|\bunsplit\b|\bcombine (the )?(words|text|lines|name)|\b(join|merge) (the )?(words|text|lines)|\b(all )?in one line\b/.test(t))
    return { mode: 'single' };
  const m = t.match(/\b(two|2|three|3|separate|multiple|different|own)\s+lines?\b/);
  if(m || /\bsplit\b|\bstack(ed)?\b|\bbreak (it|the (text|words|name|line))\b|\bon top of each other\b/.test(t)){
    const w = m ? m[1] : '';
    return { mode: 'multi', count: (w === 'three' || w === '3') ? 3 : 2 };
  }
  return null;
}
/** A line broken into n pieces at the spaces that make them most even. */
function splitEven(line, n){
  const words = String(line || '').trim().split(/\s+/).filter(Boolean);
  if(words.length < 2 || n < 2) return [words.join(' ')].filter(Boolean);
  n = Math.min(n, words.length);
  let best = null;
  const total = words.join(' ').length;
  const go = (start, left, acc) => {
    if(left === 1){ const parts = acc.concat([words.slice(start).join(' ')]);
      const dev = parts.reduce((a, p) => a + Math.abs(p.length - total / n), 0);
      if(!best || dev < best.dev) best = { dev, parts }; return; }
    for(let i = start + 1; i <= words.length - left + 1; i++) go(i, left - 1, acc.concat([words.slice(start, i).join(' ')]));
  };
  go(0, n, []);
  return best ? best.parts : [words.join(' ')];
}
/**
 * "Want something changed? Just tell us." -- the AI's change set for a FINISHED
 * design, accepted only in shapes the engine understands. Anything not stated
 * is absent (= keep). Called on the server's designEdit reply.
 */
function sanitizeEdit(h){
  if(!h || typeof h !== 'object') return null;
  const out = {};
  if(LOOKS[h.look]) out.look = h.look;
  if(h.colors && typeof h.colors === 'object'){
    if(FINISH_NAMES[h.colors.sign]) out.sign = h.colors.sign;
    if(FINISH_NAMES[h.colors.panel]) out.panel = h.colors.panel;
  }
  if(h.linesChanged === true && Array.isArray(h.lines))
    out.lines = h.lines.map(s => String(s).replace(/\s+/g,' ').trim().slice(0,32)).filter(Boolean).slice(0, 3);
  if(['top','bottom','best'].includes(h.textPlacement)) out.placement = h.textPlacement;
  if(['normal','large','small'].includes(h.textSize)) out.textSize = h.textSize;
  if(['any','nameplate','cut'].includes(h.textStyle)) out.textStyle = h.textStyle;
  if(SIZES.includes(Number(h.size))) out.size = Number(h.size);
  if(Array.isArray(h.addIcons)) out.addIcons = h.addIcons.map(String).filter(i => ICONS.includes(i)).slice(0, 2);
  if(Array.isArray(h.removeIcons)) out.removeIcons = h.removeIcons.map(String).filter(i => ICONS.includes(i));
  if(h.lineMode === 'single' || h.lineMode === 'multi'){ out.lineMode = h.lineMode; out.lineCount = h.lineCount === 3 ? 3 : 2; }
  if(Array.isArray(h.cannot)) out.cannot = h.cannot.map(s => String(s).trim().slice(0,200)).filter(Boolean).slice(0, 3);
  return out;
}
/**
 * Apply a sanitized change set to a draft. Returns { draft, changes } where
 * changes are plain sentences for the customer ("Colors: Candy Blue + ...").
 * A new look brings its own finishes unless colours were named too.
 */
function editDraft(d, plan){
  plan = plan || {};
  const changes = [];
  let n = Object.assign({}, d, { lines: (d.lines || []).slice(), icons: (d.icons || []).slice() });
  if(plan.look && plan.look !== d.look){
    const L = LOOKS[plan.look];
    n = Object.assign(n, { look: plan.look, name: L.name, style: L.style, signColorId: L.sign, panelColorId: L.panel });
    changes.push('Look: ' + L.name);
  }
  if(plan.sign || plan.panel){
    if(plan.sign) n.signColorId = plan.sign;
    if(plan.panel && plan.panel !== n.signColorId) n.panelColorId = plan.panel;
    n.custom = true;
    changes.push('Colors: ' + finishPhrase(n));
  }
  if(plan.lines){
    n.lines = plan.lines.slice();
    changes.push(plan.lines.length ? 'Words: ' + plan.lines.map(l => '“' + l + '”').join(', ') : 'Words removed');
  }
  if(plan.lineMode === 'single'){
    const one = n.lines.join(' ').replace(/\s+/g, ' ').trim();
    if(one){ n.lines = [one.slice(0, 40)]; n.lineMode = 'single'; changes.push('Words on one line'); }
  } else if(plan.lineMode === 'multi'){
    const cnt = plan.lineCount || 2;
    if(n.lines.length < cnt){ const sp = splitEven(n.lines.join(' '), cnt); if(sp.length > n.lines.length) n.lines = sp; }
    n.lineMode = 'multi';
    changes.push(n.lines.length > 1 ? 'Words on ' + (n.lines.length === 3 ? 'three' : 'two') + ' lines' : 'Words kept on one line (too short to split)');
  }
  if(plan.placement && plan.placement !== 'best'){ n.placement = plan.placement; n.placementStated = true; changes.push('Words at the ' + plan.placement); }
  else if(plan.placement === 'best' && d.placementStated){ n.placement = 'best'; delete n.placementStated; changes.push('Words placed where they fit best'); }
  if(plan.textSize){ if(plan.textSize === 'normal') delete n.textSize; else n.textSize = plan.textSize; changes.push(plan.textSize === 'large' ? 'Bigger words' : plan.textSize === 'small' ? 'Smaller words' : 'Normal-size words'); }
  if(plan.textStyle){ if(plan.textStyle === 'any') delete n.textStyle; else n.textStyle = plan.textStyle; changes.push(plan.textStyle === 'nameplate' ? 'Words on a nameplate' : plan.textStyle === 'cut' ? 'Cut-out letters' : 'Lettering style reset'); }
  if(plan.removeIcons && plan.removeIcons.length){
    const before = n.icons.length;
    n.icons = n.icons.filter(i => !plan.removeIcons.includes(i));
    if(n.icons.length < before) changes.push('Removed: ' + plan.removeIcons.join(', '));
  }
  if(plan.addIcons && plan.addIcons.length){
    const add = plan.addIcons.filter(i => !n.icons.includes(i));
    n.icons = n.icons.concat(add).slice(0, 2);
    if(add.length) changes.push('Added: ' + add.join(', '));
  }
  if(plan.size) changes.push('Size: ' + plan.size + '″');
  return { draft: n, changes };
}

const CORE = { lookRankScores, learnedThree, learnedOrder, setLookScores, isSandy, LOOKS, FAMILY_ORDER, FAMILY_INFO, SURPRISE, FINISH_NAMES, ICONS, SIZES, SIZE_INFO, SIZE_LABEL, surpriseFor, lookFit, fallbackLook, looksClash, DIRECTIONS,
  colorsFromText, lakeNameFromSearch, recommendSize, linesFromBrief, briefToDrafts, draftForLook,
  finishPhrase, sanitizeHints, layoutFromText, wordsWish, sanitizeEdit, editDraft, lineWish, splitEven, localEdit };

// Exposed for the offline tests. Harmless in a browser.
if(typeof window !== 'undefined') window.CCSC_ASSIST_CORE = CORE;
if(typeof module !== 'undefined' && module.exports) { module.exports = CORE; return; }

// ════════════════════════════════════════════════════════════════════════════
// SWITCH
// ════════════════════════════════════════════════════════════════════════════
// Read from the raw address, not URLSearchParams: a hand-typed "??lc_assist=1"
// (it happened) parses as a key named "?lc_assist" and silently left this off.
function assistEnabled(){
  try {
    const m = /[?&#]+lc_assist=([01])(?![0-9])/.exec(String(location.search || '') + String(location.hash || ''));
    const q = m ? m[1] : null;
    if(q){ try { sessionStorage.setItem('lc_assist', q); } catch(e){} }
    let v = null; try { v = sessionStorage.getItem('lc_assist'); } catch(e){}
    if(v === '1' || v === '0') return { on: v === '1', why: q ? 'url' : 'session' };
    return { on: (window.LAKE_CAPTURE_CONFIG || {}).assistedDesign === true, why: 'config' };
  } catch(e){ return { on: false, why: 'error' }; }
}
{
  const sw = assistEnabled();
  try { console.log('[assist] ' + (sw.on ? 'on' : 'off') + ' (' + sw.why + ')'); } catch(e){}
  if(!sw.on) return;
}
// Read by lake-capture.js: when the assisted flow is on, capture does not decide
// the path ("manual") -- the shopper does, on the next screen.
window.CCSC_ASSIST_ON = true;

const T = (step, props, extra) => { try { CCSC_TRACK.track(step, props, extra); } catch(e){} };

// ── Learned look order (13 Oct) ─────────────────────────────────────────────
// LookSavi's per-look counts (GET /api/t/<funnel key>/looks). Fetched once per
// page in the background; until they arrive -- or if they never do -- the
// hand-picked order stands. Off: LAKE_CAPTURE_CONFIG.lookLearning = false, or
// ?lc_learn=0 for one browser (sticky for the session; ?lc_learn=1 undoes it).
// The random draw is seeded with the visit id, so one visit sees one order.
(function loadLookRank(){
  try {
    const cfg = window.LAKE_CAPTURE_CONFIG || {};
    const m = /[?&]lc_learn=([01])/.exec(location.search || '');
    if(m){ try { sessionStorage.setItem('lc_learn', m[1]); } catch(e){} }
    let off = cfg.lookLearning === false;
    try { const v = sessionStorage.getItem('lc_learn'); if(v === '0') off = true; else if(v === '1') off = false; } catch(e){}
    const key = String(cfg.lookSaviKey || '').trim();
    if(off || !key || typeof fetch !== 'function') return;
    const base = String(cfg.lookSaviBase || 'https://looksavi.com').replace(/\/+$/, '');
    let sid = ''; try { sid = CCSC_TRACK.sessionId() || ''; } catch(e){}
    let h = 2166136261; for(const c of (sid || String(Date.now()))) h = Math.imul(h ^ c.charCodeAt(0), 16777619);
    const rand = () => { h = (h + 0x6D2B79F5) | 0; let t = Math.imul(h ^ (h >>> 15), 1 | h); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = setTimeout(() => { try { ctl && ctl.abort(); } catch(e){} }, 6000);
    fetch(base + '/api/t/' + encodeURIComponent(key) + '/looks', { signal: ctl ? ctl.signal : undefined, credentials: 'omit' })
      .then(r => r.ok ? r.json() : null)
      .then(stats => {
        clearTimeout(timer);
        const scores = CORE.lookRankScores(stats, rand);
        if(!scores) return;
        CORE.setLookScores(scores);
        window.CCSC_LOOK_RANK = { sessions: stats.sessions, stats: stats.looks, scores,
          order: Object.keys(scores).sort((a, b) => scores[b] - scores[a]) };
      })
      .catch(() => { clearTimeout(timer); });
  } catch(e){}
})();
const T1 = (step, props, extra) => { try { CCSC_TRACK.once(step, props, extra); } catch(e){} };

// ════════════════════════════════════════════════════════════════════════════
// ENGINE — applying a draft to the real builder, through its own functions
// ════════════════════════════════════════════════════════════════════════════
const fontReady = (f) => new Promise(res => { try { ensureFontLoaded(f, res); } catch(e){ res(); } setTimeout(res, 2500); });

// A font that lands after the words were measured: fontReady gives up at 2.5s,
// and document.fonts.load resolves at once for a face whose stylesheet has not
// arrived yet. The sign was then measured and cached in the stand-in font, so
// the real, wider letters ran off their nameplate ("CRATER LAK"). When any font
// finishes loading, re-measure the words that use it and drop the caches.
function refreshLateFont(families){
  if(!window.CCSC_ASSIST_ACTIVE) return;
  const hit = (textBoxes || []).filter(tb => tb.pos && tb.font && families.has(tb.font));
  if(!hit.length) return;
  const ctx = document.getElementById('silhouetteCanvas').getContext('2d');
  hit.forEach(tb => {
    tb.rect = computeNameplate(ctx, tb.value, tb.font, tb.size, tb.pos.x, tb.pos.y, tb.mfgMode === 'nameplate' ? tb.namePad : 0);
    try { evaluateRule(tb); } catch(e){}
  });
  _clearAllFinishCaches();
  try { renderCanvas(); } catch(e){}
}
try {
  document.fonts.addEventListener('loadingdone', (e) => {
    try {
      const fams = new Set((e.fontfaces || []).map(f => String(f.family || '').replace(/^["']|["']$/g, '')));
      if(fams.size) refreshLateFont(fams);
    } catch(_){}
  });
} catch(e){}
const color = (id) => PAINT_COLORS.find(c => c.id === id) || null;

function priceCents(size){
  try {
    const p = (window.LAKE_CAPTURE_CONFIG || {}).variantPrices || {};
    const c = p[String(variantIdFor(size, true))];
    return typeof c === 'number' ? c : null;
  } catch(e){ return null; }
}
const money = (c) => {
  if(typeof c !== 'number') return '';
  try { return _formatMoney(c); } catch(e){ return '$' + Math.round(c/100); }
};

/** Lake pixels inside a rect, as a fraction. Same test the engine uses (<50). */
function lakeShare(rect){
  const sp = silhouettePixels; if(!sp) return 0;
  let n = 0, k = 0;
  const step = Math.max(2, Math.round(Math.min(rect.w, rect.h) / 10));
  for(let y = rect.y; y <= rect.y + rect.h; y += step){
    for(let x = rect.x; x <= rect.x + rect.w; x += step){
      n++;
      const xi = Math.round(x), yi = Math.round(y);
      if(xi < 0 || yi < 0 || xi >= sp.w || yi >= sp.h) continue;
      if(sp.data[(yi*sp.w + xi)*4] < 50) k++;
    }
  }
  return n ? k / n : 0;
}

/** Exact share of a rect's pixels that are lake -- the rule engine's own test. */
function overlapFrac(r){
  const sp = silhouettePixels; if(!sp) return 0;
  const x0 = Math.max(0, Math.round(r.x)), x1 = Math.min(sp.w - 1, Math.round(r.x + r.w));
  const y0 = Math.max(0, Math.round(r.y)), y1 = Math.min(sp.h - 1, Math.round(r.y + r.h));
  let n = 0, k = 0;
  for(let y = y0; y <= y1; y++) for(let x = x0; x <= x1; x++){ n++; if(sp.data[(y*sp.w + x)*4] < 50) k++; }
  return n ? k / n : 0;
}

const FALLBACK_FONT = 'Stardos Stencil';   // a stencil with digits and punctuation
const sleep = (ms) => new Promise(r => setTimeout(r, ms));
/** A real break in a long build so pending taps/clicks run first (12 Oct). */
const yieldToInput = () => new Promise(r => { try { if(window.scheduler && scheduler.yield) return scheduler.yield().then(r, r); } catch(e){} setTimeout(r, 0); });
const render = () => new Promise(res => { let done = false; const fin = () => { if(!done){ done = true; res(); } };
  try { renderCanvas(fin); } catch(e){ fin(); } setTimeout(fin, 1200); });

// A look's preset, fetched once. The same files applyTheme reads.
const _presets = {};
async function lookPreset(slug){
  if(_presets[slug]) return _presets[slug];
  const url = ((window.LAKE_CAPTURE_CONFIG || {}).presetUrls || {})[slug];
  if(!url) throw new Error('look ' + slug + ' is not deployed');
  const p = await fetch(url).then(r => r.ok ? r.json() : null);
  if(!p) throw new Error('look ' + slug + ' did not load');
  return (_presets[slug] = p);
}

/**
 * Put the builder into a look: construction, finishes, corners, font.
 *
 * Not applyTheme(): that also redraws every style thumbnail and theme card and
 * prints a "Starting look" status line -- right for a shopper picking a look
 * in the builder, three times too much work behind a "Creating your designs"
 * screen, and the status line would leak out of it.
 *
 * selectDWStyle starts an unawaited rebuild of the lake bitmap; anything
 * measured against silhouettePixels before it lands is measured against the
 * previous lake. So wait for the swap (bounded), then measure.
 */
async function applyLook(d){
  const p = await lookPreset(d.look);
  const want = d.style || p.doubleWallStyle;
  // Same construction as the last look we applied, and the lake bitmap is the
  // very one that switch produced (nothing has touched the lake since): the
  // engine's style reset would re-render the same three lake bitmaps (~1.5 s,
  // and the wait below would sit out its full timeout). Skip it. Any change of
  // construction, or any lake edit in between, goes through selectDWStyle.
  const same = want === doubleWallStyle && applyLook._px && applyLook._px === silhouettePixels && applyLook._style === want;
  try { if(window.CCSC_COMP_DEBUG) console.log('[assist] applyLook same?', same, { want, cur: doubleWallStyle, px: applyLook._px === silhouettePixels, st: applyLook._style }); } catch(e){}
  if(!same){
    const before = silhouettePixels;
    const rebuilt = selectDWStyle(want);
    if(rebuilt && typeof rebuilt.then === 'function'){
      // The engine hands back its rebuild: wait for exactly that (it used to be
      // polled with a 1.5 s cap, and when the bitmap came back the same object
      // every draft sat out the whole 1.5 s).
      try { await rebuilt; } catch(e){}
    } else {
      const t0 = Date.now();
      while(silhouettePixels === before && Date.now() - t0 < 1500) await sleep(25);
      await sleep(0);   // the rebuild's own .then (island pass for cutouts) runs first
    }
  }
  if(p.roundedCorners != null) roundedCorners = !!p.roundedCorners;
  if(p.standoffMatchPanel != null) standoffMatchPanel = !!p.standoffMatchPanel;
  const src = (p.textBoxes || [])[0] || {};
  if(src.font) _themeTextDefaults = { font:src.font, size:src.size, mfgMode:src.mfgMode, lakeColor:src.lakeColor };
  if(d.signColorId && color(d.signColorId)){ signColor = color(d.signColorId); _signColorChosen = true; }
  if(d.panelColorId && color(d.panelColorId)){ panelColor = color(d.panelColorId); }
  applyLook._px = silhouettePixels; applyLook._style = doubleWallStyle;
  return src.font || 'Block Stencil';
}

// The plate as the lake left it, before we added any room for words. Every
// draft starts from this, so one draft's room never leaks into the next.
let _baseRef = null;
function resetPlate(){
  // The engine derives the reference lazily, on the first panel read after a
  // trace -- so ask for the panel before trusting _panelRefBBox to exist.
  if(!_baseRef){
    try { getPanelBounds(); } catch(e){}
    if(!_panelRefBBox) _recomputePanelRect();
    if(_panelRefBBox) _baseRef = { ..._panelRefBBox };
  }
  if(_baseRef) _panelRefBBox = { ..._baseRef };
  panelShortAxisBoost = 0; panelAlign = 'center';
  _recomputePanelRect();
}

/**
 * Room for the words. The plate is a frozen reference around the lake
 * (_panelRefBBox, saved with the design), so "more room" is that reference
 * grown on the side the words go -- the same state the More Room control and a
 * reopened design read. Grown only by what the words need, and never past the
 * 640 working frame.
 */
function growPlate(side, need){
  if(!_panelRefBBox || need <= 0) return;
  const r = { ..._panelRefBBox };
  if(side === 'top') r.minY = Math.max(0, r.minY - need);
  else r.maxY = Math.min(silhouettePixels.h, r.maxY + need);
  _panelRefBBox = r;
  _recomputePanelRect();
}

/** Free plate between the lake and the panel edge, above and below. */
function bands(){
  const pb = getPanelBounds(); const bb = computeSilhouetteBBox(silhouettePixels);
  if(!pb || !bb) return null;
  return { pb, bb, top: bb.minY - pb.y, bottom: (pb.y + pb.h) - bb.maxY };
}

/**
 * The smallest a draft will set a line. Cut-through letters get 30% over the
 * engine's floor (50%): near the floor the preview's letter-spacing rule shrinks the
 * font and then pushes the letters wider than their box, and the first and last
 * letters are clipped ("EST. 1949" draws as "ST. 194"); seen up to ~1.4x. That is a builder bug
 * for anyone typing small cut text; a draft just stays clear of it.
 */
function minSize(tb){
  const f = minFontSizePxForFont(tb.font, tb.value) || 6;
  return tb.mfgMode === 'open' ? f * 1.5 : f;
}

/** Text that fills a sensible share of the sign, never more than the engine allows. */
function sizeLine(ctx, tb, slot, pb, scale){
  // "Large" / "small" from their notes scales the share of the sign the words
  // aim for; the engine's own clamp and the size floor still have the last word.
  scale = scale || 1;
  // Large words: every line is sized like the first, not as a smaller second line.
  const lead = slot === 0 || scale > 1;
  const target = pb.w * (lead ? 0.66 : 0.46) * Math.min(scale, 1.3);
  const pad = tb.mfgMode === 'nameplate' ? tb.namePad : 0;
  const longSide = Math.max(pb.w, pb.h);
  let size = 20;
  for(let i = 0; i < 4; i++){
    const r = computeNameplate(ctx, tb.value, tb.font, size, 0, 0, pad);
    if(!r || !(r.w > 0)) break;
    size = Math.min(size * target / r.w, longSide * (lead ? 0.075 : 0.05) * scale);
  }
  try { const c = _clampTextSizeToPanel(tb, size); if(Number.isFinite(c)) size = c; } catch(e){}
  size = Math.max(size, minSize(tb));
  return Number.isFinite(size) && size > 0 ? Math.round(size * 10) / 10 : 14;
}

// ── Open space first ────────────────────────────────────────────────────────
// On a long sign, growing the plate for the words makes it longer still. When
// the plate already has a good open area -- the inside of a curve, the empty
// side of a tall lake -- "Best fit" puts the words there instead, at no less
// than 80% of the size they would have had. It works WITH the manufacturing
// rules, never around them:
//   cut letters    -- entirely on solid plate, a margin clear of every lake pixel
//   nameplates     -- each one joined to the lake (5-20% overlap; the rule
//                     engine needs >2%), none hiding much shoreline
//   both           -- inside the plate's edge and corners, off the mounting holes
// and then the rule engine itself is asked: any line it marks warning or error
// and the whole attempt is dropped for the band layout.

/**
 * The lake's shape in a few numbers (8 Oct g), from the master lake bitmap
 * (islands intact, before any style trims them): proportions, how much of its
 * box it fills, how branchy the shoreline is (perimeter^2 / 4*pi*area: ~1.4 a
 * round lake, 25+ a reservoir with arms) and its islands -- land not
 * connected to the frame's edge. Measured on 12 lakes: Crater 2.1% / Flathead
 * 1.6% largest island (prominent), Kerr / Norman specks under 0.1%. ~50 ms,
 * cached per lake.
 */
async function lakeShape(){
  try {
    const src = (typeof _lakeMasterClean !== 'undefined' && _lakeMasterClean) || cleanSilhouette;
    if(!src) return null;
    if(lakeShape._src === src) return lakeShape._v;
    const sp = await cachePixels(src);
    const W = sp.w, H = sp.h, d = sp.data, N = W * H;
    const water = new Uint8Array(N);
    let area = 0, minX = W, maxX = 0, minY = H, maxY = 0, per = 0;
    for(let i = 0; i < N; i++) if(d[i * 4] < 50){ water[i] = 1; area++; const x = i % W, y = (i / W) | 0;
      if(x < minX) minX = x; if(x > maxX) maxX = x; if(y < minY) minY = y; if(y > maxY) maxY = y; }
    if(!area) return null;
    for(let y = 1; y < H - 1; y++) for(let x = 1; x < W - 1; x++){ const i = y * W + x;
      if(water[i] && (!water[i - 1] || !water[i + 1] || !water[i - W] || !water[i + W])) per++; }
    // Land reachable from the frame edge is "outside"; any other land is an island.
    const lab = new Uint8Array(N), st = new Int32Array(N); let t = 0;
    const push = (i) => { if(!water[i] && !lab[i]){ lab[i] = 1; st[t++] = i; } };
    for(let x = 0; x < W; x++){ push(x); push((H - 1) * W + x); }
    for(let y = 0; y < H; y++){ push(y * W); push(y * W + W - 1); }
    const flood = () => { let c = 0; while(t){ const i = st[--t]; c++; const x = i % W;
      if(x > 0) push(i - 1); if(x < W - 1) push(i + 1); if(i >= W) push(i - W); if(i < N - W) push(i + W); } return c; };
    flood();
    let biggest = 0, count = 0;
    for(let i = 0; i < N; i++) if(!water[i] && !lab[i]){ push(i); const c = flood(); count++; if(c > biggest) biggest = c; }
    const bw = maxX - minX + 1, bh = maxY - minY + 1;
    const v = { aspect: bw / bh, fill: area / (bw * bh), branch: (per * per) / (4 * Math.PI * area),
                islands: count, islandFrac: biggest / area };
    v.island = v.islandFrac >= 0.01 && biggest >= 300;
    v.branchy = v.branch >= 25;
    v.river = v.fill < 0.06;
    v.tall = v.aspect < 0.7; v.wide = v.aspect > 1.6;
    lakeShape._src = src; lakeShape._v = v;
    return v;
  } catch(e){ return null; }
}

/** Integral image of lake pixels, so any rectangle's lake count is O(1). */
function lakeIntegral(){
  const sp = silhouettePixels; if(!sp) return null;
  if(lakeIntegral._src === sp) return lakeIntegral._ii;
  const W = sp.w, H = sp.h, ii = new Uint32Array((W + 1) * (H + 1));
  for(let y = 1; y <= H; y++){
    let row = 0;
    for(let x = 1; x <= W; x++){
      if(sp.data[((y-1)*W + (x-1))*4] < 50) row++;
      ii[y*(W+1) + x] = ii[(y-1)*(W+1) + x] + row;
    }
  }
  lakeIntegral._src = sp; lakeIntegral._ii = ii;
  return ii;
}
function lakeCount(r){
  const sp = silhouettePixels, ii = lakeIntegral(); if(!ii) return 0;
  const W = sp.w, H = sp.h;
  const x0 = Math.max(0, Math.floor(r.x)), y0 = Math.max(0, Math.floor(r.y));
  const x1 = Math.min(W, Math.ceil(r.x + r.w)), y1 = Math.min(H, Math.ceil(r.y + r.h));
  if(x1 <= x0 || y1 <= y0) return 0;
  const S = W + 1;
  return ii[y1*S + x1] - ii[y0*S + x1] - ii[y1*S + x0] + ii[y0*S + x0];
}
/** Inside the plate by `inset`, honouring rounded corners. */
function onPlate(r, inset){
  const pb = getPanelBounds(); if(!pb) return false;
  if(r.x < pb.x + inset || r.y < pb.y + inset || r.x + r.w > pb.x + pb.w - inset || r.y + r.h > pb.y + pb.h - inset) return false;
  if(!roundedCorners) return true;
  const cr = panelCornerRadius(pb.w, pb.h);
  const corners = [[pb.x + cr, pb.y + cr, r.x, r.y], [pb.x + pb.w - cr, pb.y + cr, r.x + r.w, r.y],
                   [pb.x + cr, pb.y + pb.h - cr, r.x, r.y + r.h], [pb.x + pb.w - cr, pb.y + pb.h - cr, r.x + r.w, r.y + r.h]];
  for(const [cx, cy, px, py] of corners){
    const inCornerBox = (px < pb.x + cr || px > pb.x + pb.w - cr) && (py < pb.y + cr || py > pb.y + pb.h - cr);
    if(inCornerBox && Math.hypot(px - cx, py - cy) > cr - inset) return false;
  }
  return true;
}
const grow = (r, k) => ({ x: r.x - k, y: r.y - k, w: r.w + 2*k, h: r.h + 2*k });

/** Is the sign long enough, or the band short enough, that open space is worth trying? */
function openSpaceWorthTrying(blockH){
  const pb = getPanelBounds(), b = bands(); if(!pb || !b) return false;
  const aspect = Math.max(pb.w, pb.h) / Math.min(pb.w, pb.h);
  return aspect >= 1.35 || Math.max(b.top, b.bottom) < blockH;
}

function placeInOpenSpace(ctx, tbs, nameplate, opts){
  opts = opts || {};
  const pb = getPanelBounds(); if(!pb || !silhouettePixels) return false;
  // `nameplate` here means JOINED to the lake (a raised sign's nameplate). A
  // nameplate on a Layered Cutout sits on the plate and is laid out like cut
  // letters -- but its plate still takes room.
  const pad = tbs[0].mfgMode === 'nameplate' ? tbs[0].namePad : 0;
  const gap = nameplate ? -2 : 4;
  const base = tbs.map(tb => tb.size);
  const measure = () => tbs.map(tb => computeNameplate(ctx, tb.value, tb.font, tb.size, 0, 0, pad));
  // Upright first; then, for a tall plate, the words running up beside the lake
  // (rotated -90deg, reading bottom to top), line 1 on the left.
  const passes = [false].concat(opts.rotate && pb.h > pb.w * 1.3 ? [true] : []);
  // Grown sizes (f > 1, "bias toward big words") stop at opts.maxSize and at the
  // engine's own panel clamp; a size set already tried is not tried again.
  const sizeAt = (tb, k, f) => {
    let v = base[k] * f;
    if(f > 1){
      if(opts.maxSize) v = Math.min(v, opts.maxSize);
      try { const c = _clampTextSizeToPanel(tb, v); if(Number.isFinite(c)) v = Math.min(v, c); } catch(e){}
      v = Math.max(v, base[k]);
    }
    return Math.max(minSize(tb), Math.round(v * 10) / 10);
  };
  for(const rot of passes){ const tried = new Set(); for(const f of (opts.factors || [1, 0.92, 0.85, 0.8])){
    tbs.forEach((tb, k) => { tb.size = sizeAt(tb, k, f); });
    if(f < 1 && tbs.every((tb, k) => tb.size >= base[k])) continue;      // already at the floor
    const key = tbs.map(tb => tb.size).join(','); if(tried.has(key)) continue; tried.add(key);
    const rects = measure();
    const bw = rot ? rects.reduce((s, r) => s + r.h, 0) + gap * (rects.length - 1) : Math.max(...rects.map(r => r.w));
    const bh = rot ? Math.max(...rects.map(r => r.w)) : rects.reduce((s, r) => s + r.h, 0) + gap * (rects.length - 1);
    let best = null;
    const step = 4;
    for(let y = pb.y; y + bh <= pb.y + pb.h; y += step){
      for(let x = pb.x; x + bw <= pb.x + pb.w; x += step){
        const block = { x, y, w: bw, h: bh };
        if(!onPlate(block, nameplate ? 6 : 8) || hitsStandoff(block, 3)) continue;
        // Each line's own (on-canvas) rectangle, centred in the block.
        let yy = y, xx = x, ok = true, worst = 1;
        const lineRects = rects.map(r => {
          if(rot){ const lr = { x: xx, y: y + (bh - r.w)/2, w: r.h, h: r.w }; xx += r.h + gap; return lr; }
          const lr = { x: x + (bw - r.w)/2, y: yy, w: r.w, h: r.h }; yy += r.h + gap; return lr; });
        for(const lr of lineRects){
          if(nameplate){
            const frac = lakeCount(lr) / (lr.w * lr.h);
            if(frac < 0.05 || frac > 0.20){ ok = false; break; }
            worst = Math.min(worst, 1 - Math.abs(frac - 0.12));
          } else if(lakeCount(grow(lr, 6)) > 0){ ok = false; break; }
        }
        if(!ok) continue;
        // Breathing room: how far the block can grow and stay clear (cut
        // letters) or stay on the plate (nameplates, which touch by design).
        let room = 0;
        for(let k = 4; k <= 40; k += 4){
          const g = grow(block, k);
          if(!onPlate(g, 0) || (!nameplate && lakeCount(g) > 0)) break;
          room = k;
        }
        const centred = rot ? 0 : 1 - Math.abs((x + bw/2) - (pb.x + pb.w/2)) / pb.w;
        const score = room + 20 * centred + (nameplate ? 30 * worst : 0);
        if(!best || score > best.score) best = { score, lineRects };
      }
    }
    if(best){
      tbs.forEach((tb, k) => {
        const lr = best.lineRects[k];
        tb.rotation = rot ? -90 : 0;
        tb.pos = { x: Math.round(lr.x + lr.w/2), y: Math.round(lr.y + lr.h/2) };
        tb.rect = computeNameplate(ctx, tb.value, tb.font, tb.size, tb.pos.x, tb.pos.y, pad);
      });
      // The rule engine has the last word.
      const bad = tbs.some(tb => {
        try { evaluateRule(tb); } catch(e){ return true; }
        const rule = tb.appliedRuleId ? SIGN_RULES.find(r => r.id === tb.appliedRuleId) : null;
        return !rule || rule.validatorResult === 'warning' || rule.validatorResult === 'error';
      });
      if(!bad) return true;
      tbs.forEach(tb => { tb.pos = null; tb.rect = null; tb.rotation = 0; });
      // A grown size the rules refused may still pass at the asked size.
      if(f > 1) continue;
      break;      // the best spot failed the rules; smaller text won't change why
    }
  } }
  tbs.forEach((tb, k) => { tb.size = base[k]; tb.rotation = 0; });
  return false;
}

/**
 * Did a layout come out the way the shop can make it? Every line placed, none
 * overlapping another, all on the plate, cut letters clear of the lake,
 * nameplates joined to it, and nothing the rule engine flags.
 */
function wordsValid(tbs, nameplate){
  const boxes = [];
  for(const tb of tbs){
    if(!tb.pos || !tb.rect) return false;
    const r = getRotatedBBox(tb.rect, tb.rotation || 0);
    if(!onPlate(r, 1)) return false;
    if(nameplate){ if(lakeCount(r) / (r.w * r.h) < 0.03) return false; }
    else if(lakeCount(grow(r, 2)) > 0) return false;
    if(boxes.some(b => !(r.x + r.w <= b.x + 1 || b.x + b.w <= r.x + 1 || r.y + r.h <= b.y + 1 || b.y + b.h <= r.y + 1)) && !nameplate) return false;
    boxes.push(r);
    try { evaluateRule(tb); } catch(e){ return false; }
    const rule = tb.appliedRuleId ? SIGN_RULES.find(x => x.id === tb.appliedRuleId) : null;
    if(!rule || rule.validatorResult === 'error') return false;
  }
  return true;
}

/**
 * Lay the lines out above and/or below the lake. Returns the main side.
 *
 *   Layered Cutout  -> letters CUT through the front plate, so they sit on
 *                      solid plate, clear of the lake: one block, in reading
 *                      order, on one side.
 *   Layered Silhouette -> each line is a nameplate that is PART OF the lake
 *                      piece, so each must touch the lake itself (a plate that
 *                      only touches another plate is a separate piece to the
 *                      rule engine). Line 1 goes on the chosen side, line 2 on
 *                      the other; a third stacks onto line 1, overlapping it.
 *
 * Grows the plate on each side the words need, and shrinks the words only if
 * the lake already fills the working frame.
 */
function layoutLines(ctx, tbs, pref, stated, o){
  if(!tbs.length) return null;
  o = o || {};
  layoutLines.note = null;
  // Joined to the lake: only a raised sign's nameplates. The plate padding goes
  // with the mode either way.
  const nameplate = tbs[0].mfgMode === 'nameplate' && doubleWallStyle === 'raised';
  const padOf = (tb) => tb.mfgMode === 'nameplate' ? tb.namePad : 0;
  const b = bands(); if(!b) return null;
  if(pref === 'best'){
    const est = tbs.reduce((s, tb) => s + computeNameplate(ctx, tb.value, tb.font, tb.size, 0, 0, padOf(tb)).h, 0);
    const grow = o.grow ? { factors: GROW_FACTORS.concat([1, 0.92, 0.85, 0.8]), maxSize: o.maxSize } : undefined;
    // Beside a tall lake on a widened plate: upright, as large as fits; if
    // nothing fits upright this candidate simply loses to the others.
    if(o.upright){ return placeInOpenSpace(ctx, tbs, nameplate, { factors: GROW_FACTORS.concat([1, 0.9, 0.8]), maxSize: o.maxSize }) ? 'open' : null; }
    if((o.forceOpen || openSpaceWorthTrying(est)) && placeInOpenSpace(ctx, tbs, nameplate, grow)) return 'open';
  }
  const first = pref === 'top' ? 'top' : pref === 'bottom' ? 'bottom' : (b.top > b.bottom + 10 ? 'top' : 'bottom');
  const other = first === 'top' ? 'bottom' : 'top';
  const groups = { top: [], bottom: [] };
  const sizes = tbs.map(tb => tb.size);
  // They said which side: try every line there first. On a raised sign each
  // nameplate must still join the lake, so if stacking them on one side breaks
  // that, the lines go back to one side each -- and the draft says so.
  if(nameplate && tbs.length > 1 && stated && (pref === 'top' || pref === 'bottom')){
    layoutBlock(ctx, tbs, first, true);
    tbs.forEach(tb => { if(tb.pos) tb.rect = computeNameplate(ctx, tb.value, tb.font, tb.size, tb.pos.x, tb.pos.y, padOf(tb)); });
    if(wordsValid(tbs, true)) return first;
    tbs.forEach((tb, k) => { tb.pos = null; tb.rect = null; tb.size = sizes[k]; });
    resetPlate();
    layoutLines.note = `One line went on the ${other} so every nameplate stays joined to your lake.`;
  }
  if(nameplate && tbs.length > 1){
    groups[first].push(tbs[0]); groups[other].push(tbs[1]);
    // Reading order on the top side runs downward, toward the lake.
    if(tbs[2]){ if(first === 'top') groups.top.unshift(tbs[2]); else groups.bottom.push(tbs[2]); }
  } else groups[first] = tbs.slice();
  for(const side of [first, other]) if(groups[side].length) layoutBlock(ctx, groups[side], side, nameplate);
  tbs.forEach(tb => {
    if(tb.pos) tb.rect = computeNameplate(ctx, tb.value, tb.font, tb.size, tb.pos.x, tb.pos.y, padOf(tb));
  });
  // A lake that already fills the working frame leaves no band to grow into,
  // and the lines end up on top of each other. Then: any open space at all,
  // down to the size floor, upright or running up beside the lake.
  if(!wordsValid(tbs, nameplate)){
    const keep = tbs.map(tb => ({ pos: tb.pos, rect: tb.rect, size: tb.size }));
    tbs.forEach((tb, k) => { tb.size = sizes[k]; });
    if(placeInOpenSpace(ctx, tbs, nameplate, { rotate:true, factors:[1, 0.9, 0.8, 0.7, 0.6] })) return 'open';
    // Still nothing: a long, thin lake on a plate with no band to grow into.
    // Widen the plate across its short side (More Room) a step at a time until
    // the words fit beside the lake, rather than leave them overlapping.
    const pb0 = getPanelBounds();
    if(pb0 && pb0.h > pb0.w){
      for(let boost = Math.min(1, panelShortAxisBoost + 0.2); boost <= 1.0001; boost += 0.2){
        panelShortAxisBoost = Math.min(1, boost); panelAlign = 'center'; _recomputePanelRect();
        tbs.forEach((tb, k) => { tb.size = sizes[k]; });
        if(placeInOpenSpace(ctx, tbs, nameplate, { rotate:true, factors:[1, 0.9, 0.8, 0.7, 0.6] })) return 'open';
      }
    }
    tbs.forEach((tb, k) => Object.assign(tb, keep[k], { rotation:0 }));
    try { console.warn('[assist] words did not fit cleanly; best effort kept'); } catch(e){}
  }
  return first;
}

/** One block of lines on one side of the lake, in reading order. */
function layoutBlock(ctx, list, side, nameplate){
  const gap = nameplate ? -2 : 4;                 // nameplates overlap (one piece); cut letters breathe
  const edge = nameplate ? 6 : 8;                 // metal kept clear around the block
  const pad = list[0].mfgMode === 'nameplate' ? list[0].namePad : 0;
  const measure = () => list.map(tb => computeNameplate(ctx, tb.value, tb.font, tb.size, 0, 0, pad));
  let rects = measure();
  const height = () => rects.reduce((s, r) => s + r.h, 0) + gap * (rects.length - 1) + edge * 2;
  let b = bands();
  // A nameplate overlaps the lake edge a little, so it needs a touch less room.
  if(b[side] < height()){ growPlate(side, height() - b[side]); b = bands(); }
  for(let i = 0; i < 6 && b[side] < height() * 0.98; i++){
    const before = height();
    list.forEach(tb => { tb.size = Math.max(minSize(tb), Math.round(tb.size * 0.85 * 10) / 10); });
    rects = measure();
    if(height() >= before * 0.999) break;          // at the floor: nothing more to give
  }
  const cx = b.pb.x + b.pb.w / 2;
  if(nameplate){
    // The line nearest the lake walks in from outside until it touches it.
    const near = side === 'bottom' ? 0 : list.length - 1;
    const r0 = rects[near];
    const spanX = b.bb.maxX - b.bb.minX, midX = (b.bb.minX + b.bb.maxX) / 2;
    let found = null;
    for(let d = 0; d < r0.h * 4 && !found; d++){
      for(const dx of [0, -0.12, 0.12, -0.25, 0.25, -0.35, 0.35]){
        const x = midX + dx * spanX;
        const cy = side === 'bottom' ? b.bb.maxY + r0.h/2 - d : b.bb.minY - r0.h/2 + d;
        const r = { x: x - r0.w/2, y: cy - r0.h/2, w: r0.w, h: r0.h };
        if(r.x < b.pb.x || r.x + r.w > b.pb.x + b.pb.w || hitsStandoff(r, 2)) continue;
        // The rule engine calls a plate "on" the lake above 2% overlap; aim
        // for 5% so it reads as joined, not as a plate grazing a shoreline.
        if(overlapFrac(r) >= 0.05){ found = { x, cy }; break; }
      }
    }
    if(!found) found = { x: cx, cy: side === 'bottom' ? b.bb.maxY + r0.h/2 - 2 : b.bb.minY - r0.h/2 + 2 };
    list[near].pos = { x: Math.round(found.x), y: Math.round(found.cy) };
    // The rest stack outward from it, each overlapping the one before.
    const step = side === 'bottom' ? 1 : -1;
    let at = found.cy;
    for(let k = near + step; k >= 0 && k < list.length; k += step){
      const prev = rects[k - step], r = rects[k];
      at += step * (prev.h/2 + gap + r.h/2);
      list[k].pos = { x: Math.round(found.x), y: Math.round(at) };
    }
  } else {
    // Cut letters: a block centred in the band, clear of every lake pixel.
    const blockH = rects.reduce((s, r) => s + r.h, 0) + gap * (rects.length - 1);
    const bandTop = side === 'bottom' ? b.bb.maxY : b.pb.y;
    const bandH = side === 'bottom' ? (b.pb.y + b.pb.h) - b.bb.maxY : b.bb.minY - b.pb.y;
    let y = bandTop + Math.max(edge, (bandH - blockH) / 2);
    list.forEach((tb, k) => {
      const r = rects[k];
      let x = cx;
      for(const dx of [0, -0.1, 0.1, -0.2, 0.2, -0.3, 0.3]){
        const tx = cx + dx * b.pb.w;
        const rr = { x: tx - r.w/2, y, w: r.w, h: r.h };
        if(rr.x < b.pb.x + 4 || rr.x + rr.w > b.pb.x + b.pb.w - 4 || hitsStandoff(rr, 2)) continue;
        if(lakeShare(rr) === 0){ x = tx; break; }
      }
      tb.pos = { x: Math.round(x), y: Math.round(y + r.h/2) };
      y += r.h + gap;
    });
  }
}

/**
 * An icon, through the engine's own path: makeIB, then the same placement tail
 * ibAutoPlace ends with (clamp, size the hit box to the drawn pixels, touching,
 * rules). `at` is a point in the 640 frame; without one, the engine places it.
 * Asset icons (house, cabin...) draw nothing until their SVG is loaded, so that
 * is awaited here rather than left to a later repaint.
 */
async function addIcon(type, at){
  try { if(ICON_ASSET_URLS[type] && !_getAssetIcon(type)) await Promise.race([_loadAssetIcon(type), sleep(2500)]); } catch(e){}
  const ib = makeIB(); ib.type = type;
  iconBoxes.push(ib);
  const idx = iconBoxes.length - 1;
  if(at) placeIconAt(ib, at.cx, at.cy);
  else if(type === 'compass' && doubleWallStyle === 'cutout'){
    // Cut through solid plate, in the clear corner nearest the top: off the
    // lake, off the words, off the mounting holes.
    const probe = computeIconNameplate(ib.size, 0, 0, ib.aspect || 1);
    const s = clearPlateSpot(probe.w, probe.h, textBoxes.filter(t => t.rect).map(t => t.rotation ? getRotatedBBox(t.rect, t.rotation) : t.rect));
    if(s) placeIconAt(ib, s.cx, s.cy); else ibAutoPlace(idx);
  } else if(type === 'compass' && doubleWallStyle === 'raised'){
    // On a Layered Silhouette the compass is cut out of the lake piece so the
    // backing shows through it -- so it has to sit where there IS lake.
    const probe = computeIconNameplate(ib.size, 0, 0, ib.aspect || 1);
    const s = findLakeInteriorSpot(probe.w, probe.h);
    if(s) placeIconAt(ib, s.cx, s.cy); else ibAutoPlace(idx);
  } else ibAutoPlace(idx);
  try { applySmartDefault(ib); } catch(e){}
  try { evaluateRule(ib); } catch(e){}
  return ib;
}
/** Does a rect come too close to one of the four mounting holes? */
function hitsStandoff(r, clear){
  let ko = null; try { ko = _standoffKeepOut(); } catch(e){}
  if(!ko) return false;
  clear = clear || 0;
  return ko.centers.some(c => Math.hypot(Math.max(r.x - c.x, 0, c.x - (r.x + r.w)), Math.max(r.y - c.y, 0, c.y - (r.y + r.h))) < ko.r + clear);
}

/** A spot on bare plate for a w x h object, nearest a top corner, or null. */
function clearPlateSpot(w, h, avoid){
  const pb = getPanelBounds(); if(!pb) return null;
  const inset = 6, clear = 4;
  let best = null, bestD = Infinity;
  for(let y = pb.y + inset + h/2; y <= pb.y + pb.h - inset - h/2; y += 5){
    for(let x = pb.x + inset + w/2; x <= pb.x + pb.w - inset - w/2; x += 5){
      const r = { x: x - w/2, y: y - h/2, w, h };
      const d = Math.min(Math.hypot(x - pb.x, y - pb.y), Math.hypot(pb.x + pb.w - x, y - pb.y));
      if(d >= bestD) continue;
      if(avoid.some(a => !(r.x + r.w + clear < a.x || a.x + a.w + clear < r.x || r.y + r.h + clear < a.y || a.y + a.h + clear < r.y))) continue;
      if(hitsStandoff(r, clear)) continue;
      if(overlapFrac({ x: r.x - clear, y: r.y - clear, w: w + clear*2, h: h + clear*2 }) > 0) continue;
      best = { cx: x, cy: y }; bestD = d;
    }
  }
  return best;
}

function placeIconAt(ib, x, y){
  const tmp = computeIconNameplate(ib.size, x, y, ib.aspect || 1);
  const c = clampToPanelBounds(x, y, tmp.w, tmp.h);
  ib.pos = computeIconNameplate(ib.size, c.cx, c.cy, ib.aspect || 1);
  try { const tight = getIconTightBBox(ib); if(tight) ib.pos = tight; } catch(e){}
  if(ib.pos) ib.pos.touching = isNameplateTouchingLake(ib.pos, ib.rotation || 0);
}

/** Make the builder show this draft. Returns what made it onto the sign. */
/**
 * Lay a set of lines onto the current sign: size, room, placement. Used by a
 * draft and by the simple text editor, so both produce the same result.
 * Replaces textBoxes; icons are left alone. Returns the side used.
 */
async function placeWords(lines, font, placement, opts){
  opts = opts || {};
  const ctx = document.getElementById('silhouetteCanvas').getContext('2d');
  await fontReady(font);
  // 8 Oct (owner): a long line ("John H. Kerr Reservoir") ran off the sign,
  // or was set so small it read as a strip. Lay it out as written; if it does
  // not fit cleanly, or two lines would let the words be clearly bigger, break
  // the longest line and keep whichever is better. Cut letters stack as one
  // block; a raised sign's nameplates must each join the lake, so they only
  // break when the single line does not fit.
  //
  // 8 Oct f/g (owner): small letters look weak and are harder to plasma-cut.
  // A draft's Best fit (not `stated`, not the words editor) now lays out a
  // few candidate compositions and keeps the best by `compScore` -- letter
  // size against the sign, upright over rotated, the lake still the hero:
  //   - as written, words growing in open space (GROW_FACTORS) before shrinking;
  //   - two stacked lines at a natural break, forced into open space (signs
  //     that aren't long, aspect < 1.5) -- must come out ~20% bigger to win;
  //   - a tall, narrow lake whose name only fitted rotated or small: the plate
  //     widened with the builder's own More Room (one side, then the other),
  //     the name upright beside the lake, one line or stacked.
  // Every candidate is a complete layout the rule engine accepted
  // (wordsValid); the manufacturing checks are not touched.
  const auto = (placement === 'best' || !placement) && !opts.stated;
  resetPlate();      // measure THIS lake's plate, not the one the last draft left
  const pb = getPanelBounds();
  const aspect = pb ? Math.max(pb.w, pb.h) / Math.min(pb.w, pb.h) : 1;
  const big = auto ? { grow: true, maxSize: pb ? Math.max(pb.w, pb.h) * 0.14 : undefined } : {};
  const run = (c) => layoutAttempt(ctx, c.lines, font, c.placement || placement, opts, c.isSplit, c.lo);
  const tried = [];
  // 12 Oct (owner: "Browse all styles was slow"): candidates are laid out one
  // after another; a break before each lets a tap be answered in between
  // (it used to wait for the whole run, a second or more on a phone).
  const consider = async (c) => { await yieldToInput(); const t0 = performance.now(); const r = run(c); r.ms = Math.round(performance.now() - t0); r.score = compScore(r, c); tried.push({ c, r }); return r; };
  const first = await consider({ lines, lo: big });
  // 12 Oct (owner): 'single' joins the lines into one; 'asis' keeps them exactly
  // as typed (the customer's own words after a design is chosen). Neither breaks
  // a line -- auto-splitting is for the cards' quick previews only.
  const noSplit = opts.lineMode === 'single' || opts.lineMode === 'asis';
  if(!auto){
    // As before for words they placed: break only a long line that doesn't fit.
    let pick = { c: tried[0].c, r: first };
    const split = noSplit ? null : splitLongest(ctx, lines, font);
    if(split){
      const raisedNP = doubleWallStyle === 'raised' && first.mode === 'nameplate';
      const worth = !first.ok || (!raisedNP && (first.lead < first.asked * 0.85 || first.widthFrac > 0.72));
      if(worth){ const c = { lines: split, isSplit: true, lo: big }, two = run(c);
        if(two.ok && (!first.ok || two.lead >= first.lead * 1.15)) pick = { c, r: two }; }
    }
    placeWords.score = pick.r.ok ? compScore(pick.r, pick.c) : 0;
    return finishWords(run(pick.c));
  }
  // They asked for one line: never break it ("make the text 1 single line").
  const splits = noSplit ? [] : splitOptions(ctx, lines, font);
  // A long line that ran off or shrank: its natural break, laid out normally.
  const raisedNP = doubleWallStyle === 'raised' && first.mode === 'nameplate';
  if(splits.length && (!first.ok || (!raisedNP && (first.lead < first.asked * 0.85 || first.widthFrac > 0.72))))
    await consider({ lines: splits[0], isSplit: true, lo: big });
  // Two stacked lines in open space, on a sign that isn't long.
  if(aspect < 1.5) for(const sp of splits) await consider({ lines: sp, isSplit: true, lo: Object.assign({ forceOpen: true }, big) });
  // Words in a band above/below the lake but small (a lake that fills its
  // plate, e.g. Crater): the band again with bigger letters -- the plate grows
  // for them, which compScore weighs against the lake getting relatively smaller.
  // Also when the words squeezed into open space came out small: the band
  // (placement 'band' = the roomier side, never open space) at bigger sizes.
  let bestB = bestOf(tried);
  if(bestB && bestB.r.textFrac < 0.095){
    const band = (bestB.r.side === 'top' || bestB.r.side === 'bottom') ? undefined : 'band';
    for(const k of [1.35, 1.7]){
      await consider({ lines, placement: band, lo: Object.assign({}, big, { scale: k }) });
      if(splits[0]) await consider({ lines: splits[0], placement: band, isSplit: true, lo: Object.assign({}, big, { scale: k }) });
    }
  }
  // A tall plate whose name came out rotated or small: room beside the lake.
  // Round 2: the least widening that works -- a quarter, half or full step of
  // More Room, the side chosen at half -- so the lake stays big and a nameplate
  // doesn't stretch across a squared-up plate.
  const best0 = bestOf(tried);
  if(pb && pb.h > pb.w * 1.2 && (!best0 || best0.r.rotated || best0.r.textFrac < 0.095)){
    const side = async (boost, align) => { const lo = Object.assign({ forceOpen: true, upright: true, boost, align }, big);
      const a = await consider({ lines, lo }); const b = splits[0] ? await consider({ lines: splits[0], isSplit: true, lo }) : null;
      return Math.max(a.score, b ? b.score : -1); };
    const sMax = await side(0.5, 'max'), sMin = await side(0.5, 'min');
    const align = sMax >= sMin ? 'max' : 'min';
    await side(0.25, align); await side(1, align);
  }
  // Round 2: proportion over maximum size. When the winner's words dominate --
  // big letters, or a nameplate stretching across the sign -- the same
  // composition at 80% and 65% of that size competes; compScore decides.
  const bw = bestOf(tried);
  if(bw && (bw.r.textFrac > 0.15 || (bw.r.mode === 'nameplate' && (bw.r.rects || []).some(q => q.w > bw.r.plateW * 0.65)))){
    for(const k of [0.8, 0.65]){
      const lo = Object.assign({}, bw.c.lo || {}, { maxSize: bw.r.lead * k, scale: ((bw.c.lo || {}).scale || 1) * k });
      await consider({ lines: bw.c.lines, isSplit: bw.c.isSplit, placement: bw.c.placement, lo });
    }
  }
  // ...and the biggest nameplate that was refused only for being a banner:
  // the same layout shrunk to 65% (and 60%) of the plate's width, so a
  // proportionate version of the strong single line gets its chance even when
  // a smaller stacked one was winning.
  const wide = tried.filter(t => t.r.ok && t.r.mode === 'nameplate' && (t.r.rects || []).some(q => q.w > t.r.plateW * 0.65))
                    .sort((a, b) => b.r.textFrac - a.r.textFrac)[0];
  if(wide && wide !== bw){
    const w0 = Math.max(...wide.r.rects.map(q => q.w));
    for(const f of [0.65, 0.6]){
      const k = (wide.r.plateW * f) / w0;
      if(k < 0.5 || k > 0.95) continue;
      const lo = Object.assign({}, wide.c.lo || {}, { maxSize: wide.r.lead * k, scale: ((wide.c.lo || {}).scale || 1) * k });
      await consider({ lines: wide.c.lines, isSplit: wide.c.isSplit, placement: wide.c.placement, lo });
    }
  }
  const pick = bestOf(tried) || tried[0];
  placeWords.score = pick.r.ok ? pick.r.score : 0;
  try { if(window.CCSC_COMP_DEBUG) console.log('[assist] compositions', tried.map(t => ({ lines: t.c.lines.join(' / '), boost: (t.c.lo || {}).boost || 0, align: (t.c.lo || {}).align || '', ok: t.r.ok, side: t.r.side, rot: t.r.rotated, text: +(t.r.textFrac || 0).toFixed(3), score: +(t.r.score || 0).toFixed(3), parts: t.r.parts, ms: t.r.ms })).map(x => JSON.stringify(x)).join('\n')); } catch(e){}
  // The last attempt run is what is on the sign; lay the chosen one down again.
  return finishWords(run(pick.c));
}
function bestOf(list){ return list.filter(t => t.r.ok).sort((a, b) => b.r.score - a.r.score)[0] || null; }

/**
 * How good a composition looks, 0..1 (aesthetics only -- `ok` already says the
 * rule engine accepted it). Mostly letter size: the lead line's size against
 * the sign's long side (0.11 = full marks), upright strongly preferred over
 * rotated; then the lake staying the hero (its box against the plate, which
 * falls when the plate grows for words); then plate proportions not pushed
 * past ~1.8:1. Two stacked lines must earn their place (~20% bigger letters).
 */
function compScore(r, c){
  if(!r.ok) return -1;
  const W = r.plateW || 1, H = r.plateH || 1, long = Math.max(W, H);
  const pb = r.plate || { x: 0, y: 0, w: W, h: H };
  r.textFrac = (r.lead || 0) / long;
  // Typography (0.30): readable is enough. Full marks at 0.10 of the long
  // side (Crater's 43 on a 375 plate, Tahoe's 72 on 597), nothing more for
  // bigger, and past 0.15 the words start to dominate (Kerr's stacked 69 on
  // 494 = 0.14 is the reference "big but right").
  let typo = Math.min(1, r.textFrac / 0.10);
  if(r.textFrac > 0.15) typo -= Math.min(0.5, (r.textFrac - 0.15) * 6);
  if(r.rotated) typo *= 0.6;
  if(c && c.isSplit) typo *= 0.97;       // a broken name costs a little coherence
  if(r.lines > 2) typo *= 0.9;
  const rects = r.rects || [];
  // Composition (0.35): the lake stays the hero (its box against the plate),
  // and the visual weight -- lake box plus the text block -- sits near the
  // middle of the plate (optical balance, not just bounding boxes).
  const lakeShare = (r.lakeArea || 0) / (W * H);
  const lakeS = Math.min(1, lakeShare / 0.5);
  let bal = 1;
  if(r.lakeBox && rects.length){
    const L = r.lakeBox, la = Math.max(1, (L.maxX - L.minX) * (L.maxY - L.minY)) * 0.6;
    let tx = 0, ty = 0, ta = 0;
    rects.forEach(q => { const a = q.w * q.h; tx += (q.x + q.w / 2) * a; ty += (q.y + q.h / 2) * a; ta += a; });
    const cx = (((L.minX + L.maxX) / 2) * la + tx) / (la + ta), cy = (((L.minY + L.maxY) / 2) * la + ty) / (la + ta);
    const d = Math.hypot((cx - (pb.x + W / 2)) / W, (cy - (pb.y + H / 2)) / H);
    bal = Math.max(0, 1 - d * 3);
  }
  const comp = 0.7 * lakeS + 0.3 * bal;
  // Nameplate proportion (0.15): a plate wider than ~65% of the sign starts to
  // read as a banner; total nameplate area past ~9% of the sign dominates.
  let np = 1;
  if(r.mode === 'nameplate' && rects.length){
    const widest = Math.max(...rects.map(q => q.w)) / W;
    const area = rects.reduce((t, q) => t + q.w * q.h, 0) / (W * H);
    np = Math.max(0, 1 - Math.max(0, (widest - 0.65) / 0.3) * 0.6 - Math.max(0, (area - 0.09) / 0.12) * 0.4);
  }
  // Crowding (0.10), soft and aesthetic only (manufacturing clearances are the
  // rule engine's): cut letters with shoreline in a ring around them, any
  // words hugging the plate edge or a mounting hole.
  let crowd = 1;
  if(rects.length){
    const k = long * 0.025, edge = long * 0.04;
    let pen = 0;
    rects.forEach(q => {
      if(r.mode !== 'nameplate'){
        const g = grow(q, k), ring = Math.max(1, g.w * g.h - q.w * q.h);
        pen += Math.min(1, (lakeCount(g) - lakeCount(q)) / ring * 20) * 0.5;
      }
      const m = Math.min(q.x - pb.x, q.y - pb.y, pb.x + W - (q.x + q.w), pb.y + H - (q.y + q.h));
      if(m < edge) pen += Math.min(1, (edge - Math.max(0, m)) / edge) * 0.4;
      if(hitsStandoff(q, long * 0.03)) pen += 0.3;
    });
    crowd = Math.max(0, 1 - pen / rects.length);
  }
  // Coherence (0.10): several lines read as one block -- one centre line,
  // stacked close. Lines on different sides of the lake are allowed, scored low.
  let coh = 1;
  if(rects.length > 1 && !r.rotated){
    const xs = rects.map(q => q.x + q.w / 2);
    const aligned = Math.max(...xs) - Math.min(...xs) <= W * 0.08;
    const sorted = rects.slice().sort((p, q) => p.y - q.y);
    let near = true;
    for(let i = 1; i < sorted.length; i++) if(sorted[i].y - (sorted[i - 1].y + sorted[i - 1].h) > sorted[i].h * 0.8) near = false;
    coh = (aligned ? 0.5 : 0.2) + (near ? 0.5 : 0.1);
  }
  const asp = long / Math.max(1, Math.min(W, H));
  const shapeP = asp <= 1.8 ? 0 : Math.min(0.15, (asp - 1.8) * 0.1);
  r.parts = { typo: +typo.toFixed(2), comp: +comp.toFixed(2), np: +np.toFixed(2), crowd: +crowd.toFixed(2), coh: +coh.toFixed(2) };
  return typo * 0.30 + comp * 0.35 + np * 0.15 + crowd * 0.10 + coh * 0.10 - shapeP;
}
/** How far a draft's words may grow in open space, as multiples of their asked size. */
const GROW_FACTORS = [1.8, 1.6, 1.45, 1.3, 1.15];

/** Break the widest multi-word line in two at the space that balances them best. */
function splitLongest(ctx, lines, font, anyLength){
  const o = splitOptions(ctx, lines, font, anyLength ? 6 : 12);
  return o[0] || null;
}
/**
 * Where a name can break into two lines, best first (up to two). Never inside
 * a word; never a second line that starts with a joining word ("of", "the"…)
 * -- "Lake of the / Ozarks", not "Lake / of the Ozarks"; never a half with
 * fewer than 3 letters. Ranked by how even the two lines are, measured in the
 * real font. A 3+ word name offers its two best breaks ("Smith Mountain /
 * Lake" and "Smith / Mountain Lake") so the layout can judge both.
 */
const JOINERS = /^(of|the|and|&|at|on|in|de|la|du|des|by|to)$/i;
function splitOptions(ctx, lines, font, minLen){
  if(lines.length >= 3) return [];
  minLen = minLen || 6;
  const w = (t) => { ctx.font = `${fontWeightFor(font)} 20px "${font}",sans-serif`; return ctx.measureText(t).width; };
  let k = -1, best = 0;
  lines.forEach((l, i) => { const words = String(l).trim().split(/\s+/); if(words.length > 1 && l.length >= minLen && w(l) > best){ best = w(l); k = i; } });
  if(k < 0) return [];
  const words = String(lines[k]).trim().split(/\s+/);
  const opts = [];
  for(let i = 1; i < words.length; i++){
    const a = words.slice(0, i).join(' '), b = words.slice(i).join(' ');
    if(JOINERS.test(words[i])) continue;
    if([a, b].some(h => h.replace(/[^\p{L}\p{N}]/gu, '').length < 3)) continue;
    opts.push({ a, b, m: Math.max(w(a), w(b)) });
  }
  return opts.sort((x, y) => x.m - y.m).slice(0, 2).map(o => { const out = lines.slice(); out.splice(k, 1, o.a, o.b); return out; });
}

/** One complete layout of `lines` on a fresh plate; leaves it on the sign. */
function layoutAttempt(ctx, lines, font, placement, opts, isSplit, lo){
  // Cutout: letters cut through the plate, showing the backing (lakeColor=true
  // is the engine's "shows the colour behind" for cut letters -- false paints
  // them in the plate's own colour, i.e. invisible). Raised: a nameplate that
  // is part of the lake piece, in the lake's colour.
  //
  // Asked for the other kind in their notes, and the rule engine allows it:
  //  - a nameplate on a Layered Cutout sign is a raised plate on the front,
  //    in the PLATE's colour (rules 146/152; in the lake's colour is an error);
  //  - cut letters on a Layered Silhouette go through the back plate, also in
  //    the plate's colour (the engine's valid combination for raised).
  const cut = doubleWallStyle === 'cutout';
  const mode = cut ? (opts.textStyle === 'nameplate' ? 'nameplate' : 'open')
                   : (opts.textStyle === 'cut' ? 'open' : 'nameplate');
  const lakeColor = cut ? mode === 'open' : mode === 'nameplate';
  const scale = (opts.textSize === 'large' ? 1.3 : opts.textSize === 'small' ? 0.8 : 1) * ((lo && lo.scale) || 1);
  resetPlate();
  textBoxes = [];
  // A tall lake's name beside it: the plate widened with More Room first, so
  // the words are sized for the wider sign.
  if(lo && lo.boost){ panelShortAxisBoost = lo.boost; panelAlign = lo.align || 'center'; _recomputePanelRect(); }
  const pb0 = getPanelBounds();
  const tbs = lines.map((value, slot) => {
    const tb = makeTB();
    tb.value = value; tb.font = font; tb.mfgMode = mode;
    tb.mfgOp = (mode === 'nameplate') ? 'add' : 'subtract';
    tb.lakeColor = lakeColor;
    // The two halves of one broken line are the same size: both "lead".
    tb.size = sizeLine(ctx, tb, isSplit ? 0 : slot, pb0, scale);
    return tb;
  });
  if(isSplit){ const m = Math.min(...tbs.map(t => t.size)); tbs.forEach(t => { t.size = m; }); }
  const asked = tbs.map(tb => tb.size);
  if(!(lo && lo.boost)) widenForWords(ctx, tbs);
  // A broken line reads as one block: treat its side as stated, so raised
  // nameplates try stacking on one side before splitting across the lake.
  const side = layoutLines(ctx, tbs, placement, !!opts.stated || !!isSplit, lo);
  let note = layoutLines.note;
  if(isSplit) note = null;
  if(opts.textSize === 'large' && !note && tbs.some((tb, k) => tb.pos && tb.size < asked[k] * 0.85)){
    note = 'Your words were set as large as this sign allows.';
  }
  const nameplate = mode === 'nameplate' && doubleWallStyle === 'raised';
  let ok = false;
  try { ok = tbs.every(tb => tb.pos && tb.rect) && wordsValid(tbs, nameplate); } catch(e){}
  // One name broken over two lines must read as one block, in order: the
  // halves stacked together, never pushed to opposite sides of the lake (a
  // raised sign did that to "Lake / Norman" and it read "Norman ... Lake").
  if(ok && isSplit && tbs.length >= 2 && !tbs[0].rotation){
    for(let k = 1; k < tbs.length && ok; k++){
      const a = tbs[k - 1], b = tbs[k];
      const gap = (b.rect.y) - (a.rect.y + a.rect.h);
      if(b.pos.y <= a.pos.y || gap > Math.max(a.rect.h, b.rect.h) * 0.6) ok = false;
    }
  }
  const pb = getPanelBounds() || pb0;
  const widthFrac = Math.max(...tbs.map(tb => tb.rect ? getRotatedBBox(tb.rect, tb.rotation || 0).w : 0)) / (pb ? pb.w : 1);
  let lakeArea = 0, lakeBox = null;
  try { const bb = computeSilhouetteBBox(silhouettePixels); lakeBox = bb; lakeArea = (bb.maxX - bb.minX) * (bb.maxY - bb.minY); } catch(e){}
  const rects = tbs.filter(tb => tb.rect).map(tb => getRotatedBBox(tb.rect, tb.rotation || 0));
  return { tbs, side, note, ok, mode, lead: tbs[0] ? tbs[0].size : 0, asked: asked[0] || 0, widthFrac,
           rotated: tbs.some(tb => tb.rotation), lines: tbs.length, plateW: pb ? pb.w : 0, plateH: pb ? pb.h : 0, lakeArea,
           lakeBox, rects, plate: pb ? { x: pb.x, y: pb.y, w: pb.w, h: pb.h } : null };
}

function finishWords(a){
  placeWords.note = a.note;
  a.tbs.forEach(tb => { if(tb.pos) textBoxes.push(tb); });
  textBoxes.forEach(tb => { try { evaluateRule(tb); } catch(e){} });
  return a.side;
}

/**
 * A line wider than the plate (a very long, thin lake) would hang over its
 * edges. Widen the plate across its short side with the builder's own More
 * Room setting (panelShortAxisBoost) -- just enough for the widest line and a
 * margin -- before anything is placed.
 */
function widenForWords(ctx, tbs){
  const pb = getPanelBounds(); if(!pb || !tbs.length) return;
  const pad = tbs[0].mfgMode === 'nameplate' ? tbs[0].namePad : 0;
  const widest = Math.max(...tbs.map(tb => computeNameplate(ctx, tb.value, tb.font, tb.size, 0, 0, pad).w));
  const margin = 18;
  const short = Math.min(pb.w, pb.h), long = Math.max(pb.w, pb.h);
  if(pb.w >= pb.h || widest + 2*margin <= pb.w || long <= short) return;   // only a portrait plate that is too narrow
  panelShortAxisBoost = Math.min(1, (widest + 2*margin - pb.w) / (long - short));
  panelAlign = 'center';
  _recomputePanelRect();
  // Still too narrow at a square plate: the words come down to fit.
  const pb2 = getPanelBounds();
  tbs.forEach(tb => {
    let r = computeNameplate(ctx, tb.value, tb.font, tb.size, 0, 0, pad);
    while(r.w + 2*margin > pb2.w && tb.size > minSize(tb)){ tb.size = Math.max(minSize(tb), tb.size * 0.92); r = computeNameplate(ctx, tb.value, tb.font, tb.size, 0, 0, pad); }
  });
}

/** Two looks' fonts cannot print digits (Brannboll not even an apostrophe). */
function fontCanPrint(font, lines){
  try {
    const re = blockedCharsRegex(font);
    return !(re && lines.some(v => { re.lastIndex = 0; return re.test(v); }));
  } catch(e){ return true; }
}

/** After words or icons change: rules, caches, a fresh frame. */
async function settleSign(opts){
  _clearAllFinishCaches();
  try { updateColorStep(); } catch(e){}
  try { evaluateAllRules(); } catch(e){}
  // The rule engine may not leave cut letters painted the plate's colour.
  if(doubleWallStyle === 'cutout') textBoxes.forEach(tb => { if(tb.mfgMode !== 'nameplate') tb.lakeColor = true; });
  try { _centerOnLake(); } catch(e){}
  await render();
}

/** Make the builder show this draft. Returns what made it onto the sign. */
async function applyDraft(d, opts){
  const _t0 = performance.now();
  let font = await applyLook(d);
  if(d.fontOverride) font = d.fontOverride;   // the font they picked in Words & fonts (a lake edit keeps it)
  await yieldToInput();
  const _t1 = performance.now();
  textBoxes2 = []; iconBoxes = []; iconBoxes2 = [];
  // A draft must not drop their words: if the look's font cannot print them,
  // the whole sign is set in a stencil that can.
  const lineFont = fontCanPrint(font, d.lines) ? font : FALLBACK_FONT;
  d.font = lineFont;
  // No redraws while candidate layouts are measured (lake-capture.js renderCanvas, 12 Oct).
  let side;
  window._lcHoldRender = 1;
  try { side = await placeWords(d.lines, lineFont, d.placement, { textSize: d.textSize, textStyle: d.textStyle, stated: d.placementStated, lineMode: d.lineMode }); }
  finally { window._lcHoldRender = 0; }
  d.note = placeWords.note || null;
  d.score = placeWords.score || 0;
  const _t2 = performance.now();
  for(const type of (d.icons || [])) await addIcon(type);
  await yieldToInput();
  await settleSign();
  d._ms = { look: Math.round(_t1 - _t0), words: Math.round(_t2 - _t1), settle: Math.round(performance.now() - _t2) };

  const placed = { lines: textBoxes.filter(t => t.pos).length, wanted: d.lines.length,
                   icons: iconBoxes.filter(b => b.pos).length, side, look: d.look };
  try {
    const pb = getPanelBounds();
    const lettering = textBoxes.map(t => `${t.mfgMode}${t.lakeColor ? '/lake' : '/plate'}@${Math.round(t.size)}`).join(',');
    console.log('[assist] draft', d.look, 'words:', side, 'plate:', Math.round(pb.w) + 'x' + Math.round(pb.h), lettering, d.note ? 'note: ' + d.note : '', JSON.stringify(d._ms));
  } catch(e){}
  if(placed.lines < placed.wanted || placed.icons < (d.icons || []).length){
    try { console.warn('[assist] draft incomplete', placed); } catch(e){}
  }
  return placed;
}

/**
 * The canvas is square; a wide sign sits in the middle of it with empty bands
 * above and below, which made every draft card and look tile mostly white
 * space. Cut the picture to the sign plus a margin. Same forward transform as
 * the engine's getCanvasCoords, in reverse. Null when it can't (rotated view).
 */
function cropToSign(c){
  try {
    if(silRotation || _viewRotation) return null;
    const pb = getPanelBounds(); if(!pb) return null;
    const ls = _logicalCanvasSize(c), k = c.width / ls.w, cx = ls.w / 2, cy = ls.h / 2;
    const X = v => ((v - cx + silPanX) * silScale + cx) * k;
    const Y = v => ((v - cy + silPanY) * silScale + cy) * k;
    const m = 22 * silScale * k;
    const x0 = Math.max(0, Math.floor(X(pb.x) - m)), y0 = Math.max(0, Math.floor(Y(pb.y) - m));
    const x1 = Math.min(c.width, Math.ceil(X(pb.x + pb.w) + m)), y1 = Math.min(c.height, Math.ceil(Y(pb.y + pb.h) + m));
    if(x1 - x0 < 60 || y1 - y0 < 60) return null;
    const o = document.createElement('canvas'); o.width = x1 - x0; o.height = y1 - y0;
    const g = o.getContext('2d'); g.fillStyle = '#fff'; g.fillRect(0, 0, o.width, o.height);
    g.drawImage(c, x0, y0, o.width, o.height, 0, 0, o.width, o.height);
    return o.toDataURL('image/jpeg', 0.84);
  } catch(e){ return null; }
}

/** A picture of whatever the builder shows now, without editor guides. */
async function snapshot(){
  const c = document.getElementById('silhouetteCanvas');
  await yieldToInput();
  _hideGuidesForCapture = true; _captureOwnsCanvas = true;
  try {
    await render();
    return cropToSign(c) || c.toDataURL('image/jpeg', 0.84);
  } finally {
    _hideGuidesForCapture = false; _captureOwnsCanvas = false;
    try { renderCanvas(); } catch(e){}
  }
}

/** The AI's reading of the brief, or null. Never longer than `ms`. */
async function askAI(brief, ms){
  const ctl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
  const timer = setTimeout(() => { try { ctl && ctl.abort(); } catch(e){} }, ms);
  try {
    const r = await fetch(APPS_SCRIPT_URL, {
      method:'POST', signal: ctl ? ctl.signal : undefined,
      body: JSON.stringify({ action:'designBrief', brief: {
        purpose: brief.purpose, look: brief.look, wants: brief.wants,
        // The words they want printed are sign content, expected and needed;
        // nothing else about them is sent.
        text: brief.text, marker: brief.marker, notes: String(brief.notes || '').slice(0, 500),
      }}),
    });
    const j = await r.json();
    if(!j || !j.ok) return { hints:null, reason: (j && j.reason) || 'error' };
    return { hints: CORE.sanitizeHints(j.hints), reason:null };
  } catch(e){
    return { hints:null, reason: (e && e.name === 'AbortError') ? 'timeout' : 'error' };
  } finally { clearTimeout(timer); }
}

/** The hub's "Just tell us": the current design + their instruction -> a change set. */
async function askEdit(current, instruction, ms){
  const ctl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
  const timer = setTimeout(() => { try { ctl && ctl.abort(); } catch(e){} }, ms);
  try {
    const r = await fetch(APPS_SCRIPT_URL, {
      method:'POST', signal: ctl ? ctl.signal : undefined,
      body: JSON.stringify({ action:'designEdit', current, instruction: String(instruction || '').slice(0, 300) }),
    });
    const j = await r.json();
    if(!j || !j.ok) return { plan:null, reason: (j && j.reason) || 'error' };
    return { plan: CORE.sanitizeEdit(j.edit), reason:null };
  } catch(e){
    return { plan:null, reason: (e && e.name === 'AbortError') ? 'timeout' : 'error' };
  } finally { clearTimeout(timer); }
}

// ════════════════════════════════════════════════════════════════════════════
// UI
// ════════════════════════════════════════════════════════════════════════════
const S = {           // the session
  tellMsg: '',        // the hub's last "Just tell us" answer
  mailed: '', mailErr: '',   // "Email me my design": where it went / why it didn't
  brief: { purpose:null, look:null, wants:[], text:{ family:'', lake:'', year:'', other:'' }, marker:null, notes:'' },
  drafts: [], notes: [], images: [], chosen: -1, size: null, sizeAdvice: null, source: 'rules',
  words: null,        // their own lines once edited; new looks use these, not the interview's
  stale: new Set(),   // draft cards whose picture predates an edit of the words
  shown: 0,
  editing: null,      // { step, label } while a builder control is open from the hub
  custOpen: false,    // Step 3's "Want to change something?" panel (8 Oct e): closed on arrival, kept once they edit
  hubY: null,         // where they were on Step 3 when a tool / Review opened, restored on the way back
  started: 0,
};
let root = null;

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));

function mount(){
  if(root) return root;
  const steps = document.getElementById('stepsContainer');
  if(!steps) throw new Error('no #stepsContainer');
  root = document.createElement('div');
  root.id = 'lcAssist';
  root.className = 'steps-container lc-assist';
  root.setAttribute('aria-live', 'polite');
  steps.parentNode.insertBefore(root, steps);
  const st = document.createElement('style');
  st.textContent = CSS;
  document.head.appendChild(st);
  return root;
}
function showAssist(){ mount(); root.style.display = ''; const s = document.getElementById('stepsContainer'); if(s) s.style.display = 'none'; document.getElementById('lake-capture-app').classList.add('lc-assisting'); }
function showBuilder(){ if(root) root.style.display = 'none'; const s = document.getElementById('stepsContainer'); if(s) s.style.display = 'flex'; }
function screen(html, opts){
  opts = opts || {};
  showAssist();
  // While drafts are being built and compared, the live preview shows whichever
  // one was built last -- not the one in the card -- so it is hidden and the
  // card gets the room.
  document.getElementById('lake-capture-app').classList.toggle('lca-picking', !!opts.picking);
  S.onDrafts = false;   // SCREENS.drafts sets it again after drawing
  const app = document.getElementById('lake-capture-app');
  app.classList.toggle('lca-hub', !!opts.hub);
  // One-scroll Step 3 (10 Oct): the overview and every focused editor scroll
  // as one page on phones (lake-capture.css "MOBILE UX REFACTOR"). An editor
  // ("tool") also holds one browser-history entry, so the phone's Back button
  // closes it instead of leaving the page.
  const flowWas = app.classList.contains('lc-flow');
  app.classList.toggle('lc-flow', !!(opts.hub || opts.tool));
  app.classList.toggle('lca-tool', !!opts.tool);
  if(opts.tool) histPush(); else histDrop();
  try { srowSync(!!opts.styles); } catch(e){ console.warn('[styles]', e); }
  try { if(_hubIO){ _hubIO.disconnect(); _hubIO = null; } } catch(e){}
  app.classList.remove('lca-stk-on');
  const y = opts.keepScroll ? _scrollPos() : null;
  root.innerHTML = html;
  if(flowWas !== app.classList.contains('lc-flow')){ try { updateBuilderViewportOffset(); } catch(e){} }
  if(y) _scrollRestore(y); else { try { root.scrollTop = 0; const sh = shellEl(); if(sh) sh.scrollTop = 0; } catch(e){} }
  try { window._lcAutosave && window._lcAutosave(); } catch(e){}
}
// Step 3 keeps their place across redraws (8 Oct e). The scroller is the shell
// (phones, one-scroll Step 3), #lcAssist (phones, other screens) or the page --
// remember all three.
let _hubIO = null;
const shellEl = () => document.querySelector('#lake-capture-app .ccsc-builder-shell');
function _scrollPos(){ const sh = shellEl(); return { r: root ? root.scrollTop : 0, s: sh ? sh.scrollTop : 0, w: window.scrollY || 0 }; }
function _scrollRestore(y){
  if(!y) return;
  try { if(root) root.scrollTop = y.r; } catch(e){}
  try { const sh = shellEl(); if(sh && y.s != null) sh.scrollTop = y.s; } catch(e){}
  try { if(Math.abs((window.scrollY || 0) - y.w) > 2) window.scrollTo(0, y.w); } catch(e){}
}

// ── Browser Back inside Step 3 (10 Oct) ─────────────────────────────────────
// An open editor or Review holds ONE history entry. The phone's Back button
// pops it, and the popstate closes the editor the way Done does (owner: Back
// keeps changes). Closing with a button drops the entry again (history.back(),
// whose popstate is skipped). The room full screen pushes its own entry on top
// (lake-capture.js CCSC_ROOM); popping that one lands back on ours, which is
// recognised by its state and left alone.
const HIST = { on:false, skip:0 };
function histPush(){
  if(HIST.on) return;
  try { history.pushState({ lcEd: 1 }, ''); HIST.on = true; } catch(e){}
}
function histDrop(){
  if(!HIST.on) return;
  HIST.on = false; HIST.skip++;
  try { history.back(); } catch(e){ HIST.skip--; }
}
window.addEventListener('popstate', (e) => {
  if(HIST.skip > 0){ HIST.skip--; return; }
  if(!HIST.on) return;
  if(e.state && e.state.lcEd) return;      // the room full screen closed; still in the editor
  HIST.on = false;
  safely(async () => {
    if(window.CCSC_ROOM && CCSC_ROOM.closeFull){ try { CCSC_ROOM.closeFull(true); } catch(_){} }
    if(S.editing && !isTool()) return backToHub();   // Review / a builder step
    return commitEditor();
  });
});

// ── Editor sessions: Done / Cancel / Undo (10 Oct, owner's contract) ───────
// Entering an editor records the undo depth and a snapshot of the design.
// Done keeps the changes and leaves ONE undo step for the whole visit (the
// gestures made inside are folded into it); Cancel restores the snapshot and
// leaves no undo step. A size tap, a style tap and an AI change each push one
// undo step of their own (undoPoint). Undo itself is the engine's
// (_undoLast); CCSC_ASSIST.onUndo re-syncs the screen afterwards.
const SESSION = { mark:null };
function designKey(){
  try { return JSON.stringify([srowDesignKey(), signColor && signColor.id, panelColor && panelColor.id, _widgetSize, S.size]); } catch(e){ return ''; }
}
function sessionStart(tool){
  if(SESSION.mark) return;
  try { SESSION.mark = { tool, len: _undoStack.length, snap: _snapshotDesign(), key: designKey(), y: _scrollPos() }; } catch(e){ SESSION.mark = null; }
}
function sessionCommit(){
  const m = SESSION.mark; SESSION.mark = null;
  if(!m) return;
  SAVE.edited = true;   // the save prompt's main trigger: back on Customize after an edit
  try {
    if(_undoStack.length > m.len) _undoStack.length = m.len;
    if(designKey() !== m.key){ _undoStack.push(m.snap); while(_undoStack.length > _UNDO_MAX) _undoStack.shift(); }
    _updateUndoBtn();
  } catch(e){}
}
/** Cancel: the design exactly as it was when the editor opened. */
async function sessionCancel(){
  const m = SESSION.mark; SESSION.mark = null;
  if(!m) return;
  T('editor_cancelled', { tool: m.tool });
  try {
    if(_undoStack.length > m.len) _undoStack.length = m.len;
    if(designKey() !== m.key){
      // Restore through the engine's own undo path (bitmaps, boxes, colours,
      // plate, size), then drop the step it would otherwise leave.
      _undoStack.push(m.snap);
      await onSign(() => _undoLast());
      S.size = parseInt(_widgetSize, 10) || S.size;
    }
    _updateUndoBtn();
  } catch(e){ try { console.warn('[assist] cancel', e); } catch(_){} }
}
function undoPoint(){ try { if(!SESSION.mark) _pushUndo(); } catch(e){} }
const isTool = () => document.getElementById('lake-capture-app').classList.contains('lca-tool');
/** The four-stage bar (lake-capture.js). Stage 2 reads "Pick a design" here (designs come first; the questions are optional). */
function stage(n){ try { window.CCSC_STAGE && window.CCSC_STAGE(n, { stage2:'Pick a design', pastCapture:true }); } catch(e){} }

// One handler for every button on every screen: data-act="name" [data-v="..."].
function wire(acts){
  // Every focused editor shares one footer and one way back (10 Oct):
  // "‹ Back to personalization" and Done keep the changes, Cancel restores.
  acts = Object.assign({ eback: () => commitEditor(), edone: () => commitEditor(), ecancel: () => cancelEditor() }, acts);
  root.onclick = (e) => {
    const b = e.target.closest('[data-act]'); if(!b || !root.contains(b)) return;
    const fn = acts[b.getAttribute('data-act')]; if(!fn) return;
    e.preventDefault();
    safely(() => fn(b.getAttribute('data-v'), b));
  };
}
// The customer-comes-first rule, made mechanical: any failure drops them into
// the normal builder where they would otherwise be.
function safely(fn){
  try {
    const r = fn();
    if(r && typeof r.catch === 'function') r.catch(fail);
  } catch(e){ fail(e); }
}
function fail(e){
  try { console.warn('[assist] falling back to the builder:', e); } catch(_){}
  T('ai_fallback', { reason:'assist-error', source:'rules' });
  try { CCSC_TRACK.error('drafts', 'assist-' + String((e && e.name) || 'error')); } catch(_){}
  window.CCSC_ASSIST_ACTIVE = false;
  try { S.editing = null; document.getElementById('lake-capture-app').classList.remove('lc-assisting','lc-assist-editing'); } catch(_){}
  showBuilder();
  try { _origStepGo(STEPS.size.n); } catch(_){}
}

// 9 Oct (owner): the small pencil cue on the first three cards -- the words
// are theirs to change. Informational only; the edit happens in Step 3.
const MAIL_ICO = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><rect x="3" y="5" width="18" height="14" rx="2.5" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M4 7l8 6 8-6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>';
const PENCIL = '<svg class="lca-pencil" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false"><path d="M11.3 1.6a1.5 1.5 0 0 1 2.1 0l1 1a1.5 1.5 0 0 1 0 2.1L5.6 13.5 2 14.4l.9-3.6z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M9.8 3.1l3.1 3.1" stroke="currentColor" stroke-width="1.5"/></svg>';
const head = (title, sub) => `<div class="lca-head"><h3>${esc(title)}</h3>${sub ? `<p>${esc(sub)}</p>` : ''}</div>`;
const back = (to) => `<button type="button" class="lca-back" data-act="go" data-v="${to}">‹ Back</button>`;
// ── Focused editors (10 Oct, owner's mobile UX spec) ───────────────────────
// Same pattern in every Step 3 tool: a way back, a title, the sign above (it
// scrolls with the page), the common choices, one "More" disclosure, Done +
// Cancel. Back and Done commit (sessionCommit), Cancel restores the design
// exactly as it was when the editor opened (sessionCancel).
const etop = (title, sub) => `<button type="button" class="lca-back lca-eback" data-act="eback">‹ Back to personalization</button>${head(title, sub)}`;
const efoot = (label) => `<div class="lca-efoot"><button type="button" class="lc-btn lc-btn-primary lca-wide" data-act="edone">${esc(label || 'Done')}</button>
  <button type="button" class="lca-link" data-act="ecancel">Cancel</button></div>`;
const _svg = (d) => `<svg class="lca-catico" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;
const ICO = {
  words: _svg('<path d="M5 7V5h14v2"/><path d="M12 5v14"/><path d="M9 19h6"/>'),
  colors: _svg('<path d="M12 3a9 9 0 1 0 0 18c1.1 0 1.7-.8 1.7-1.7 0-.9-.7-1.3-.7-2.2 0-.9.7-1.6 1.6-1.6H16a5 5 0 0 0 5-5C21 6.6 17 3 12 3z"/><circle cx="7.5" cy="11.5" r="1.1"/><circle cx="10" cy="7.5" r="1.1"/><circle cx="14.5" cy="7.5" r="1.1"/>'),
  lake: _svg('<path d="M12 3v18M3 12h18"/><path d="M9 6l3-3 3 3M9 18l3 3 3-3M6 9l-3 3 3 3M18 9l3 3-3 3"/>'),
  marker: _svg('<path d="M12 21s-6.5-5.6-6.5-10.5a6.5 6.5 0 0 1 13 0C18.5 15.4 12 21 12 21z"/><circle cx="12" cy="10.5" r="2.3"/>'),
};
/** Open a Step 3 tool from the overview (an editor session starts here). */
function openTool(v){
  if(v === 'look'){
    if(S.drafts.length) return SCREENS.drafts(S.chosen);
    // A reopened order has no drafts yet: build three looks with its words.
    if(!S.brief.look) S.brief.look = 'surprise';
    return generate();
  }
  const name = { colors:'colors', text:'text', icon:'pickIcon', lake:'lake', plate:'plate' }[v];
  if(!name) return;
  sessionStart(v === 'icon' ? 'markers' : v);
  T('step_viewed', { step: v === 'icon' ? 'markers' : v === 'text' ? 'words' : v });
  return SCREENS[name]();
}
/** Done / Back / the phone's Back button: keep what the editor changed, back to the overview. */
async function commitEditor(){
  try { await onSign(() => {}); } catch(e){}
  if(PLACE.on){ endPlace(); }
  else if(WORDS.on){ return wordsDone(); }
  else if(LAKE.on){ await LAKE_SETTLE(); T('manual_edit_saved', { tool:'lake' }); endLake(); }
  else if(S.editing && S.editing.label === 'plate'){ T('manual_edit_saved', { tool:'plate' }); endPlate(); }
  else if(COLORS.on){ endColors(); T('manual_edit_saved', { tool:'colors' }); }
  else if(S.editing){ return backToHub(); }
  SCREENS.hub();          // sessionCommit runs there
}
/** Cancel: the design exactly as it was when the editor opened. */
async function cancelEditor(){
  try { await onSign(() => {}); } catch(e){}
  if(WORDS.on){ clearTimeout(WORDS.timer); try { await WORDS.chain; } catch(e){} endWords(); }
  if(LAKE.on){ await LAKE_SETTLE(); endLake(); }
  if(PLACE.on) endPlace();
  if(S.editing && S.editing.label === 'plate') endPlate();
  if(COLORS.on) endColors();
  S.editing = null;
  await sessionCancel();
  try { await settleSign(); } catch(e){}
  SCREENS.hub();
}
/** A large selectable tile. `extra` is markup placed above the label (swatches, icon). */
const tile = (act, v, label, on, opts) => {
  opts = opts || {};
  return `<button type="button" class="lca-tile${on ? ' on' : ''}${opts.cls ? ' ' + opts.cls : ''}" data-act="${act}" data-v="${esc(v)}" aria-pressed="${on ? 'true' : 'false'}">
    ${opts.top || ''}${opts.emoji ? `<span class="lca-tile-emoji" aria-hidden="true">${opts.emoji}</span>` : ''}
    <span class="lca-tile-text"><strong>${esc(label)}</strong>${opts.desc ? `<small>${esc(opts.desc)}</small>` : ''}</span>
    ${opts.check ? `<span class="lca-check" aria-hidden="true">✓</span>` : ''}</button>`;
};
/** The builder's own finish photo for a colour id (material sheet, then swatch). */
// No loading="lazy": the theme's CSS hides img[loading] until its lazy-loader
// marks it, and that loader never sees images made after page load -- they
// downloaded and stayed invisible (blank swatches on the theme, not locally).
const finishImg = (id) => { const c = color(id); if(!c) return ''; try { return buildSwatchThumb(c).replace(/\sloading="lazy"/, ''); } catch(e){ return `<i style="background:${c.mid}"></i>`; } };
/** An icon drawn by the builder's own painter, filled in after the screen renders. */
const iconImg = (type) => `<canvas class="lca-ico" data-icon="${esc(type)}" width="96" height="96" aria-hidden="true"></canvas>`;
async function paintIcons(){
  const cvs = root ? root.querySelectorAll('canvas[data-icon]') : [];
  for(const c of cvs){
    const type = c.getAttribute('data-icon');
    try { if(ICON_ASSET_URLS[type] && !_getAssetIcon(type)) await Promise.race([_loadAssetIcon(type), sleep(2500)]); } catch(e){}
    try { const x = c.getContext('2d'); x.clearRect(0, 0, 96, 96); drawIconShape(x, type, 48, 48, 30, '#012335', null); } catch(e){}
  }
}

// ── The screens ─────────────────────────────────────────────────────────────
const SCREENS = {};

// "Personalize these" (owner, 7 Oct 2026): a short loop of questions, then the
// designs again. Questions only -- no live sign beside them (they are rebuilding
// all the cards, not editing one) -- and the look LAST: tapping a look rebuilds
// every design and goes straight back to the cards, where they can choose or
// personalize again. No notes box and no Create button in the loop: less to
// read, and no AI wait (that only runs on free text).
const QSTEPS = () => ['purpose', 'wants'].concat((S.brief.wants || []).includes('marker') ? ['marker'] : []).concat(['look']);
const qstep = (key) => { const st = QSTEPS(), n = st.indexOf(key) + 1; return n ? `<p class="lca-qstep">${n} of ${st.length}</p>` : ''; };

SCREENS.purpose = () => {
  stage(2);
  const o = [['home','Home or lake house','🏡'],['cabin','Cabin','🌲'],['gift','A gift','🎁'],['rental','Vacation rental','🛶'],['business','Business','🏢'],['other','Something else','✨']];
  screen(`${S.drafts.length ? back('drafts') : ''}${qstep('purpose')}${head('What are you making this sign for?', S.drafts.length ? 'A few quick questions and we’ll redo your designs to match.' : '')}
    <div class="lca-tiles">${o.map(([v,l,e]) => tile('pick', v, l, S.brief.purpose === v, { emoji:e })).join('')}</div>`, { picking:true });
  wire({ go: (v) => SCREENS[v](S.shown), pick(v){ S.brief.purpose = v; SCREENS.wants(); } });
};

SCREENS.look = (mode) => {
  stage(2);
  const fams = Object.keys(CORE.FAMILY_INFO);
  // "Show me 3 more" asks this first, then swaps in three new designs in that style.
  const more = mode === 'more';
  screen(`${more ? back('drafts') : back((S.brief.wants || []).includes('marker') ? 'marker' : 'wants')}${more ? '' : qstep('look')}${head(more ? 'Which style should the next 3 be?' : 'What kind of look do you want?', more ? 'We’ll swap in three new designs in that style.' : 'Pick one and we’ll rebuild your designs in it.')}
    <div class="lca-tiles lca-looks">${fams.map(f => {
      const F = CORE.FAMILY_INFO[f], L = LOOKS[F.lead];
      return tile('pick', f, F.name, S.brief.look === f, { desc:F.desc, cls:'lca-look',
        top:`<span class="lca-pair lca-swatches" aria-hidden="true">${(F.swatches || [L.sign, L.panel]).map(finishImg).join('')}</span>` });
    }).join('')}
    ${tile('pick', 'surprise', 'Choose for me', S.brief.look === 'surprise', { desc:'Three different directions to compare', emoji:'✨', cls:'lca-look' })}</div>`, { picking:true });
  wire({ go: (v) => SCREENS[v](), pick(v){ S.brief.look = v; return more ? swapThree(v) : generate(); } });
};

// Three new designs in the chosen style, replacing the three on show (which go
// back into "Or choose another look"). Looks already on show are skipped.
async function swapThree(fam){
  const shown = new Set(S.drafts.map(d => d.look));
  const order = (CORE.FAMILY_ORDER[fam] || []).slice()
    .concat(Object.keys(LOOKS).filter(k => (LOOKS[k].families || []).includes(fam)))
    .concat(fam === 'surprise' ? CORE.SURPRISE : [])
    .concat(Object.keys(LOOKS));
  const looks = [];
  order.forEach(k => { if(LOOKS[k] && !shown.has(k) && !looks.includes(k) && looks.length < 3) looks.push(k); });
  if(!looks.length) return SCREENS.drafts(0);
  T('drafts_more', { count: looks.length, look: fam });
  screen(`<div class="lca-busy" role="status"><div class="lca-spin" aria-hidden="true"></div>
      <h3>Creating 3 new designs…</h3></div>`, { picking:true });
  const app = document.getElementById('lake-capture-app');
  app.classList.add('lca-generating');
  const drafts = [], images = [];
  try {
    for(const k of looks){
      const ready = LP.cache[k];
      if(ready){ drafts.push(ready.draft); images.push(ready.image); continue; }
      const d = CORE.draftForLook(k, S.brief, S.hints, 'best'); if(!d) continue;
      fromTheirs(d);
      await onSign(async () => { await applyDraft(d); images.push(await snapshot()); drafts.push(d); });
    }
  } finally { app.classList.remove('lca-generating'); }
  S.browseFam = null;
  if(drafts.length){ S.returned = S.drafts.map(d => d.look); S.drafts = drafts; S.images = images; S.chosen = -1; S.stale = new Set(); }
  T('drafts_built', { source:'more', count: S.drafts.length });
  SCREENS.drafts(0);
}

SCREENS.wants = () => {
  stage(2);
  // Offered back only when the lake is reliably known (CCSC_LAKE_NAME), not
  // guessed from whatever was typed in the search.
  const guess = (window.CCSC_LAKE_NAME && String(window.CCSC_LAKE_NAME.name || '').trim()) || null;
  if(guess && !S.brief.text.lake) S.brief.text.lake = guess;
  const o = [['family','Family name','👪'],['lake','Lake name','🌊'],['year','Est. year','📅'],['marker','Mark my property','📍'],['icon','Add an icon','🧭'],['other','Something else','✏️']];
  const w = new Set(S.brief.wants);
  const field = (k, label, ph, hint) => w.has(k) ? `<label class="lca-field"><span>${esc(label)}</span>
      <input type="text" data-field="${k}" maxlength="32" value="${esc(S.brief.text[k])}" placeholder="${esc(ph)}" autocomplete="off" />
      ${hint ? `<small>${esc(hint)}</small>` : ''}</label>` : '';
  screen(`${back('purpose')}${qstep('wants')}${head('What should be on your sign?', 'Pick any that apply.')}
    <div class="lca-tiles">${o.map(([v,l,e]) => tile('toggle', v, l, w.has(v), { emoji:e, check:true })).join('')}</div>
    <div class="lca-fields">
      ${field('family','Family name','e.g. THE JOHNSONS')}
      ${field('lake','Lake name','e.g. Lake Norman', guess && S.brief.text.lake === guess ? 'From the lake you picked — change it if you like.' : '')}
      ${field('year','Year established','e.g. 1998')}
      ${field('other','What should it say?','A short line')}
    </div>
    <button type="button" class="lc-btn lc-btn-primary lca-wide" data-act="next">Continue</button>`, { picking:true });
  const keep = () => root.querySelectorAll('[data-field]').forEach(i => { S.brief.text[i.getAttribute('data-field')] = i.value; });
  wire({
    go: (v) => { keep(); SCREENS[v](); },
    toggle(v){ keep(); w.has(v) ? w.delete(v) : w.add(v); S.brief.wants = [...w]; SCREENS.wants();
      const f = root.querySelector(`[data-field="${v}"]`); if(f) try { f.focus(); } catch(e){} },
    next(){ keep(); (w.has('marker') ? SCREENS.marker : SCREENS.look)(); },
  });
};

const MARKERS = [['house','House'],['cabin','Cabin'],['star','Star'],['heart','Heart']];
SCREENS.marker = (all) => {
  stage(2);
  if(!S.brief.marker) S.brief.marker = 'house';
  const shown = all ? ICONS.filter(i => i !== 'compass').map(i => [i, nice(i)]) : MARKERS;
  if(!shown.some(([v]) => v === S.brief.marker) && !all) shown.push([S.brief.marker, nice(S.brief.marker)]);
  screen(`${back('wants')}${qstep('marker')}${head('Which marker should show your property?', 'You’ll tap the sign to place it in a moment.')}
    <div class="lca-tiles lca-icons">${shown.map(([v,l]) => tile('pick', v, l, S.brief.marker === v, { top:iconImg(v), cls:'lca-icontile' })).join('')}</div>
    ${all ? '' : `<button type="button" class="lca-link" data-act="all">See all icons</button>`}
    <button type="button" class="lc-btn lc-btn-primary lca-wide" data-act="next">Continue</button>`, { picking:true });
  paintIcons();
  wire({
    go: (v) => SCREENS[v](),
    pick(v){ S.brief.marker = v; SCREENS.marker(all); },
    all(){ SCREENS.marker(true); },
    next(){ SCREENS.look(); },
  });
};

SCREENS.notes = () => {
  stage(2);
  screen(`${back(S.brief.wants.includes('marker') ? 'marker' : 'wants')}${head('Anything else you want us to know?', 'Optional. Tell us anything about colors, style, wording, or the feel you want.')}
    <textarea class="lca-notes" data-field="notes" maxlength="500" rows="6" placeholder="e.g. White and navy, a little more modern than rustic, and keep it simple.">${esc(S.brief.notes)}</textarea>
    <p class="lca-fine">Your notes may be read by our AI design assistant to create your designs. Please don’t include passwords, payment details or other private information.</p>
    <button type="button" class="lc-btn lc-btn-primary lca-wide" data-act="create">${S.drafts.length ? 'Update my designs' : 'Create my designs'}</button>`);
  wire({
    go: (v) => { S.brief.notes = root.querySelector('[data-field=notes]').value; SCREENS[v](); },
    create(){ S.brief.notes = root.querySelector('[data-field=notes]').value; return generate(); },
  });
};

async function generate(){
  stage(2);
  const STEPS_TXT = ['Choosing your style', 'Matching colors', 'Building your layouts'];
  screen(`<div class="lca-busy" role="status">
      <div class="lca-spin" aria-hidden="true"></div>
      <h3>${window._lcDirectLook ? 'Opening your design…' : 'Creating 3 designs for your lake…'}</h3>
      <ol class="lca-progress">${STEPS_TXT.map((t, k) => `<li data-k="${k}">${esc(t)}</li>`).join('')}</ol>
    </div>`, { picking:true });
  const mark = (k) => { if(!root) return; root.querySelectorAll('.lca-progress li').forEach(li => {
    const j = +li.dataset.k; li.classList.toggle('done', j < k); li.classList.toggle('now', j === k); }); };
  mark(0);
  const app = document.getElementById('lake-capture-app');
  app.classList.add('lca-generating');
  const t0 = Date.now();
  let hints = null, reason = null;
  // Only when there is something to interpret: the structured answers alone
  // are fully handled by the rules, instantly. ~6s total, then rules.
  if(String(S.brief.notes || '').trim()){
    const r = await askAI(S.brief, 6000);
    hints = r.hints; reason = r.reason;
    if(!hints) T('ai_fallback', { reason: reason || 'error', source:'rules', ms: Date.now() - t0 });
  }
  S.source = hints ? 'ai' : 'rules';
  S.hints = hints; S.browseAll = false; S.browseFam = null; S.browseExpanded = false; S.browseOpen = false;
  mark(1);
  S.shape = await lakeShape();
  try { if(S.shape) console.log('[assist] lake shape', JSON.stringify(S.shape)); } catch(e){}
  const { drafts, notes } = CORE.briefToDrafts(S.brief, hints, S.shape);
  // 26 Oct 2026 (lake gallery Phase 2): an ad card's link names the look it
  // showed (?lc_look=). That look leads, is built alone and opened; the other
  // two build when they go Back to the designs (previewLooks, S.directPending).
  const direct = takeDirectLook(drafts, hints);
  S.drafts = drafts; S.notes = notes;
  S.sizeAdvice = CORE.recommendSize(S.brief.purpose, hints && hints.size ? { size:hints.size, why:hints.why } : null);
  if(!S.size) { S.size = S.sizeAdvice.inches; try { selectSize(S.size); } catch(e){} }
  S.images = []; S.stale = new Set();
  const gen = S.genId = (S.genId || 0) + 1;
  // A reopened order keeps its own words through a fresh set of looks.
  if(S.fromOrder) drafts.forEach(fromTheirs);
  else S.words = null;
  resetLookPreviews();
  // Every look's preset file at once (they were fetched one by one, ~0.3-0.5 s each, between builds).
  try { await Promise.all(drafts.map(d => lookPreset(d.look).catch(() => null))); } catch(e){}
  try {
    await onSign(async () => {
      // Built grouped by construction (the one already on the builder first):
      // switching between cutout and raised re-renders the lake (~1.5-3 s), so
      // sandbar, whiteout, cabin is one switch where sandbar, cabin, whiteout
      // was two. Shown in the intended order all the same.
      const lead = drafts.some(d => d.style === doubleWallStyle) ? doubleWallStyle : (drafts[0] || {}).style;
      const order = direct ? [0] : drafts.map((d, k) => k).sort((a, b) =>
        ((drafts[a].style === lead ? 0 : 1) - (drafts[b].style === lead ? 0 : 1)) || a - b);
      // Round 2, per card (9 Oct c -- the cards now appear one by one, so a card
      // is judged and, if weak, given its one fallback BEFORE it is shown; a
      // card never changes under the customer). Only for the default three
      // (nothing chosen by them or the AI). At most two extra builds.
      const auto3 = !direct && !CORE.FAMILY_ORDER[S.brief.look] && !(hints && hints.looks && hints.looks.length) && S.brief.purpose !== 'business' && !S.fromOrder;
      const total = (d) => (d.score || 0) + CORE.lookFit(d, S.shape);
      const BAR = 0.92;
      let budget = 2;   // extra builds, total (~1-1.5 s each)
      const build = async (d) => {
        await applyDraft(d, { snap: true });
        applyLook._px = silhouettePixels;   // nothing touches the lake between builds of one construction
        const ts = performance.now(); const img = await snapshot();
        if(d._ms) d._ms.snap = Math.round(performance.now() - ts);
        try { if(window.CCSC_COMP_DEBUG) console.log('[assist] built', d.look, JSON.stringify(d._ms)); } catch(e){}
        return img;
      };
      let shown = false;
      for(let j = 0; j < order.length; j++){
        if(j === 1) mark(2);
        const k = order[j];
        S.images[k] = await build(drafts[k]);
        if(auto3 && drafts.length === 3 && total(drafts[k]) < BAR){
          const tried = [];
          while(budget > 0){
            const others = drafts.filter((d, jj) => jj !== k).map(d => d.look);
            const used = drafts.map(d => d.look).concat(tried);
            // First the construction the set lacks; if that loses, the other one.
            const alt = tried.length ? CORE.fallbackLook(k, used, S.shape, tried[0], [tried[0], tried[0]], others)
                                     : CORE.fallbackLook(k, used, S.shape, drafts[k].look, others, others);
            if(!alt) break;
            tried.push(alt); budget--;
            const d2 = CORE.draftForLook(alt, S.brief, hints, 'best'); if(!d2) continue;
            if(S.words) d2.lines = S.words.slice();
            try { await lookPreset(alt); } catch(e){}
            const img = await build(d2);
            // Three of one construction only when it is clearly stronger (or an island lake).
            const os = others.map(o => (CORE.LOOKS[o] || {}).style);
            const sameAll = os.every(st => st === d2.style) && !(S.shape && S.shape.island);
            const margin = sameAll ? 0.06 : 0.01;
            try { if(window.CCSC_COMP_DEBUG) console.log('[assist] fallback', drafts[k].look, total(drafts[k]).toFixed(3), '->', alt, total(d2).toFixed(3), sameAll ? '(same construction x3)' : ''); } catch(e){}
            if(total(d2) > total(drafts[k]) + margin){ drafts[k] = d2; S.images[k] = img; break; }
          }
        }
        // The first finished design goes on screen at once; the rest fill in.
        if(gen !== S.genId) continue;
        if(!shown){
          shown = true;
          app.classList.remove('lca-generating');
          T1('draft_first_shown', { ms: Date.now() - t0 });
          S.chosen = 0;
          if(!direct) SCREENS.drafts(0);
        } else fillDraftCard(k);
      }
      try { if(window.CCSC_COMP_DEBUG) console.log('[assist] first three', drafts.map(d => d.look + ' ' + ((d.score || 0) + CORE.lookFit(d, S.shape)).toFixed(3)).join(', ')); } catch(e){}
    });
  } finally { app.classList.remove('lca-generating'); }
  T1('draft_generated', { count: drafts.length, source: direct ? 'link' : S.source, ms: Date.now() - t0 });
  if(direct && gen === S.genId && S.images[0]){ S.directPending = true; return openChosen(0, 'link'); }
  // Normally already on screen (the first card shows as soon as it's built).
  if(gen === S.genId && !S.onDrafts && root && root.querySelector('.lca-busy')){ S.chosen = 0; SCREENS.drafts(0); }
}

/** ?lc_look=<look> from loadPresetFromURL (window._lcDirectLook, set once): put
 * that look first among the drafts. false when there is none or it isn't a look. */
function takeDirectLook(drafts, hints){
  const look = window._lcDirectLook; window._lcDirectLook = null;
  if(!look || !LOOKS[look]) return false;
  const d = CORE.draftForLook(look, S.brief, hints, 'best'); if(!d) return false;
  const at = drafts.findIndex(x => x.look === look);
  if(at >= 0) drafts.splice(at, 1); else if(drafts.length >= 3) drafts.pop();
  drafts.unshift(d);
  return true;
}

// 2026-10 (funnel review): all the drafts at once -- three across on desktop,
// a swipe row on phones -- instead of one big card behind arrows. Someone who
// did not like the first card rarely went looking for the other two (19 saw
// drafts, 8 picked one). No size price here: "From $169 ~~$219~~" (the
// smallest size after the launch discount) until the hub, where they choose the size. "Show me 3 more" builds three more looks with the same
// words; "Personalize these" is the interview, now optional refinement.
/** The words on the draft cards right now, as one line for the field. */
function draftWords(){
  const w = S.words || (S.drafts[0] && S.drafts[0].lines) || [];
  return w.join(' ').replace(/\s+/g, ' ').trim();
}
/** The words field under the cards (9 Oct, owner): one field, all three
 * designs. Focus clears it; typing settles for 900ms (or Enter / leaving the
 * field) and then every card is redrawn with the new words through the same
 * background path "Your words -> Done" uses. Left empty, it goes back. */
function wireDraftWords(){
  const inp = root && root.querySelector('#lcaWordsIn'); if(!inp) return;
  let before = inp.value, timer = 0;
  const apply = () => {
    clearTimeout(timer);
    const text = inp.value.replace(/\s+/g, ' ').trim();
    if(!text || text === draftWords()) return;
    const lines = [text];
    S.words = lines.slice();
    S.drafts.forEach((x, k) => { x.lines = lines.slice(); if(S.images[k]) S.stale.add(k); });
    resetLookPreviews();
    root.querySelectorAll('.lca-gimg').forEach(e => e.classList.add('is-updating'));
    T('draft_words_changed', { chars: text.length });
    // A running preview loop belongs to the old words (its generation is now
    // stale and it is winding down); start the redraw once it has stopped.
    const go = () => { if(LP.running) return setTimeout(go, 120); previewLooks(); };
    go();
  };
  inp.addEventListener('focus', () => { before = inp.value; inp.value = ''; });
  inp.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(apply, 900); });
  inp.addEventListener('keydown', (e) => { if(e.key === 'Enter'){ e.preventDefault(); inp.blur(); } });
  inp.addEventListener('blur', () => { if(!inp.value.trim()){ clearTimeout(timer); inp.value = before; } else { apply(); before = inp.value; } });
}
/** A design finished building while the cards are on screen: show it in its card. */
function fillDraftCard(k){
  if(!S.onDrafts || !root || !SCREENS.drafts._card) return;
  const el = root.querySelector(`.lca-gcard[data-k="${k}"]`); if(!el) return;
  const tmp = document.createElement('div');
  tmp.innerHTML = SCREENS.drafts._card(S.drafts[k], k).trim();
  const fresh = tmp.firstElementChild; if(!fresh) return;
  el.className = fresh.className.replace(' is-focus', '') + (el.classList.contains('is-focus') ? ' is-focus' : '');
  el.innerHTML = fresh.innerHTML;
}
SCREENS.drafts = (i) => {
  stage(2);
  i = Math.max(0, Math.min(S.drafts.length - 1, Number(i) || 0));
  S.shown = i;
  const from = fromPriceHtml();
  const more = moreLooks();
  const card = (d, k) => {
    const L = LOOKS[d.look] || {};
    const ready = !!S.images[k];
    return `<div class="lca-gcard${k === i ? ' is-focus' : ''}${ready ? '' : ' is-pending'}" data-k="${k}">
      <div class="lca-gimg"${ready ? ` data-act="choose" data-v="${k}" role="button" tabindex="0" aria-label="Choose ${esc(d.name)}"` : ''} data-dimg="${k}">${ready ? `<img src="${S.images[k]}" alt="${esc(d.name)} design for your lake" />` : '<div class="lca-gwait" role="status"><i></i><span>Designing…</span></div>'}</div>
      <div class="lca-gmeta"><strong>${esc(d.name)}</strong>
        <small>${esc(CORE.finishPhrase(d))}</small>${d.note ? `<small class="lca-dnote">${esc(d.note)}</small>` : ''}
        <span class="lca-gfrom">${from}</span></div>
      <button type="button" class="lc-btn lc-btn-primary lca-wide lca-choose" data-act="choose" data-v="${k}" aria-label="Choose ${esc(d.name)}"${ready ? '' : ' disabled'}>Choose this design</button>
    </div>`;
  };
  SCREENS.drafts._card = card;
  // 13 Oct (owner): the words field sits ABOVE the cards (no "Optional: your
  // words" line -- the field speaks for itself), and "Browse all styles" is the
  // carousel's last card: a tap adds every other style as cards in the same row
  // (built nearest the one in view first), so they just keep swiping.
  const total = Object.keys(LOOKS).length;
  const bcard = S.browseExpanded ? '' : `<div class="lca-gcard lca-browsecard">
      <button type="button" class="lca-browsego" data-act="browseall" aria-label="Browse all ${total} styles">
        <span class="lca-bswatch" aria-hidden="true">${['sand','blue','hwhite','green','smoke','pmetal'].map(f => finishImg(f)).join('')}</span>
        <strong>Browse all ${total} styles</strong>
        <small>Every style, with your words.</small>
        <span class="lca-bgo">See every style →</span>
      </button>
    </div>`;
  const nCards = S.drafts.length + (bcard ? 1 : 0);
  const dots = S.browseExpanded
    ? `<div class="lca-dots lca-cardcount" aria-hidden="true"><span data-lccount>${i + 1}</span> of ${S.drafts.length}</div>`
    : `<div class="lca-dots" aria-hidden="true">${Array.from({ length: nCards }, (x, k) => `<i${k === i ? ' class="on"' : ''}></i>`).join('')}</div>`;
  screen(`${S.notes.length ? `<p class="lca-note">${S.notes.map(esc).join(' ')}</p>` : ''}
    ${head('Pick your favorite design', 'Every design is customizable. Add your family name, cottage name, lake name, or any words you want in the next step.')}
    <div class="lca-wordsrow lca-wordstop" role="group" aria-label="Words on your sign">
      <button type="button" class="lca-words-ed" data-act="wordsfocus" aria-label="Change the words">${PENCIL}</button>
      <input id="lcaWordsIn" type="text" maxlength="40" value="${esc(draftWords())}" placeholder="Your words" aria-label="Words on your sign" enterkeyhint="done" autocomplete="off" autocapitalize="words" spellcheck="false" />
    </div>
    <div class="lca-grid${bcard ? ' has-browse' : ''}${S.browseExpanded ? ' is-all' : ''}" data-grid>${S.drafts.map(card).join('')}${bcard}</div>
    ${dots}
    <div class="lca-pickrow">
      <button type="button" class="lca-mailcards" data-act="savedrafts">${MAIL_ICO}<span><b>Not ready to choose?</b> Email me this design</span></button>
      <div class="lca-pickalt">
        <button type="button" class="lca-link" data-act="personalize">Personalize with a few questions</button>
      </div>
    </div>
`, { picking:true });
  S.onDrafts = true;
  try { SAVE.bind(); } catch(e){}   // its "back from another app" listener, for Step 2 too
  previewLooks();
  // Which designs were actually seen: all three on a desktop row, the ones
  // swiped to on a phone. Once per design per screen.
  try {
    const grid = root.querySelector('[data-grid]');
    const seen = new Set();
    const io = new IntersectionObserver((ents) => ents.forEach(en => {
      if(!en.isIntersecting) return;
      const k = Number(en.target.getAttribute('data-k'));
      // The expanded carousel builds the cards nearest this one first.
      if(en.intersectionRatio >= 0.3) S.viewK = k;
      if(en.intersectionRatio < 0.6) return;
      if(seen.has(k)) return; seen.add(k);
      const d = S.drafts[k]; if(d) T('draft_viewed', { index: k, look: d.look });
    }), { root: null, threshold: [0.3, 0.6] });
    grid.querySelectorAll('.lca-gcard[data-k]').forEach(c => io.observe(c));
    // The dots under a phone's swipe row follow the card in view (the browse
    // card has its own dot; the expanded row shows "N of M" instead).
    const dots = root.querySelectorAll('.lca-dots i');
    const count = root.querySelector("[data-lccount]");
    let raf = 0;
    grid.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => {
      const cards = grid.querySelectorAll('.lca-gcard'); if(!cards.length) return;
      const mid = grid.scrollLeft + grid.clientWidth / 2; let best = 0, bd = Infinity;
      cards.forEach((c, k) => { const d = Math.abs(c.offsetLeft - grid.offsetLeft + c.offsetWidth / 2 - mid); if(d < bd){ bd = d; best = k; } });
      dots.forEach((d, k) => d.classList.toggle('on', k === best));
      if(count) count.textContent = String(best + 1);
      if(S.browseExpanded) S.viewK = best;
    }); }, { passive:true });
    // Back from a later screen: put the card they had in view back in view.
    const f = grid.querySelector('.is-focus');
    if(f && i > 0 && grid.scrollWidth > grid.clientWidth) grid.scrollLeft = f.offsetLeft - grid.offsetLeft - 16;
  } catch(e){}
  wireDraftWords();
  wire({
    wordsfocus(){ const inp = root.querySelector('#lcaWordsIn'); if(inp) inp.focus(); },
    show: (v) => SCREENS.drafts(v),
    // 13 Oct (owner): the last card opens every other style IN the carousel.
    browseall(){
      if(S.browseExpanded) return;
      const shown = new Set(S.drafts.map(d => d.look));
      const back = (S.returned || []).filter(k => LOOKS[k] && !shown.has(k));
      let rest = Object.keys(LOOKS).filter(k => !shown.has(k) && !back.includes(k));
      const sc = window.CCSC_LOOK_RANK && window.CCSC_LOOK_RANK.scores;
      if(sc) rest = CORE.learnedOrder(rest, sc);
      const first = S.drafts.length;
      back.concat(rest).forEach(k => {
        const ready = LP.cache[k];
        const d = ready ? ready.draft : CORE.draftForLook(k, S.brief, S.hints, 'best');
        if(!d) return;
        if(!ready) fromTheirs(d);
        S.drafts.push(d); S.images.push(ready ? ready.image : undefined);
      });
      S.browseExpanded = true; S.viewK = first;
      T('step_viewed', { step:'browse-all' });
      SCREENS.drafts(first);
      // Bring the first new card into view (the row scrolls; on desktop the page does).
      setTimeout(() => { try {
        const c = root.querySelector(`.lca-gcard[data-k="${first}"]`), grid = root.querySelector('[data-grid]');
        if(!c || !grid) return;
        if(grid.scrollWidth > grid.clientWidth + 4) grid.scrollTo({ left: c.offsetLeft - grid.offsetLeft - 16, behavior:'smooth' });
        else c.scrollIntoView({ block:'nearest', behavior:'smooth' });
      } catch(e){} }, 60);
    },
    restart(){ SCREENS.purpose(); },
    personalize(){ T('personalize_opened'); T1('interview_started'); SCREENS.purpose(); },
    myself(){ CCSC_TRACK.setVariant('manual'); T1('design_myself'); leave(); },
    more(){ SCREENS.look('more'); },
    // 10 Oct: "See more designs" lives in Browse all styles now -- pick a style, then this.
    swap3(v){ S.brief.look = v; return swapThree(v); },
    browseopen(){ S.browseOpen = !(S.browseOpen || !!S.browseFam); if(!S.browseOpen) S.browseFam = null; const y = root.scrollTop; SCREENS.drafts(i); root.scrollTop = y;
      // 12 Oct (owner: "you click the button and it looks like nothing
      // happens"): '.lca-browse' matched the BUTTON (same class), already on
      // screen, so nothing moved. Scroll the styles up: the button row to the
      // top of whatever scrolls here, the first tiles in view under it.
      if(S.browseOpen) setTimeout(() => { try { revealBrowse(); } catch(e){} }, 60); },
    morelooks(){ S.browseAll = true; const y = root.scrollTop; SCREENS.drafts(i); root.scrollTop = y; },
    bfam(v){ S.browseFam = v; S.browseAll = false; T('looks_filtered', { look: v }); const y = root.scrollTop, wy = window.scrollY; SCREENS.drafts(i); root.scrollTop = y; window.scrollTo(0, wy); },
    async browse(v, btn){
      // Already built? Just show it.
      const have = S.drafts.findIndex(d => d.look === v);
      if(have >= 0) return SCREENS.drafts(have);
      const ready = LP.cache[v];
      if(ready){
        S.drafts.push(ready.draft); S.images.push(ready.image);
        T('drafts_built', { source:'browse', look: v, count: S.drafts.length });
        return SCREENS.drafts(S.drafts.length - 1);
      }
      const d = CORE.draftForLook(v, S.brief, S.hints, 'best'); if(!d) return;
      fromTheirs(d);
      if(btn){ btn.disabled = true; btn.classList.add('busy'); const t = btn.querySelector('.lca-tile-text strong'); if(t) t.textContent = 'Building ' + d.name + '…'; }
      document.getElementById('lake-capture-app').classList.add('lca-generating');
      try { await onSign(async () => { await applyDraft(d); S.images.push(await snapshot()); S.drafts.push(d); }); }
      finally { document.getElementById('lake-capture-app').classList.remove('lca-generating'); }
      T('drafts_built', { source:'browse', look: v, count: S.drafts.length });
      SCREENS.drafts(S.drafts.length - 1);
    },
    // 15 Oct (owner, $800 to find a buyer): 8 of 12 ad visitors left Step 2
    // within seconds without touching anything. Nothing captured them; this
    // emails the design in view, with the same follow-ups as the Save sheet.
    savedrafts(){
      const k = draftInView(); if(k < 0) return;
      SAVE.draftK = k;
      SAVE.open('drafts');
    },
    async choose(v, btn){
      const k = Number(v); if(!S.images[k]) return;
      // It may wait for the last designs to finish building: say so.
      if(btn){ btn.disabled = true; btn.textContent = 'Opening your design…'; }
      return openChosen(k, S.source);
    },
  });
};

/** The design card on screen (the one crossing, source 'link'): apply the draft,
 * then -- 26 Oct 2026 (owner, 25 reached Customize, 5 left it) -- straight to
 * Review, where the size and the price are; changing anything is the optional
 * "Make changes" link back to Customize. quickReview() off = Customize first,
 * exactly as before. */
async function openChosen(k, source){
  if(!S.drafts[k]) return;
  S.chosen = k; S.styleLook = null;
  S.onDrafts = false;   // stops the preview builder after its current one
  T1('draft_selected', { look: S.drafts[k].look, index: k, source: source || S.source });
  if(S.designOpen) undoPoint();   // "Try a different design": one Undo returns to the old one
  await onSign(() => applyDraft(S.drafts[k]));
  // Straight to the one thing only they can do -- where their place is --
  // then the review. A newly chosen design arrives with the change panel closed (8 Oct e).
  S.custOpen = false; S.custGlowed = false; S.hubY = null; S.tellMsg = '';
  if(S.brief.wants.includes('marker') && S.brief.marker) return placeByTap(S.brief.marker, 'marker');
  SCREENS.hub();
  if(quickReview()) openEditor(STEPS.finalize.n, 'finalize', null);
}

/** A switch: the theme setting (LAKE_CAPTURE_CONFIG[key], absent = on), overridden
 * for one browser tab by ?<param>=0 / =1 (sticky for the session). */
function flagOn(key, param){
  try {
    const q = new URLSearchParams(location.search).get(param);
    if(q === '0' || q === '1') sessionStorage.setItem(param, q);
    const v = sessionStorage.getItem(param);
    if(v === '0') return false;
    if(v === '1') return true;
  } catch(e){}
  return (window.LAKE_CAPTURE_CONFIG || {})[key] !== false;
}
function quickReview(){ return flagOn('chooseToReview', 'lc_quick'); }

/** Review's own size row (quickReview): the hub's size buttons, beside the price. */
function finSizes(){
  const fp = document.getElementById('lcFinPrice'); if(!fp) return;
  let row = document.getElementById('lcaFinSizes');
  if(!quickReview()){ if(row) row.remove(); const m = document.getElementById('lcaFinMake'); if(m) m.remove(); return; }
  if(!row){
    row = document.createElement('div'); row.id = 'lcaFinSizes'; row.className = 'lca-finsizes';
    fp.parentNode.insertBefore(row, fp);
    row.addEventListener('click', (e) => { const b = e.target.closest('[data-finsize]'); if(!b) return;
      safely(() => { undoPoint(); S.size = Number(b.getAttribute('data-finsize'));
        try { selectSize(S.size); } catch(err){} try { _renderFinPrice(); } catch(err){}
        T('size_chosen', { size: S.size, step: 'review' }); finSizes(); }); });
  }
  const a = S.sizeAdvice || CORE.recommendSize(S.brief.purpose);
  row.innerHTML = `<p class="lca-finsizes-h" id="lcaFinSizeH">Choose your size</p>
    <div class="lca-finsizes-row" role="radiogroup" aria-labelledby="lcaFinSizeH">${SIZES.map(sz => `<button type="button" role="radio" class="lca-fsz${S.size === sz ? ' on' : ''}" data-finsize="${sz}" aria-checked="${S.size === sz}">${sz}&Prime;${a.inches === sz ? '<small>Recommended</small>' : ''}</button>`).join('')}</div>`;
  if(!document.getElementById('lcaFinMake')){
    const m = document.createElement('button'); m.type = 'button'; m.id = 'lcaFinMake'; m.className = 'lca-finmake';
    m.innerHTML = 'Want different words or colors? <b>Make changes</b>';
    m.onclick = () => safely(() => { T('step_viewed', { step: 'review-make-changes' }); backToHub(); });
    fp.parentNode.insertBefore(m, fp.nextSibling);
  }
}

/** The design card on screen (the one crossing the row's middle; S.viewK only follows the expanded browse row), or -1. */
function draftInView(){
  let k = S.images[S.shown] ? S.shown : (S.images[0] ? 0 : -1);
  try {
    const g = root.querySelector('[data-grid]'), gr = g.getBoundingClientRect(), mid = gr.left + g.clientWidth / 2;
    const c = [...g.querySelectorAll('.lca-gcard[data-k]')].find(el => { const r = el.getBoundingClientRect(); return r.left <= mid && r.right >= mid; });
    if(c && S.images[Number(c.getAttribute('data-k'))]) k = Number(c.getAttribute('data-k'));
  } catch(e){}
  return k;
}

/** The next looks for "Show me 3 more": not yet shown, alternating constructions. */
function moreLooks(){
  const shown = new Set(S.drafts.map(d => d.look));
  const rest = Object.keys(LOOKS).filter(k => !shown.has(k));
  const a = rest.filter(k => LOOKS[k].style === 'raised'), b = rest.filter(k => LOOKS[k].style !== 'raised');
  const out = [];
  while(a.length || b.length){ if(b.length) out.push(b.shift()); if(a.length) out.push(a.shift()); }
  return out;
}

/** A new look keeps what is theirs: their edited words, and a reopened order's icons. */
function fromTheirs(d){
  if(S.words) d.lines = S.words.slice();
  if(S.fromOrder && S.orderIcons) d.icons = S.orderIcons.slice();
  return d;
}

// ── Look previews ───────────────────────────────────────────────────────────
// Each "Or choose another look" tile shows their own sign in that look, built
// the same way as the three drafts -- one at a time, in the background, while
// they compare. Building draws on the one real builder, so every build (these,
// the drafts, browse, choose) takes turns through onSign(). choose() re-applies
// its draft, so whatever a preview last drew never ends up in their design.
const LP = { cache:{}, failed:{}, gen:0, running:false, lock:Promise.resolve() };
function onSign(fn){ const p = LP.lock.then(fn, fn); LP.lock = p.catch(() => {}); return p; }
function resetLookPreviews(){ LP.gen++; LP.cache = {}; LP.failed = {}; }
function previewLooks(){
  if(LP.running) return;
  LP.running = true;
  const gen = LP.gen;
  const live = () => gen === LP.gen && S.onDrafts;
  (async () => {
    try {
      await sleep(400);   // let the drafts screen paint and settle first
      // Draft cards drawn before their words were edited, first.
      while(live() && S.stale.size){
        const j = S.stale.values().next().value; S.stale.delete(j);
        const d = S.drafts[j]; if(!d) continue;
        await onSign(async () => {
          if(gen !== LP.gen) return;
          try { await applyDraft(d); S.images[j] = await snapshot(); } catch(e){ try { console.warn('[assist] card refresh', e); } catch(_){} }
        });
        const img = S.onDrafts && root && root.querySelector(`[data-dimg="${j}"] img`);
        if(img){ img.src = S.images[j]; if(img.parentElement) img.parentElement.classList.remove('is-updating'); }
      }
      // The expanded carousel (13 Oct): build the unbuilt cards nearest the one
      // in view first; each lands in its own card (fillDraftCard).
      while(live() && (S.browseExpanded || S.directPending)){
        let j = -1, bd = Infinity; const v = S.viewK || 0;
        S.drafts.forEach((d, k) => { if(!S.images[k] && !(d && d._failed)){ const dd = Math.abs(k - v); if(dd < bd){ bd = dd; j = k; } } });
        if(j < 0){ S.directPending = false; break; }
        const d = S.drafts[j];
        await onSign(async () => {
          if(!live() || S.images[j]) return;
          try { await applyDraft(d); S.images[j] = await snapshot(); T('drafts_built', { source:'browse', look: d.look, count: S.drafts.length }); }
          catch(e){ d._failed = true; try { console.warn('[assist] browse card', d.look, e); } catch(_){} }
        });
        if(live()) fillDraftCard(j);
        await sleep(30);
      }
      // Browse tiles only while "Browse all styles" is open (9 Oct d): built
      // behind a closed panel they held the one builder, so "Choose this
      // design" waited for a preview nobody could see.
      while(live() && (S.browseOpen || S.browseFam)){
        const k = browseList().find(x => !LP.cache[x] && !LP.failed[x]);
        if(!k) break;
        await onSign(async () => {
          if(!live() || LP.cache[k]) return;
          const d = CORE.draftForLook(k, S.brief, S.hints, 'best');
          if(!d){ LP.failed[k] = true; return; }
          fromTheirs(d);
          try { await applyDraft(d); const image = await snapshot(); if(gen === LP.gen) LP.cache[k] = { draft:d, image }; }
          catch(e){ LP.failed[k] = true; try { console.warn('[assist] look preview', k, e); } catch(_){} }
        });
        if(gen === LP.gen) fillLookTile(k);
        await sleep(50);
      }
    } finally { LP.running = false; }
  })();
}
/** Swap one tile's placeholder for its preview, leaving the rest of the screen alone. */
function fillLookTile(k){
  try {
    const b = root && root.querySelector(`[data-act="browse"][data-v="${k}"] .lca-lookprev`);
    if(b) b.outerHTML = lookPrev(k);
  } catch(e){}
}

/** "Or choose another look": every look not already in the carousel. */
// "Or choose another look" filter: a style family, or 'all'. Unset = the style
// they picked last (S.brief.look -- "Show me 3 more" / Personalize), else all.
function browseFam(){
  const f = S.browseFam || S.brief.look;
  return CORE.FAMILY_INFO[f] ? f : 'all';
}
function browseRest(){
  const shown = new Set(S.drafts.map(d => d.look));
  // Looks just swapped out by "Show me 3 more" come back first.
  const back = (S.returned || []).filter(k => LOOKS[k] && !shown.has(k));
  const fam = browseFam();
  const inFam = (k) => fam === 'all' || (LOOKS[k].families || []).includes(fam);
  let rest = Object.keys(LOOKS).filter(k => !shown.has(k) && !back.includes(k));
  // 13 Oct: in learned order once LookSavi's counts are in (Sand looks spread out).
  const sc = window.CCSC_LOOK_RANK && window.CCSC_LOOK_RANK.scores;
  if(sc) rest = CORE.learnedOrder(rest, sc);
  return back.concat(rest).filter(inFam);
}
// A family is short enough to show whole; "All" keeps the first six + "See all".
function browseList(){ const rest = browseRest(); return (S.browseAll || browseFam() !== 'all') ? rest : rest.slice(0, 6); }
function lookPrev(k){
  const L = LOOKS[k], p = LP.cache[k];
  return `<span class="lca-lookprev" aria-hidden="true">${p ? `<img class="lca-lookimg" src="${p.image}" alt="" />`
    : `<span class="lca-pair">${finishImg(L.sign)}${finishImg(L.panel)}</span>${LP.failed[k] ? '' : '<em>Preparing preview…</em>'}`}</span>`;
}
function revealBrowse(){
  const row = root.querySelector('.lca-pickrow'), sec = root.querySelector('div.lca-browse');
  if(!row || !sec) return;
  let sc = row.parentElement;
  while(sc && sc !== document.body){
    const o = getComputedStyle(sc).overflowY;
    if((o === 'auto' || o === 'scroll') && sc.scrollHeight > sc.clientHeight + 4) break;
    sc = sc.parentElement;
  }
  const top = row.getBoundingClientRect().top;
  if(!sc || sc === document.body){ window.scrollBy({ top: top - ((window._lcHeaderH && _lcHeaderH()) || 0) - 12, behavior: 'smooth' }); return; }
  const delta = top - sc.getBoundingClientRect().top - 12;
  if(delta > 4) sc.scrollTo({ top: sc.scrollTop + delta, behavior: 'smooth' });
}
function browseLooks(){
  const rest = browseRest();
  const list = browseList(), fam = browseFam();
  const chip = (v, label) => `<button type="button" class="lca-fam${fam === v ? ' on' : ''}" data-act="bfam" data-v="${v}" aria-pressed="${fam === v}">${esc(label)}</button>`;
  return `<div class="lca-browse">
    <h4 class="lca-sub">Or choose another look</h4>
    <p class="lca-fine">We’ll build it with your words so you can compare.</p>
    <div class="lca-fams" role="group" aria-label="Filter looks by style">${chip('all', 'All')}${Object.keys(CORE.FAMILY_INFO).map(f => chip(f, CORE.FAMILY_INFO[f].name)).join('')}</div>
    ${list.length ? `<div class="lca-tiles lca-looks lca-browse-grid">${list.map(k => { const L = LOOKS[k];
      return tile('browse', k, L.name, false, { desc:L.desc, cls:'lca-look', top: lookPrev(k) }); }).join('')}</div>`
      : `<p class="lca-fine">All the ${esc((CORE.FAMILY_INFO[fam] || {}).name || '')} looks are already above.</p>`}
    ${fam === 'all' && !S.browseAll && rest.length > 6 ? `<button type="button" class="lca-link" data-act="morelooks">See all ${rest.length} looks</button>` : ''}
    ${fam !== 'all' && list.length ? `<button type="button" class="lca-pill lca-swap3" data-act="swap3" data-v="${esc(fam)}">Show 3 new ${esc((CORE.FAMILY_INFO[fam] || {}).name || '')} designs</button>` : ''}
  </div>`;
}

/** The automatic launch discount, in dollars (wording only; Shopify applies it). */
function launchOff(){ const n = Number((window.LAKE_CAPTURE_CONFIG || {}).launchDiscount || 0); return n > 0 ? n : 0; }
/** "From $169 <s>$219</s>": the smallest layered size after the launch discount
 *  -- the same maths as the hero, the hub's size cards and Finalize. */
function fromPriceHtml(){
  const c = priceCents(SIZES[0]); if(c == null) return '';
  const off = launchOff() * 100;
  return off ? `From ${esc(money(Math.max(0, c - off)))} <s>${esc(money(c))}</s>` : `From ${esc(money(c))}`;
}
/** A size card's price: the list price, and what it comes to after the launch discount. */
function sizePrice(sz){
  const c = priceCents(sz);
  if(c == null) return '';
  const off = launchOff() * 100;
  return off ? `<b><s>${esc(money(c))}</s> ${esc(money(Math.max(0, c - off)))}</b>` : `<b>${esc(money(c))}</b>`;
}
/** The four size cards; used on the hub. */
function sizeCards(){
  const a = S.sizeAdvice || CORE.recommendSize(S.brief.purpose);
  return `<div class="lca-sizes">${SIZES.map(sz => `<button type="button" class="lca-size${S.size === sz ? ' on' : ''}" data-act="size" data-v="${sz}" aria-pressed="${S.size === sz}">
      ${a.inches === sz ? '<span class="lca-badge">Recommended</span>' : ''}
      <strong>${sz}&Prime;</strong><span>${esc(CORE.SIZE_LABEL[sz] || '')}</span>${sizePrice(sz)}</button>`).join('')}</div>
    ${launchOff() ? `<p class="lca-launch">Launch special: $${launchOff()} off, applied automatically at checkout.</p>` : ''}
    <p class="lca-sizehelp">${a.inches === S.size ? `<b>Why ${a.inches}″?</b> ${esc(a.why)}` : esc(SIZE_INFO[S.size] || '')}</p>`;
}

/** Step 3's price (8 Oct e): what they pay for the chosen size, the list price
 *  struck, and that the launch discount is already in it. Same priceCents /
 *  launchOff as the cards and Finalize; display only -- FIRST50 is applied by
 *  Shopify, once. `short` = the sticky bar's one-liner. */
function hubPrice(short){
  const c = priceCents(S.size); if(c == null) return '';
  const off = launchOff() * 100, now = Math.max(0, c - off);
  if(short) return `<span class="lca-stkp"><b>${esc(money(now))}</b>${off ? ` <s>${esc(money(c))}</s>` : ''} · ${S.size}″</span>`;
  return `<div class="lca-price" id="lcaPrice" aria-live="polite"><span class="lca-pnow">${esc(money(now))}</span>${off ? ` <s class="lca-pwas">${esc(money(c))}</s>
    <span class="lca-psave">$${launchOff()} launch savings included</span>` : ''}</div>`;
}
/** Phones: a slim price + "Review sign" bar once the real button is off screen.
 *  Never with the button in view, never while typing, never on desktop. */
function hubSticky(){
  const bar = root.querySelector('#lcaSticky'), btn = root.querySelector('#lcaReview');
  if(!bar || !btn) return;
  const price = root.querySelector('.lca-pricerow');
  const phone = () => window.matchMedia('(max-width: 900px)').matches;
  // 10 Oct: shown only while NEITHER the price nor the Review button is on
  // screen -- the price used to show twice (bar + the price right under it).
  const seen = new Set();
  let off = false, typing = false;
  const set = () => {
    off = seen.size === 0;
    const on = phone() && off && !typing;
    bar.classList.toggle('on', on); bar.setAttribute('aria-hidden', on ? 'false' : 'true');
    const b = bar.querySelector('button'); if(b) b.tabIndex = on ? 0 : -1;
    document.getElementById('lake-capture-app').classList.toggle('lca-stk-on', on);
  };
  try {
    const sc = root.scrollHeight > root.clientHeight + 4 && getComputedStyle(root).overflowY !== 'visible' ? root : null;
    _hubIO = new IntersectionObserver((en) => { en.forEach(e => {
      // Off screen = scrolled past it (above), or not reached yet (below the fold).
      if(e.isIntersecting) seen.add(e.target); else seen.delete(e.target); }); set(); }, { root: sc, threshold: 0 });
    _hubIO.observe(btn); if(price) _hubIO.observe(price);
  } catch(e){ return; }
  root.addEventListener('focusin', (e) => { if(e.target.matches('input,textarea,select')){ typing = true; set(); } });
  root.addEventListener('focusout', (e) => { if(e.target.matches('input,textarea,select')){ typing = false; set(); } });
}

SCREENS.size = () => SCREENS.hub();

// ── Tap to place ────────────────────────────────────────────────────────────
// "A cross hair, the object lands where you tap, THEN it is draggable." The
// canvas is put on the Objects step behind our own panel, because that is the
// step on which the engine lets icons be dragged; the wizard stays hidden.
//
// One capture-phase listener on the document runs BEFORE the canvas's own
// mousedown/touchstart. It drops the icon at the tap and lets the event carry
// on, so the engine's startDrag finds the icon right under the finger: the
// same press that places it can drag it. Afterwards a press ON the icon is left
// entirely to the engine (move, resize, rotate handles); a press anywhere else
// moves the icon there -- so a tap can never start dragging the lake instead.
const PLACE = { on:false, ib:null, type:null };

/** Canvas client point -> the 640 design frame. Mirrors lake-capture.js's getCanvasCoords. */
function designPoint(canvas, clientX, clientY){
  const rect = canvas.getBoundingClientRect();
  const localX = clientX - rect.left, localY = clientY - rect.top;
  const ls = _logicalCanvasSize(canvas);
  const k = ls.w / rect.width;
  const ccx = ls.w / 2, ccy = ls.h / 2;
  const v = _unrotateViewPoint(localX * k - ccx, localY * k - ccy);
  const ux = v.x / silScale, uy = v.y / silScale;
  let rx = ux, ry = uy;
  if(silRotation){ const r = -silRotation * Math.PI / 180, c = Math.cos(r), sn = Math.sin(r); rx = ux*c - uy*sn; ry = ux*sn + uy*c; }
  return { cx: Math.round(rx + ccx - silPanX), cy: Math.round(ry + ccy - silPanY) };
}

function onPlacePress(e){
  if(!PLACE.on){
    // 7 Oct 2026 (owner): on "Add a marker or icon", an icon ALREADY on the
    // sign could not be moved until a new one was picked. A press on one now
    // opens it for adjusting, and the same press drags it.
    if(!document.querySelector('#lcAssist [data-lca-pickicon]')) return;
    const cv = document.getElementById('silhouetteCanvas');
    if(e.target !== cv || (e.touches && e.touches.length !== 1)) return;
    const p0 = e.touches ? e.touches[0] : e;
    let a0; try { a0 = designPoint(cv, p0.clientX, p0.clientY); } catch(err){ return; }
    const hit = (iconBoxes || []).find(b => b && b.pos && (b.type || b.shape) && a0.cx >= b.pos.x - 8 && a0.cx <= b.pos.x + b.pos.w + 8 && a0.cy >= b.pos.y - 8 && a0.cy <= b.pos.y + b.pos.h + 8);
    if(!hit) return;
    // Opening the adjust screen moves the canvas on the page (the panel
    // changes height), so the engine would read this press at the old spot
    // and miss the icon. The press is ours: drag it here, by the pointer's
    // movement in design units, then the engine has it from the next press.
    try { e.stopPropagation(); } catch(err){}
    adjustIcon(hit);
    const x0 = hit.pos.x, y0 = hit.pos.y;
    let s0; try { s0 = designPoint(cv, p0.clientX, p0.clientY); } catch(err){ return; }
    const mv = (ev) => {
      const q = ev.touches ? ev.touches[0] : ev; if(!q || !hit.pos) return;
      let d; try { d = designPoint(cv, q.clientX, q.clientY); } catch(err){ return; }
      hit.pos.x = x0 + (d.cx - s0.cx); hit.pos.y = y0 + (d.cy - s0.cy);
      try { renderCanvas(); } catch(err){}
    };
    const up = () => {
      ['mousemove','touchmove'].forEach(t => window.removeEventListener(t, mv, true));
      ['mouseup','touchend','touchcancel'].forEach(t => window.removeEventListener(t, up, true));
      // applySmartDefault, not only evaluateRule: a new icon dragged on/off the
      // lake keeps a colour that shows (lake-capture.js, NEW ICONS ARE VISIBLE).
      try { if(!applySmartDefault(hit)) evaluateRule(hit); } catch(err){}
      try { renderCanvas(); } catch(err){}
    };
    ['mousemove','touchmove'].forEach(t => window.addEventListener(t, mv, true));
    ['mouseup','touchend','touchcancel'].forEach(t => window.addEventListener(t, up, true));
    return;
  }
  const canvas = document.getElementById('silhouetteCanvas');
  if(e.target !== canvas) return;
  if(e.touches && e.touches.length !== 1) return;          // pinch: the engine's
  const pt = e.touches ? e.touches[0] : e;
  let at; try { at = designPoint(canvas, pt.clientX, pt.clientY); } catch(err){ return; }
  const ib = PLACE.ib;
  const m = 28;   // the engine's own selection handles sit just outside the box
  const near = (b, pad) => b && b.pos && at.cx >= b.pos.x - pad && at.cx <= b.pos.x + b.pos.w + pad && at.cy >= b.pos.y - pad && at.cy <= b.pos.y + b.pos.h + pad;
  if(ib && ib.pos && iconBoxes.includes(ib)){
    // Another icon already on the sign: that one becomes the one being
    // adjusted -- dragged, resized, or removed -- instead of the newest one
    // jumping to where they pressed. Checked before the current icon's own
    // handle margin, which can overlap a neighbour. Selected before the
    // engine's own press handler runs, so its drag and corners act on it.
    const other = iconBoxes.find(b => b !== ib && (b.type || b.shape) && near(b, near(ib, m) ? 0 : 6));
    if(!other && near(ib, m)) return;
    if(other){
      PLACE.ib = other; PLACE.type = other.type || other.shape || PLACE.type;
      try { _selectedKey = 'icon:' + iconBoxes.indexOf(other); } catch(err){}
      placeScreen();
      return;
    }
    placeIconAt(ib, at.cx, at.cy);
  } else {
    PLACE.ib = addIconNow(PLACE.type, at);
    if(PLACE.why === 'marker') T1('marker_placed', { icon: PLACE.type });
  }
  try { applySmartDefault(PLACE.ib); evaluateRule(PLACE.ib); } catch(err){}
  try { _selectNewBox('icon', iconBoxes.indexOf(PLACE.ib)); } catch(err){ try { renderCanvas(); } catch(_){} }
  document.getElementById('lake-capture-app').classList.remove('lca-placing');
  placeScreen();
}
document.addEventListener('mousedown', (e) => { try { onPlacePress(e); } catch(err){} }, true);
document.addEventListener('touchstart', (e) => { try { onPlacePress(e); } catch(err){} }, { capture:true, passive:true });

/** addIcon without the awaits: the asset is loaded before placement starts. */
function addIconNow(type, at){
  const ib = makeIB(); ib.type = type;
  iconBoxes.push(ib);
  placeIconAt(ib, at.cx, at.cy);
  return ib;
}

const nice = (t) => String(t || '').charAt(0).toUpperCase() + String(t || '').slice(1);

async function placeByTap(type, why){
  stage(3);
  PLACE.type = type; PLACE.why = why; PLACE.ib = null;
  // The SVG must be in hand before the tap, so the icon can be created in the
  // same event the engine then reads.
  try { if(ICON_ASSET_URLS[type] && !_getAssetIcon(type)) await Promise.race([_loadAssetIcon(type), sleep(2500)]); } catch(e){}
  S.editing = { step: STEPS.objects.n, label: 'place' };
  _origStepGo(STEPS.objects.n);
  // stepGo seeds an empty Objects step with one blank box; a blank box is
  // nothing to the customer, and only clutter in the review list.
  iconBoxes = iconBoxes.filter(b => b.type || b.shape);
  PLACE.on = true;
  document.getElementById('lake-capture-app').classList.add('lca-placing');
  try { window.CCSC_PREVIEW_FRAC = 0.62; updateBuilderViewportOffset(); } catch(e){}
  placeScreen();
  try { renderCanvas(); } catch(e){}
}

/** Open an icon that is already on the sign for dragging / resizing / removal. */
function adjustIcon(ib){
  stage(3);
  PLACE.type = ib.type || ib.shape; PLACE.why = 'icon'; PLACE.ib = ib;
  S.editing = { step: STEPS.objects.n, label: 'place' };
  _origStepGo(STEPS.objects.n);
  iconBoxes = iconBoxes.filter(b => b.type || b.shape);
  PLACE.on = true;
  try { _selectedKey = 'icon:' + iconBoxes.indexOf(ib); } catch(e){}
  try { window.CCSC_PREVIEW_FRAC = 0.62; updateBuilderViewportOffset(); } catch(e){}
  placeScreen();
  try { renderCanvas(); } catch(e){}
}

function placeScreen(){
  const placed = !!(PLACE.ib && PLACE.ib.pos);
  const what = PLACE.type === 'house' || PLACE.type === 'cabin' ? `your ${PLACE.type}` : `the ${PLACE.type}`;
  screen(`<div class="lca-placebar">
      ${placed ? '' : `<span class="lca-tapcue" aria-hidden="true"><span></span></span>`}
      <div>${head(placed ? 'Drag to fine-tune' : (PLACE.why === 'marker' ? 'Tap the sign where your property is' : `Tap the sign to place ${what}`),
              placed ? `Drag ${what} to adjust it, or drag a corner to resize it.${iconBoxes.filter(b => b.pos && (b.type || b.shape)).length > 1 ? ' Tap any other icon on the sign to adjust that one.' : ''}` : `${nice(what)} lands where you tap. You can drag it after.`)}</div>
    </div>
    <button type="button" class="lc-btn lc-btn-primary lca-wide" data-act="edone" ${placed ? '' : 'disabled'}>Done</button>
    <button type="button" class="lca-link" data-act="skip">${placed ? `Remove ${what}` : 'Skip this'}</button>
    ${SESSION.mark ? `<button type="button" class="lca-link" data-act="ecancel">Cancel</button>` : ''}`, { tool:true });
  wire({
    skip(){
      if(PLACE.ib){ const i = iconBoxes.indexOf(PLACE.ib); if(i >= 0) iconBoxes.splice(i, 1); PLACE.ib = null; }
      endPlace(); SCREENS.hub();
    },
  });
}

function endPlace(){
  PLACE.on = false;
  try { window.CCSC_PREVIEW_FRAC = null; updateBuilderViewportOffset(); } catch(e){}
  S.editing = null;
  document.getElementById('lake-capture-app').classList.remove('lca-placing');
  try { _selectedKey = null; } catch(e){}
  try { evaluateAllRules(); } catch(e){}
  try { renderCanvas(); } catch(e){}
}

SCREENS.pickIcon = (all) => {
  stage(3);
  const shown = all ? ICONS.map(i => [i, nice(i)]) : MARKERS;
  const onSign = (iconBoxes || []).filter(b => b && b.pos && (b.type || b.shape));
  screen(`${etop('Markers & icons', 'Pick one, then tap the sign where it should go.')}
    ${onSign.length ? `<p class="lca-hint" data-lca-pickicon>Or tap ${onSign.length > 1 ? 'an icon' : `the ${esc(nice(onSign[0].type || onSign[0].shape))}`} already on your sign to move, resize or remove it.</p>` : ''}
    <div class="lca-tiles lca-icons">${shown.map(([v,l]) => tile('pick', v, l, false, { top:iconImg(v), cls:'lca-icontile' })).join('')}</div>
    ${all ? '' : `<button type="button" class="lca-link lca-left lca-morebtn" data-act="all">See all icons (${ICONS.length})</button>`}
    <details class="lca-more"><summary>More options</summary>
      <button type="button" class="lca-row" data-act="more"><b>Resize, turn or add shapes</b><small>The full icon tools.</small></button>
    </details>
    ${efoot()}`, { tool:true });
  paintIcons();
  wire({
    pick(v){ return placeByTap(v, 'icon'); },
    all(){ SCREENS.pickIcon(true); },
    more(){ openEditor(STEPS.objects.n, 'icon', 'Add or move a marker or icon, then tap Done.'); },
  });
};

/** Whatever they had typed in "Make it yours" survives a hub redraw. */
function keepTell(){ try { const b = root.querySelector('[data-field=tell]'); if(b) S.tellDraft = b.value; } catch(e){} }
SCREENS.hub = (o) => {
  stage(3);
  // Any editor still open is committed on the way back (Back = Done, owner 10 Oct).
  sessionCommit();
  S.designOpen = true;
  // Back from a tool or from Review: land where they were.
  if(!o && S.hubY){ o = { y: S.hubY }; }
  S.hubY = null;
  const d = S.drafts[S.chosen] || {};
  const lines = (textBoxes || []).map(t => t.value).filter(Boolean);
  const icons = (iconBoxes || []).map(b => b.type).filter(Boolean);
  const facts = [
    ['Style', (LOOKS[d.look] || {}).name || (typeof styleDisplayName === "function" ? styleDisplayName() : "")],
    ['Front', signColor && signColor.name], ['Backing', panelColor && panelColor.name],
    ['Corners', roundedCorners ? 'Rounded' : 'Square'],
    ['Words', lines.map(l => `“${l}”`).join(', ')],
    ['Icons', icons.map(i => nice(i) + (i === S.brief.marker ? ' marker' : '')).join(', ')],
  ].filter(f => f[1]);
  if(!(o && o.keep)) T('step_viewed', { step:'hub' });
  // 8 Oct e (owner's Step 3 spec): sign -> size + price -> Review. Changing
  // anything is optional, behind "Want to change something?"; the design's
  // details are one closed row. The size row, the price, the edit tools and
  // every handler are the ones this screen always had.
  const open = !!S.custOpen;
  const a = S.sizeAdvice || CORE.recommendSize(S.brief.purpose);
  // 9 Oct (owner): show that the lake-name lettering can be REPLACED. The two
  // word chips put a start in the box for them to finish (the cursor lands at
  // the end); the existing designEdit does the rest.
  const lead = (window.CCSC_LAKE_NAME && CCSC_LAKE_NAME.name) || lines.join(' ') || 'Lake Norman';
  const TELL_IDEAS = [['Change the lake name', 'Change ' + lead + ' to '], ['Add our family name', 'Add our family name: '], ['Make the lake blue', 'Make the lake blue']];
  // 10 Oct (owner's mobile UX spec): size, then four optional categories, then
  // "Or describe your changes", then ONE Review button. Every tool is a tap
  // behind a category; nothing has to be opened to buy.
  const CATS = [['text', 'Words & fonts', ICO.words], ['colors', 'Colors & finishes', ICO.colors],
                ['lake', 'Lake & layout', ICO.lake], ['icon', 'Markers & icons', ICO.marker]];
  const glow = !open && !S.custGlowed;
  screen(`<div class="lca-buy">
      <h3 class="lca-buyh" id="lcaSizeH">Choose your size</h3>
      <div class="lca-sizes2" role="radiogroup" aria-labelledby="lcaSizeH">${SIZES.map(sz => `<button type="button" role="radio" class="lca-sz${S.size === sz ? ' on' : ''}" data-act="size" data-v="${sz}" aria-checked="${S.size === sz}">${sz}&Prime;${a.inches === sz ? '<small>Recommended</small>' : ''}</button>`).join('')}</div>
      <div class="lca-pricerow">${hubPrice()}
        <details class="lca-why"><summary aria-label="About this size">${a.inches === S.size ? `Why ${a.inches}″?` : `About ${S.size}″`}</summary><p>${esc(a.inches === S.size ? a.why : (SIZE_INFO[S.size] || ''))}</p></details>
      </div>
    </div>
    <section class="lca-pers" aria-labelledby="lcaPersH">
      <h3 class="lca-buyh" id="lcaPersH">Personalize your sign <small>Optional. Your design is ready as-is.</small></h3>
      <div class="lca-cats">${CATS.map(([v, l, ic]) => `<button type="button" class="lca-cat" data-act="edit" data-v="${v}">${ic}<span>${esc(l)}</span></button>`).join('')}</div>
      <details class="lca-ai${glow ? ' is-glow' : ''}"${open ? ' open' : ''}>
        <summary><span class="lca-aitxt">Or describe your changes<small>“Add our family name” or “Make the lake blue”</small></span><span class="lca-aichev" aria-hidden="true"></span></summary>
        <div class="lca-tell">
          <label for="lcaTell" class="lca-vh">What should we change?</label>
          <textarea id="lcaTell" class="lca-notes" data-field="tell" maxlength="300" rows="2" placeholder="${esc('Change ' + lead + ' to The Johnson Family Cottage.')}">${esc(S.tellDraft || '')}</textarea>
          <div class="lca-tellideas" role="group" aria-label="Ideas">${TELL_IDEAS.map(([l, v]) => `<button type="button" class="lca-idea" data-act="idea" data-v="${esc(v)}">${esc(l)}</button>`).join('')}</div>
          <button type="button" class="lc-btn lc-btn-primary lca-wide" data-act="tell">Update my design</button>
          ${S.tellMsg ? `<p class="lca-tellmsg" role="status">${S.tellMsg}</p>` : ''}
        </div>
      </details>
    </section>
    <div class="lca-buy lca-buyend">
      <button type="button" class="lc-btn lc-btn-primary lca-wide lca-review" id="lcaReview" data-act="done">Review my sign →</button>
      <div class="lca-mail${S.mailed ? '' : ' is-closed'}">
      ${S.mailed ? `<p class="lca-mailmsg" role="status"><b>Saved!</b> Check ${esc(S.mailed)} for a link back to this design.<span class="lca-mailspam">Don’t see it in a few minutes? Check your <b>spam or junk</b> folder and tap <b>“Not spam”</b> (or move it to your Inbox), so our emails reach you.</span></p>`
      : `<button type="button" class="lca-link lca-saveopen" data-act="saveopen">Save for later</button>`}
      </div>
    </div>
    <details class="lca-details"${S.detailsOpen ? ' open' : ''}><summary>Design details</summary>
      <dl>${facts.map(f => `<dt>${esc(f[0])}</dt><dd>${esc(f[1])}</dd>`).join('')}<dt>Size</dt><dd>${S.size}″</dd></dl>
    </details>
    <div class="lca-sticky" id="lcaSticky" aria-hidden="true">${hubPrice(true)}<button type="button" class="lc-btn lc-btn-primary" data-act="done" tabindex="-1">Review sign →</button></div>`,
    { hub:true, keepScroll: !!(o && o.keep) });
  try { updateBuilderViewportOffset(); } catch(e){}
  if(o && o.y) _scrollRestore(o.y);
  hubSticky();
  { const dt = root.querySelector('.lca-details'); if(dt) dt.addEventListener('toggle', () => { S.detailsOpen = dt.open; }); }
  // "Or describe your changes": a plain disclosure; its state is kept across redraws.
  { const ai = root.querySelector('.lca-ai'); if(ai) ai.addEventListener('toggle', () => {
      S.custOpen = ai.open; ai.classList.remove('is-glow');
      T('step_viewed', { step: ai.open ? 'customize-open' : 'customize-closed' });
      if(ai.open){ try { ai.scrollIntoView({ block:'nearest', behavior:'smooth' }); } catch(e){} }
    }); }
  // The describe box pulses once per design so people see they can still change it (owner, 9 Oct c).
  if(!S.custOpen && !S.custGlowed) S.custGlowed = true;
  SAVE.onHub();
  wire({
    edit(v){
      T('manual_edit_opened', { tool: v });
      keepTell(); S.hubY = _scrollPos();
      return openTool(v);
    },
    size(v){ keepTell(); undoPoint(); S.size = Number(v); try { selectSize(S.size); } catch(e){} T('size_chosen', { size: S.size }); SCREENS.hub({ keep:true }); },
    // An idea replaces what is in the box (owner, 9 Oct d: tapping two chips
    // used to pile them up into one sentence).
    idea(v){ const box = root.querySelector('[data-field=tell]'); if(!box) return;
      box.value = v; box.focus();
      try { const n = box.value.length; box.setSelectionRange(n, n); } catch(e){} },
    saveopen(){ keepTell(); SAVE.open('hub'); },
    done(){ keepTell(); S.hubY = _scrollPos(); openEditor(STEPS.finalize.n, 'finalize', null); },
    async tell(v, btn){
      const box = root.querySelector('[data-field=tell]');
      const said = String(box && box.value || '').trim();
      if(!said){ if(box) box.focus(); return; }
      S.tellDraft = said;   // kept if the update fails; cleared once it lands
      S.custOpen = true;
      T('ai_edit_submitted');
      if(btn){ btn.disabled = true; btn.textContent = 'Updating your design…'; }
      const cur = S.drafts[S.chosen] || {};
      const current = {
        look: cur.look, sign: (signColor && signColor.id) || cur.signColorId, panel: (panelColor && panelColor.id) || cur.panelColorId,
        lines: (textBoxes || []).map(t => t.value).filter(x => String(x || '').trim()),
        icons: (iconBoxes || []).map(b => b.type).filter(Boolean), size: S.size,
        placement: cur.placement || 'best',
      };
      const t0 = Date.now();
      // Read in the page first (9 Oct d): colours, words, top/bottom, size,
      // one/two lines. A request it fully understands never waits on the AI
      // (whose Apps Script call timed out for the owner every time); anything
      // else asks the AI, and what the page understood still applies if the
      // AI fails. What the page read wins over the AI for the same field.
      const local = CORE.localEdit(said, { style: doubleWallStyle, sign: current.sign, panel: current.panel, lines: current.lines });
      let plan = null, reason = null;
      if(local.complete){ plan = local.plan; T('ai_edit_local', { fields: Object.keys(plan).join(',') }); }
      else {
        const r = await askEdit(current, said, 15000);
        plan = r.plan; reason = r.reason;
        if(Object.keys(local.plan).length){ plan = Object.assign({}, plan || {}, local.plan); }
      }
      if(plan){
        // The AI can echo the colours already on the sign; that is no change.
        if(plan.sign && plan.sign === current.sign) delete plan.sign;
        if(plan.panel && plan.panel === current.panel) delete plan.panel;
        // "make it one line": the AI may hand the same words back as two lines.
        if(plan.lineMode === 'single' && plan.lines && plan.lines.length > 1) plan.lines = [plan.lines.join(' ')];
      }
      if(!plan){
        T('ai_fallback', { reason: reason || 'error', source: 'edit', ms: Date.now() - t0 });
        S.tellMsg = 'Sorry — we couldn’t update it that way just now. You can make the change with the buttons below.';
        return SCREENS.hub({ keep:true });
      }
      const base = Object.assign({}, cur, { lines: current.lines, icons: current.icons.filter(i => i !== S.brief.marker) });
      const { draft, changes } = CORE.editDraft(base, plan);
      if(!changes.length && !(plan.cannot && plan.cannot.length)){
        S.tellMsg = 'We weren’t sure what to change. Try naming a color, the words, where they go, or the size.';
        return SCREENS.hub({ keep:true });
      }
      if(plan.lines || plan.lineMode) S.words = draft.lines.slice();
      // Their own new words stay as written (no automatic break), unless they
      // asked for one line / several (12 Oct, owner).
      if(plan.lines && !plan.lineMode) draft.lineMode = 'asis';
      S.tellDraft = '';
      // Only colours and/or size: paint them in place, so words they moved by
      // hand and a marker they placed stay exactly where they are. Anything
      // else rebuilds the sign through applyDraft, keeping their marker.
      const relayout = !!(plan.look || plan.lines || plan.lineMode || plan.placement || plan.textSize || plan.textStyle
                          || (plan.addIcons && plan.addIcons.length) || (plan.removeIcons && plan.removeIcons.length));
      undoPoint();   // one Undo takes the whole change back (10 Oct)
      document.getElementById('lake-capture-app').classList.add('lca-generating');
      try {
        if(relayout){
          const keep = (iconBoxes || []).filter(b => b.pos && b.type && b.type === S.brief.marker && !(plan.removeIcons || []).includes(b.type));
          await onSign(async () => {
            await applyDraft(draft);
            if(keep.length){ keep.forEach(b => iconBoxes.push(b)); try { renderIconBoxes(); } catch(e){} renderCanvas(); }
          });
        } else {
          if(plan.sign){ const c = color(draft.signColorId); if(c){ signColor = c; _signColorChosen = true; } }
          if(plan.panel){ const c = color(draft.panelColorId); if(c) panelColor = c; }
          _clearAllFinishCaches(); try { updateColorStep(); } catch(e){} renderCanvas();
        }
        if(S.chosen >= 0) S.drafts[S.chosen] = draft; else { S.drafts.push(draft); S.chosen = S.drafts.length - 1; }
        if(plan.size){ S.size = plan.size; try { selectSize(S.size); } catch(e){} }
      } catch(e){ try { console.warn('[assist] edit apply', e); } catch(_){} }
      finally { document.getElementById('lake-capture-app').classList.remove('lca-generating'); }
      const done = changes.length ? 'Done: ' + changes.map(esc).join(' · ') + '.' : '';
      const cant = (plan.cannot || []).map(esc).join(' ');
      S.tellMsg = [done, cant].filter(Boolean).join(' ');
      SCREENS.hub({ keep:true });
    },
  });
};

// ── Colors & finishes (10 Oct, owner's mobile UX spec) ────────────────────
// One focused editor for colour: six coordinated combinations first (the one
// on the sign + five that share no lake colour or colour finish with each
// other -- CORE.looksClash, the same rule as the first three designs), then
// "See all styles", then the full finish picker for each layer. Every tile is
// THEIR sign rendered in that combination (built in the background, the tiles
// on screen first). Taps apply at once; Done keeps, Cancel restores what was
// there when the editor opened (sessionCancel). Replaces the "Try another
// style" row that sat under the sign on Customize (owner: "a lot happening",
// "only 11 looks", "a lot of the same colors").
const COLORS = { on:false, all:false, fin:false };
function endColors(){ COLORS.on = false; SROW.gen++; }
SCREENS.colors = (o) => {
  stage(3);
  o = o || {};
  if(!COLORS.on){ COLORS.all = false; COLORS.fin = false; }
  COLORS.on = true;
  const isCut = doubleWallStyle === 'cutout';
  const all = srowLooks(), cur = srowCurrent(), picks = srowPicks();
  const rest = all.filter(k => !picks.includes(k));
  const tileHtml = (k) => {
    const L = LOOKS[k], img = SROW.cache[k], pr = srowPair(k), on = k === cur;
    return `<button type="button" class="lca-srow-tile lca-ctile${on ? ' on' : ''}" data-act="style" data-v="${k}" data-sr="${k}" aria-pressed="${on}">
      <span class="lca-srow-img">${img && img !== 'data:,' ? `<img src="${img}" alt="">` : '<i class="lca-srow-wait"></i>'}</span>
      <span class="lca-srow-name">${esc(L.name)}${on ? ' <em>On your sign</em>' : ''}</span>
      <span class="lca-srow-sub">${esc(FINISH_NAMES[pr.sign] || '')} + ${esc(FINISH_NAMES[pr.panel] || '')}</span></button>`;
  };
  const grid = (which, c) => `<div class="lca-finishes">${PAINT_COLORS.map(p =>
    `<button type="button" class="lca-finish${c && c.id === p.id ? ' on' : ''}" data-act="${which}" data-v="${p.id}" aria-pressed="${!!(c && c.id === p.id)}">
       <span class="lca-finish-img">${finishImg(p.id)}</span><span class="lca-finish-name">${esc(p.name)}</span></button>`).join('')}</div>`;
  const a = isCut ? 'Front plate' : 'Lake piece';
  const b = isCut ? 'Color through the lake' : 'Back plate';
  screen(`${etop('Colors & finishes', 'Tap a combination to try it on your sign.')}
    <div class="lca-cgrid" data-cgrid="picks">${picks.map(tileHtml).join('')}</div>
    ${rest.length ? `<button type="button" class="lca-link lca-left lca-morebtn" data-act="allstyles" aria-expanded="${COLORS.all}">${COLORS.all ? 'Show fewer styles' : `See all styles (${all.length})`}</button>` : ''}
    ${COLORS.all ? `<div class="lca-cgrid lca-cgrid-all">${rest.map(tileHtml).join('')}</div>` : ''}
    <details class="lca-more" data-cfin${COLORS.fin ? ' open' : ''}><summary>Choose individual finishes</summary>
      <p class="lca-hint">${isCut ? 'Your sign has two layers: the front plate, with your lake cut out of it, and the back plate, whose color shows through the lake.'
                                  : 'Your sign has two layers: the raised lake piece, and the back plate behind it.'}</p>
      <h4 class="lca-sub">${a} <small>${esc(signColor ? signColor.name : '')}</small></h4>${grid('sign', signColor)}
      <h4 class="lca-sub">${b} <small>${esc(panelColor ? panelColor.name : '')}</small></h4>${grid('panel', panelColor)}
    </details>
    ${efoot()}`, { tool:true, styles:true, keepScroll: !!o.keep });
  { const f = root.querySelector('[data-cfin]'); if(f) f.addEventListener('toggle', () => { COLORS.fin = f.open; }); }
  const repaint = async (fn) => {
    await onSign(async () => { fn(); _clearAllFinishCaches(); try { updateColorStep(); } catch(e){} await render(); });
    SCREENS.colors({ keep:true });
  };
  wire({
    style(k){ return srowApply(k); },
    allstyles(){ COLORS.all = !COLORS.all; T('step_viewed', { step: COLORS.all ? 'styles-more' : 'styles-less' }); SCREENS.colors({ keep:true }); },
    sign(v){ const c = color(v); if(c) return repaint(() => { signColor = c; _signColorChosen = true; }); },
    panel(v){ const c = color(v); if(c) return repaint(() => { panelColor = c; }); },
  });
};

function srowPair(k){
  const L = LOOKS[k]; if(!L) return null;
  return L.style === doubleWallStyle ? { sign: L.sign, panel: L.panel } : { sign: L.panel, panel: L.sign };
}
function srowLooks(){
  const ok = (k) => { const pr = srowPair(k); return pr && color(pr.sign) && color(pr.panel) && pr.sign !== pr.panel; };
  const order = [];
  // This construction's own looks first (family order), then the rest.
  const mine = (k) => LOOKS[k].style === doubleWallStyle;
  [true, false].forEach(want => {
    Object.values(FAMILY_ORDER).forEach(list => list.forEach(k => { if(LOOKS[k] && mine(k) === want && ok(k) && !order.includes(k)) order.push(k); }));
    Object.keys(LOOKS).forEach(k => { if(mine(k) === want && ok(k) && !order.includes(k)) order.push(k); });
  });
  // One tile per colour pair (e.g. Granite and Lakehouse Classic share theirs).
  const seen = new Set();
  return order.filter(k => { const pr = srowPair(k), key = pr.sign + '/' + pr.panel; if(seen.has(key)) return false; seen.add(key); return true; });
}
/** The first six: the one on the sign, then the most different others -- no two
 *  sharing a lake colour or a colour finish (CORE.looksClash), topped up in
 *  family order if the rule leaves fewer than six. */
function srowPicks(){
  const all = srowLooks(), cur = srowCurrent(), out = [];
  if(cur) out.push(cur);
  for(const k of all){ if(out.length >= 6) break; if(!out.includes(k) && !out.some(o => CORE.looksClash(o, k))) out.push(k); }
  for(const k of all){ if(out.length >= 6) break; if(!out.includes(k)) out.push(k); }
  return out;
}
function srowCurrent(){
  if(!signColor || !panelColor) return null;
  return srowLooks().find(k => { const pr = srowPair(k); return pr.sign === signColor.id && pr.panel === panelColor.id; }) || null;
}
// What the tiles depend on: lake, plate, words, icons, construction, size.
function srowDesignKey(){
  const tb = (textBoxes || []).map(t => [t.value, t.font, t.pos && Math.round(t.pos.x), t.pos && Math.round(t.pos.y), Math.round((t.size || 0) * 10), t.rotation || 0, t.mfgMode, t.lakeColor ? 1 : 0]);
  const ib = (iconBoxes || []).map(b => [b.type, b.pos && Math.round(b.pos.x), b.pos && Math.round(b.pos.y), Math.round(b.size || 0)]);
  const pr = _panelRefBBox ? [_panelRefBBox.minX, _panelRefBBox.minY, _panelRefBBox.maxX, _panelRefBBox.maxY].map(Math.round) : null;
  return JSON.stringify([doubleWallStyle, roundedCorners, pr, panelShortAxisBoost, tb, ib, _lakeCumMatrix ? _lakeCumMatrix.toString() : '']);
}
const SROW = { gen: 0, cache: {}, key: '', running: false, shown: false };
// A still copy of the sign over the live canvas while a tile renders.
async function srowFreeze(){
  const c = document.getElementById('silhouetteCanvas'), wrap = c && c.parentElement;
  if(!c || !wrap) return () => {};
  const img = new Image();
  img.src = c.toDataURL('image/png');
  try { await img.decode(); } catch(e){}
  const cr = c.getBoundingClientRect(), wr = wrap.getBoundingClientRect();
  img.className = 'lca-freeze';
  img.style.cssText = `position:absolute;left:${cr.left - wr.left}px;top:${cr.top - wr.top}px;width:${cr.width}px;height:${cr.height}px;z-index:4;pointer-events:none`;
  wrap.appendChild(img);
  return () => { try { img.remove(); } catch(e){} };
}
// 12 Oct (owner: "it holds the font type and location ... it should still show
// the native styles in their normal font type"): a style shows -- and applies --
// its own font. When that differs from the sign's, the words are laid out again
// in it the way the Words editor does on a font change (placeWords, stated);
// a style in the sign's own font keeps the layout exactly as it is.
async function srowFont(k){
  try { const p = await lookPreset(k); const f = ((p.textBoxes || [])[0] || {}).font; if(f) return f; } catch(e){}
  return null;
}
async function srowRestyle(font){
  const live = (textBoxes || []).filter(t => t.pos && String(t.value || '').trim());
  if(!font || !live.length || live.every(t => t.font === font)) return false;
  const lines = live.map(t => t.value);
  const use = fontCanPrint(font, lines) ? font : FALLBACK_FONT;
  if(live.every(t => t.font === use)) return false;
  const d0 = S.drafts[S.chosen] || {}, t0 = live[0];
  const textStyle = doubleWallStyle === 'cutout' ? (t0.mfgMode === 'nameplate' ? 'nameplate' : null)
                                                 : (t0.mfgMode === 'open' ? 'cut' : null);
  window._lcHoldRender = 1;
  // The cards' own composition (best fit, scored), unless they asked for a spot.
  try { await placeWords(lines, use, d0.placement || 'best', { textStyle, textSize: d0.textSize, stated: !!d0.placementStated, lineMode: 'asis' }); }
  finally { window._lcHoldRender = 0; }
  return use;
}
async function srowThumb(k){
  const pr = srowPair(k); const s0 = signColor, p0 = panelColor;
  const keep = { tb: JSON.stringify(textBoxes), pref: _panelRefBBox ? { ..._panelRefBBox } : null, boost: panelShortAxisBoost, align: panelAlign };
  const c = document.getElementById('silhouetteCanvas');
  const font = await srowFont(k);
  const unfreeze = await srowFreeze();
  let restyled = false;
  try {
    signColor = color(pr.sign); panelColor = color(pr.panel);
    restyled = !!(await srowRestyle(font));
    _clearAllFinishCaches();
    _hideGuidesForCapture = true; _captureOwnsCanvas = true;
    await render();
    return cropToSign(c) || c.toDataURL('image/jpeg', 0.84);
  } finally {
    signColor = s0; panelColor = p0;
    if(restyled){
      textBoxes = JSON.parse(keep.tb); _panelRefBBox = keep.pref ? { ...keep.pref } : null;
      panelShortAxisBoost = keep.boost; panelAlign = keep.align;   // the saved copy is exact; no rule pass (it nudges words)
      try { panelRectStore = null; _recomputePanelRect(); } catch(e){}   // the plate cache the re-layout replaced (as _undoLast does)
    }
    _clearAllFinishCaches();
    _hideGuidesForCapture = false; _captureOwnsCanvas = false;
    try { await render(); } catch(e){}
    unfreeze();
  }
}
const srowLive = (gen) => gen === SROW.gen && SROW.shown && COLORS.on
  && !(window.CCSC_ROOM && CCSC_ROOM.showing && CCSC_ROOM.showing());
/** Builds the tiles' pictures one at a time, the ones on screen first. */
function srowBuild(){
  if(SROW.running) return;
  SROW.running = true;
  const gen = SROW.gen;
  (async () => {
    try {
      await sleep(500);   // let the editor paint first
      for(;;){
        if(!srowLive(gen)) break;
        const tiles = [...root.querySelectorAll('[data-sr]')].filter(t => !SROW.cache[t.dataset.sr]);
        if(!tiles.length) break;
        const vh = window.innerHeight;
        const vis = tiles.filter(t => { const r = t.getBoundingClientRect(); return r.bottom > 0 && r.top < vh; });
        const next = (vis[0] || tiles[0]).dataset.sr;
        const img = await onSign(() => srowLive(gen) ? srowThumb(next) : null).catch(() => null);
        if(!srowLive(gen)) break;
        SROW.cache[next] = img || 'data:,';
        if(img) root.querySelectorAll(`[data-sr="${next}"] .lca-srow-img`).forEach(t => { t.innerHTML = `<img src="${img}" alt="">`; });
        await sleep(40);
      }
    } finally {
      SROW.running = false;
      // Interrupted by a design change: start over on the new design.
      if(gen !== SROW.gen && SROW.shown && COLORS.on) setTimeout(srowBuild, 300);
    }
  })();
}
// Called on every assist screen: the pictures are built only while Colors & finishes is open.
function srowSync(on){
  SROW.shown = !!on;
  if(!SROW.shown) return;
  const key = srowDesignKey();
  if(key !== SROW.key){ SROW.key = key; SROW.cache = {}; SROW.gen++; }
  srowBuild();
}
async function srowApply(k){
  const L = LOOKS[k]; if(!L || k === srowCurrent()) return;
  const pr = srowPair(k);
  const font = await srowFont(k);
  let used = false;
  await onSign(async () => {
    signColor = color(pr.sign); panelColor = color(pr.panel); _signColorChosen = true;
    used = await srowRestyle(font);
    _clearAllFinishCaches(); try { updateColorStep(); } catch(e){}
    await settleSign();
  });
  const d = S.drafts[S.chosen];
  if(d && used) d.font = used;
  // The construction stays this sign's; only a same-construction look renames the design.
  if(d){ if(L.style === doubleWallStyle){ d.look = k; d.name = L.name; } d.signColorId = pr.sign; d.panelColorId = pr.panel; }
  S.styleLook = k;
  T('style_tried', { look: k });
  SCREENS.colors({ keep: true });
  try { window._lcAutosave && window._lcAutosave(); } catch(e){}
}

// ── Simple text editor ──────────────────────────────────────────────────────
// The words, a font and Top / Bottom / Best fit -- applied to the sign as they
// change (typing settles for a moment first), so nothing is chosen blind. The
// builder sits on its Text step behind the screen, which is what lets the
// engine's own drag and corner handles move and resize the words on the sign.
// Done keeps what is on the sign; Cancel puts back what was there on entry.
const WORDS = { on:false, entry:null, cur:null, allFonts:false, advOpen:false, moved:false, timer:0, chain:Promise.resolve(), pending:false, relayout:false, downKey:null };
// Spaces inside a line are theirs to choose (extra gaps are a real layout
// choice on a sign); only the ends are trimmed, and tabs become spaces.
const cleanLines = (lines) => lines.map(v => String(v || '').replace(/[\t\r\n]/g, ' ').trim()).filter(Boolean).slice(0, 3);
const wordsKey = () => JSON.stringify((textBoxes || []).map(t => t.pos ? [Math.round(t.pos.x), Math.round(t.pos.y), Math.round(t.size * 10), Math.round(t.rotation || 0)] : 0));

SCREENS.text = () => {
  const lines = (textBoxes || []).map(t => t.value).filter(Boolean);
  WORDS.cur = {
    lines: lines.length ? lines : [''],
    font: (textBoxes[0] && textBoxes[0].font) || (S.drafts[S.chosen] || {}).font || FALLBACK_FONT,
    placement: (S.drafts[S.chosen] || {}).placement || 'best',
  };
  WORDS.entry = {
    tbs: JSON.parse(JSON.stringify(textBoxes || [])),
    ref: _panelRefBBox ? { ..._panelRefBBox } : null, boost: panelShortAxisBoost, align: panelAlign,
  };
  WORDS.moved = false; WORDS.on = true; WORDS.allFonts = false; WORDS.advOpen = false;
  S.editing = { step: STEPS.text.n, label: 'words' };
  _origStepGo(STEPS.text.n);
  // stepGo seeds an empty Text step with a blank box; that is nothing to them.
  textBoxes = textBoxes.filter(t => String(t.value || '').trim());
  // Hand the lake to nobody: on this screen a drag is for the words. A lake
  // drag is baked into its pixels, which Cancel could not take back.
  window.CCSC_LOCK_LAKE = true;
  try { _selectedKey = textBoxes.length ? 'text:0' : null; } catch(e){}
  document.getElementById('lake-capture-app').classList.add('lca-words');
  try { window.CCSC_PREVIEW_FRAC = 0.62; updateBuilderViewportOffset(); } catch(e){}
  FONTS.forEach(f => { try { ensureFontLoaded(f); } catch(e){} });
  drawWords();
  try { renderCanvas(); } catch(e){}
};

function drawWords(focusLine){
  stage(3);
  const cur = WORDS.cur;
  // Four fonts up front (the one in use first, then those that can print these
  // words), the rest behind "See all fonts" (10 Oct, owner's spec).
  const printable = FONTS.filter(f => f !== cur.font && fontCanPrint(f, cur.lines));
  const first = [cur.font].concat(printable).slice(0, 4);
  const shown = WORDS.allFonts ? [cur.font].concat(FONTS.filter(f => f !== cur.font)) : first;
  screen(`${etop('Words & fonts', 'Changes show on your sign as you make them.')}
    <p class="lca-hint">Replace the words with a family name, cottage name or date.</p>
    <div class="lca-fields">${cur.lines.map((v, k) => `<label class="lca-field"><span>Line ${k+1}</span>
      <input type="text" data-line="${k}" maxlength="32" value="${esc(v)}" autocomplete="off" /></label>`).join('')}</div>
    ${cur.lines.length < 3 ? `<button type="button" class="lca-link lca-left" data-act="add">+ Add a line</button>` : ''}
    <h4 class="lca-sub">Font</h4>
    <div class="lca-fonts">${shown.map(f => `<button type="button" class="lca-font" data-act="font" data-v="${esc(f)}">
        <span style="font-family:'${esc(f)}',sans-serif"></span><small></small></button>`).join('')}</div>
    ${FONTS.length > first.length ? `<button type="button" class="lca-link lca-left lca-morebtn" data-act="allfonts" aria-expanded="${!!WORDS.allFonts}">${WORDS.allFonts ? 'Fewer fonts' : `See all fonts (${FONTS.length})`}</button>` : ''}
    <details class="lca-more" data-wplace${WORDS.advOpen ? ' open' : ''}><summary>Advanced text placement</summary>
      <p class="lca-hint">Tap any line on the sign to select it, then drag to move it, use its corners to resize, or the top handle to turn it.</p>
      <h4 class="lca-sub">Position</h4>
      <div class="lca-seg">${[['top','Top'],['bottom','Bottom'],['best','Best fit']].map(([v,l]) =>
        `<button type="button" data-act="place" data-v="${v}">${l}</button>`).join('')}</div>
      <button type="button" class="lca-link lca-left" data-act="more">More text options (size, rotation, cut or raised letters)</button>
    </details>
    ${efoot()}`, { tool:true });
  { const d = root.querySelector('[data-wplace]'); if(d) d.addEventListener('toggle', () => { WORDS.advOpen = d.open; }); }
  syncWordsControls();
  root.oninput = (e) => {
    const i = e.target.closest('[data-line]'); if(!i) return;
    cur.lines[+i.dataset.line] = i.value;
    syncWordsControls();
    clearTimeout(WORDS.timer);
    WORDS.timer = setTimeout(() => queueWords(false), 350);
  };
  wire({
    add(){ cur.lines.push(''); drawWords(cur.lines.length - 1); },
    font(v){ cur.font = v; syncWordsControls(); queueWords(false); },
    allfonts(){ const y = _scrollPos(); WORDS.allFonts = !WORDS.allFonts; drawWords(); _scrollRestore(y); },
    // Choosing a position again is "arrange them for me": it re-lays the
    // words even after a hand move.
    place(v){ cur.placement = v; syncWordsControls(); queueWords(true); },
    async more(){ clearTimeout(WORDS.timer); await queueWords(false); endWords(); openEditor(STEPS.text.n, 'text', 'Edit your text, then tap Done.'); },
  });
  markWordsField();
  if(focusLine != null){ const i = root.querySelector(`[data-line="${focusLine}"]`); if(i) i.focus(); }
}
/** Done on Words & fonts (also Back, and the phone's Back button). */
async function wordsDone(){
  const cur = WORDS.cur;
  clearTimeout(WORDS.timer); await queueWords(false);
  const d = S.drafts[S.chosen];
  const now = textBoxes.map(t => t.value).filter(Boolean);
  if(d){ d.font = (textBoxes[0] && textBoxes[0].font) || cur.font; d.placement = cur.placement; }
  // Their words, not the interview's, from here on: every draft and every
  // "another look" preview is rebuilt with them (in the background).
  if(JSON.stringify(now) !== JSON.stringify(S.words || (d && d.lines) || [])){
    S.words = now.slice();
    S.drafts.forEach((x, k) => { x.lines = now.slice(); S.stale.add(k); });
    resetLookPreviews();
  }
  T('manual_edit_saved', { tool:'text' });
  endWords(); SCREENS.hub();
}

/** The font tiles and position buttons follow the form without redrawing it (the caret stays put). */
function syncWordsControls(){
  const cur = WORDS.cur;
  const sample = cur.lines.map(v => String(v || '').trim()).find(Boolean) || 'Your words';
  root.querySelectorAll('.lca-font').forEach(b => {
    const f = b.getAttribute('data-v'), ok = fontCanPrint(f, cur.lines), on = cur.font === f;
    b.disabled = !ok; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on);
    b.querySelector('span').textContent = sample;
    b.querySelector('small').textContent = ok ? f : 'Can’t print numbers or symbols';
  });
  root.querySelectorAll('.lca-seg [data-act=place]').forEach(b => {
    // Once they have moved the words by hand, none of the three is "on" --
    // pressing one re-arranges the words.
    const on = !WORDS.moved && cur.placement === b.getAttribute('data-v'); b.classList.toggle('on', on); b.setAttribute('aria-pressed', on);
  });
}

/** One update at a time, newest wins: rapid typing collapses into one layout. */
function queueWords(relayout){
  WORDS.relayout = WORDS.relayout || relayout;
  if(WORDS.pending) return WORDS.chain;
  WORDS.pending = true;
  WORDS.chain = WORDS.chain.then(async () => {
    WORDS.pending = false;
    const r = WORDS.relayout; WORDS.relayout = false;
    if(WORDS.on) await applyWords(r);
  }).catch(e => { try { console.warn('[assist] words', e); } catch(_){} });
  return WORDS.chain;
}

async function applyWords(relayout){
  const cur = WORDS.cur;
  const lines = cleanLines(cur.lines);
  const font = fontCanPrint(cur.font, lines) ? cur.font : FALLBACK_FONT;
  const live = textBoxes.filter(t => t.pos && String(t.value || '').trim());
  if(!relayout && WORDS.moved && lines.length && lines.length === live.length){
    // Hand-placed words keep where and how big they are; only the wording and
    // the font change, each line about its own centre.
    await fontReady(font);
    const ctx = document.getElementById('silhouetteCanvas').getContext('2d');
    live.forEach((tb, k) => {
      tb.value = lines[k]; tb.font = font;
      // Longer words in the same size could outgrow the plate: the builder's
      // own cap (the Text step's size slider uses it) brings them back inside.
      try { tb.size = _clampTextSizeToPanel(tb, tb.size); } catch(e){}
      tb.rect = computeNameplate(ctx, tb.value, tb.font, tb.size, tb.pos.x, tb.pos.y, tb.mfgMode === 'nameplate' ? tb.namePad : 0);
      try { evaluateRule(tb); } catch(e){}
    });
    textBoxes = live;
  } else {
    // Keep the kind of lettering the draft has (a nameplate asked for in their
    // notes stays a nameplate when they retype the words).
    const d0 = S.drafts[S.chosen] || {};
    const t0 = textBoxes[0];
    const textStyle = t0 ? (doubleWallStyle === 'cutout' ? (t0.mfgMode === 'nameplate' ? 'nameplate' : null)
                                                          : (t0.mfgMode === 'open' ? 'cut' : null)) : d0.textStyle;
    await placeWords(lines, font, cur.placement, { textStyle, textSize: d0.textSize, stated: true, lineMode: 'asis' });
    WORDS.moved = false;
    syncWordsControls();
  }
  // Keep whichever line they are adjusting; line 1 only when nothing valid is.
  // Keep the line they are adjusting; nothing when they clicked off (12 Oct).
  try { const k = /^text:(\d+)$/.exec(_selectedKey || ''); if(!(k && textBoxes[+k[1]])) _selectedKey = null; } catch(e){}
  await settleSign();
  markWordsField();
}

function endWords(){
  WORDS.on = false; clearTimeout(WORDS.timer);
  window.CCSC_LOCK_LAKE = false;
  S.editing = null;
  root.oninput = null;
  document.getElementById('lake-capture-app').classList.remove('lca-words');
  try { window.CCSC_PREVIEW_FRAC = null; updateBuilderViewportOffset(); } catch(e){}
  try { _selectedKey = null; } catch(e){}
  textBoxes = textBoxes.filter(t => String(t.value || '').trim());
  try { evaluateAllRules(); } catch(e){}
  try { renderCanvas(); } catch(e){}
}

// A drag or corner resize on the sign is a hand move: later edits keep it.
document.addEventListener('pointerdown', (e) => {
  if(WORDS.on && e.target && e.target.id === 'silhouetteCanvas') WORDS.downKey = wordsKey();
}, true);

// A press selects the line under the finger. The engine gives the SELECTED box
// a generous grab radius, so with line 1 selected a press on line 2 (right
// below it) grabbed line 1 -- line 2 could never be moved, resized or turned.
// Selecting here, before the engine's own press handler, hands it the right one.
function onWordsPress(e){
  if(!WORDS.on) return;
  const canvas = document.getElementById('silhouetteCanvas');
  if(e.target !== canvas || (e.touches && e.touches.length !== 1)) return;
  const pt = e.touches ? e.touches[0] : e;
  let at; try { at = designPoint(canvas, pt.clientX, pt.clientY); } catch(err){ return; }
  const inside = (tb, pad) => {
    const r = tb.rect; if(!r || !tb.pos) return false;
    const cx = r.x + r.w/2, cy = r.y + r.h/2, a = -(tb.rotation || 0) * Math.PI / 180;
    const dx = at.cx - cx, dy = at.cy - cy;
    const x = dx * Math.cos(a) - dy * Math.sin(a), y = dx * Math.sin(a) + dy * Math.cos(a);
    return Math.abs(x) <= r.w/2 + pad && Math.abs(y) <= r.h/2 + pad;
  };
  const sel = /^text:(\d+)$/.exec(_selectedKey || ''), cur = sel ? +sel[1] : -1;
  // Inside ANOTHER line's words: that line, even where the selected line's
  // handles overlap it -- stacked lines put line 1's turn handle right on
  // line 2, so a press on line 2 used to turn line 1.
  const k = textBoxes.findIndex((tb, n) => n !== cur && inside(tb, 2));
  if(k >= 0){ try { _selectedKey = 'text:' + k; } catch(err){} markWordsField(); return; }
  // Otherwise the selected line's own handles, and any line by a near miss,
  // are the engine's to handle as usual.
  if(cur < 0){ const n = textBoxes.findIndex(tb => inside(tb, 6)); if(n >= 0){ try { _selectedKey = 'text:' + n; } catch(err){} markWordsField(); } }
}
document.addEventListener('mousedown', (e) => { try { onWordsPress(e); } catch(err){} }, true);
document.addEventListener('touchstart', (e) => { try { onWordsPress(e); } catch(err){} }, { capture:true, passive:true });

/** The "Line N" field of the line selected on the sign gets an outline (display only). */
function markWordsField(){
  try {
    const m = /^text:(\d+)$/.exec(_selectedKey || ''); const tb = m ? textBoxes[+m[1]] : null;
    const live = textBoxes.filter(t => t.pos && String(t.value || '').trim());
    const line = tb ? live.indexOf(tb) : -1;
    root.querySelectorAll('[data-line]').forEach(i => i.closest('.lca-field').classList.toggle('lca-sel', +i.dataset.line === line));
  } catch(e){}
}
document.addEventListener('pointerup', () => {
  if(!WORDS.on || WORDS.downKey == null) return;
  const before = WORDS.downKey; WORDS.downKey = null;
  // After the engine's own pointer-up has settled the box.
  setTimeout(() => { if(!WORDS.on) return; markWordsField(); if(wordsKey() !== before){ WORDS.moved = true; syncWordsControls(); } }, 0);
}, true);

// ── The lake: move, turn, resize ───────────────────────────────────────────
// The builder sits on its Color step behind the screen with the lake selected,
// so the engine's own lake drag, corner resize and rotate handle work on the
// sign. The buttons do the same through the same path (_lakeG* then
// _bakeLakeGesture), for anyone who'd rather tap than drag. The plate does not
// move -- that is the engine's rule -- so the lake moves within it.
const LAKE = { on:false, saved:null };
async function lakeStep(rot, scale){
  await onSign(async () => {
    _lakeGRotation = rot || 0; _lakeGScale = scale || 1;
    try { _capLakeScaleToPanel(); } catch(e){}
    await _bakeLakeGesture();
    try { evaluateAllRules(); } catch(e){}
    if(doubleWallStyle === 'cutout' || doubleWallStyle === 'single-cutout'){ try { detectFloatingPieces(); } catch(e){} }
    try { _selectedKey = 'lake'; } catch(e){}
    renderCanvas();
  });
}
async function restoreLake(m){
  await onSign(async () => {
    _lakeCumMatrix = m ? DOMMatrix.fromMatrix(m) : null;
    await _restoreWorkingFromMaster();
    try { _rescanLakeBBox(); } catch(e){}
    _floatingIslands = []; _disconnectedBodies = [];
    if(doubleWallStyle === 'cutout' || doubleWallStyle === 'single-cutout'){ try { detectFloatingPieces(); } catch(e){} }
    try { evaluateAllRules(); } catch(e){}
    renderCanvas();
  });
}
SCREENS.lake = () => {
  stage(3);
  LAKE.saved = _lakeCumMatrix ? DOMMatrix.fromMatrix(_lakeCumMatrix) : null;
  LAKE.on = true;
  S.editing = { step: STEPS.color.n, label: 'lake' };
  _origStepGo(STEPS.color.n);
  try { _selectedKey = 'lake'; } catch(e){}
  document.getElementById('lake-capture-app').classList.add('lca-words');
  try { window.CCSC_PREVIEW_FRAC = 0.62; updateBuilderViewportOffset(); } catch(e){}
  screen(`${etop('Lake & layout', 'The sign stays the same size; the lake moves within it.')}
    <p class="lca-hint">Drag the lake on the sign to move it. Drag a corner to resize it, or the round handle on top to turn it.</p>
    <h4 class="lca-sub">Turn</h4>
    <div class="lca-seg lca-seg2">
      <button type="button" data-act="turn" data-v="-15">⟲ Left</button>
      <button type="button" data-act="turn" data-v="15">Right ⟳</button></div>
    <h4 class="lca-sub">Size</h4>
    <div class="lca-seg lca-seg2">
      <button type="button" data-act="size" data-v="0.94">− Smaller</button>
      <button type="button" data-act="size" data-v="1.06">+ Bigger</button></div>
    <button type="button" class="lca-link lca-left" data-act="reset">Reset the lake</button>
    <details class="lca-more lca-adv"><summary>Advanced settings</summary>
      <button type="button" class="lca-row" data-act="shape"><b>Change the sign’s shape</b><small>Trim bare metal or add room. The price stays the same.</small></button>
      <button type="button" class="lca-row" data-act="look"><b>Try a different design</b><small>Back to the designs, with your words.</small></button>
      <button type="button" class="lca-row" data-act="adv" data-v="section"><b>Use only part of the lake</b><small>Pick just the section you want on your sign.</small></button>
      <button type="button" class="lca-row" data-act="adv" data-v="water"><b>Add missing water</b><small>Include another bay, branch, or nearby lake.</small></button>
      ${(window._lcDespur && window._lcDespur.removed > 0 && window._lcDespurOff !== true) ? '<button type="button" class="lca-row" data-act="adv" data-v="channels"><b>Bring back thin channels</b><small>We removed thin lines that looked like roads or creeks.</small></button>' : ''}
      <button type="button" class="lca-row" data-act="advover"><b>Wrong lake?</b><small>Start over with a different lake.</small></button>
    </details>
    ${efoot()}`, { tool:true });
  wire({
    turn(v){ return lakeStep(Number(v), 1); },
    size(v){ return lakeStep(0, Number(v)); },
    async reset(){ await restoreLake(LAKE.saved); },
    // The lake visit is kept (Done), then the other tool opens.
    async shape(){ await LAKE_SETTLE(); T('manual_edit_saved', { tool:'lake' }); endLake(); sessionCommit(); openTool('plate'); },
    async look(){ await LAKE_SETTLE(); endLake(); sessionCommit(); openTool('look'); },
    // 13 Oct (owner): the lake-shape tools that used to be "Adjust" on Is this
    // your lake?. The lake visit is kept, then lake-capture's lake edit opens.
    async adv(v){ await LAKE_SETTLE(); endLake(); sessionCommit(); await onSign(async () => {}); if(window._lcLakeEditStart) window._lcLakeEditStart(v); },
    advover(){ try { _lcStartOverAsk(); } catch(e){} },
  });
  try { renderCanvas(); } catch(e){}
};
// ── Sign shape ──────────────────────────────────────────────────────────────
// 7 Oct 2026 (owner): "an option to adjust the size of the sign -- can't go
// smaller than the lake, but there's a lot of bare room." The plate is the
// frozen reference _panelRefBBox (saved with the design), so a shorter or
// narrower sign is that reference trimmed on one axis. Never past what is on
// it: the lake, every line and icon, plus a clear edge (mounting holes sit
// near the corners). A trim comes off the side(s) with bare room, in
// proportion, so nothing on the sign moves. The long side is still the size
// they picked, so trimming the short side makes the sign itself smaller.
const PLATE = { saved:null };
function platePad(){
  const r = _panelRefBBox;
  return (r && (doubleWallStyle === 'cutout' || doubleWallStyle === 'single-cutout')) ? Math.max(r.lakeW, r.lakeH) * 0.05 : 0;
}
/** What has to stay on the plate, in frame px, with the edge it needs. */
function plateContent(){
  const pb = getPanelBounds(); if(!pb) return null;
  let ppi = null; try { ppi = pixelsPerInch(); } catch(e){}
  const edge = Math.max(8, (ppi || 0) * 0.75);
  const bb = computeSilhouetteBBox(silhouettePixels);
  // A lake cut through the plate keeps its structural border.
  const lp = platePad() || edge * 0.5;
  let x0 = bb ? bb.minX - lp : Infinity, y0 = bb ? bb.minY - lp : Infinity;
  let x1 = bb ? bb.maxX + lp : -Infinity, y1 = bb ? bb.maxY + lp : -Infinity;
  const ctx = document.getElementById('silhouetteCanvas').getContext('2d');
  const boxOf = (b) => {
    if(b.value){   // a text box's pos is its centre; measure the words as drawn
      const r = computeNameplate(ctx, b.value, b.font, b.size, b.pos.x, b.pos.y, b.mfgMode === 'nameplate' ? b.namePad : 0);
      return getRotatedBBox(r, b.rotation || 0);
    }
    return getIconTightBBox(b) || b.pos;
  };
  (textBoxes || []).filter(b => b && b.pos && b.value).concat((iconBoxes || []).filter(b => b && b.pos && (b.type || b.shape))).forEach(b => {
    let q; try { q = boxOf(b); } catch(e){ return; }
    if(!q || !isFinite(q.x) || !isFinite(q.w)) return;
    x0 = Math.min(x0, q.x - edge); y0 = Math.min(y0, q.y - edge);
    x1 = Math.max(x1, q.x + q.w + edge); y1 = Math.max(y1, q.y + q.h + edge);
  });
  return { pb, x0, y0, x1, y1 };
}
/** Fold any More Room boost into the reference, so the plate is the reference. */
function plateFold(){
  if(!_panelRefBBox || !(panelShortAxisBoost > 0)) return;
  const pb = getPanelBounds(); const m = platePad();
  if(pb) _panelRefBBox = { ..._panelRefBBox, minX: pb.x + m, maxX: pb.x + pb.w - m, minY: pb.y + m, maxY: pb.y + pb.h - m };
  panelShortAxisBoost = 0; panelAlign = 'center';
  _recomputePanelRect();
}
/** axis 'x'|'y', dir -1 trims, +1 grows. Returns false when it can't go further. */
function plateStep(axis, dir){
  if(!_panelRefBBox) return false;
  plateFold();
  const c = plateContent(); if(!c) return false;
  const { pb } = c;
  const lo = axis === 'x' ? pb.x : pb.y, size = axis === 'x' ? pb.w : pb.h, hi = lo + size;
  const cLo = axis === 'x' ? c.x0 : c.y0, cHi = axis === 'x' ? c.x1 : c.y1;
  const frame = axis === 'x' ? silhouettePixels.w : silhouettePixels.h;
  const step = Math.max(6, size * 0.06);
  const r = { ..._panelRefBBox }, kLo = axis === 'x' ? 'minX' : 'minY', kHi = axis === 'x' ? 'maxX' : 'maxY';
  if(dir < 0){
    const fLo = Math.max(0, cLo - lo), fHi = Math.max(0, hi - cHi), free = fLo + fHi;
    if(free < 1) return false;
    const take = Math.min(step, free);
    r[kLo] += take * fLo / free; r[kHi] -= take * fHi / free;
  } else {
    const room = frame - size; if(room < 1) return false;
    let add = Math.min(step, room), a = add / 2, b = add / 2;
    if(lo - a < 0){ b += a - lo; a = lo; }
    if(hi + b > frame){ a += hi + b - frame; b = frame - hi; }
    r[kLo] -= a; r[kHi] += b;
  }
  _panelRefBBox = r;
  _recomputePanelRect();
  try { evaluateAllRules(); } catch(e){}
  // Refit the view to the new plate (owner, 8 Oct): Taller/Wider used to grow
  // the sign past the preview's frame and crop it.
  try { _centerOnLake(); } catch(e){}
  try { renderCanvas(); } catch(e){}
  return true;
}
/** Every element (and the page) that is scrolled right now, with its position. */
function _plateScrollers(){
  const out = [[window, window.scrollY || 0]];
  for(let el = root; el && el !== document.body; el = el.parentElement){
    if(el.scrollTop > 0) out.push([el, el.scrollTop]);
  }
  document.querySelectorAll('.step-card, #lcAssist, #rightPanel, #leftPanel').forEach(el => { if(el.scrollTop > 0 && !out.some(p => p[0] === el)) out.push([el, el.scrollTop]); });
  return out;
}
function plateDims(){
  const pb = getPanelBounds(); let ppi = null; try { ppi = pixelsPerInch(); } catch(e){}
  if(!pb || !ppi) return '';
  const r = (v) => Math.round(v / ppi * 2) / 2;
  return `${r(pb.w)}″ wide × ${r(pb.h)}″ tall`;
}
SCREENS.plate = () => {
  stage(3);
  PLATE.saved = { ref: _panelRefBBox ? { ..._panelRefBBox } : null, boost: panelShortAxisBoost, align: panelAlign };
  S.editing = { step: STEPS.color.n, label: 'plate' };
  _origStepGo(STEPS.color.n);
  try { _selectedKey = null; } catch(e){}
  try { window.CCSC_PREVIEW_FRAC = 0.62; updateBuilderViewportOffset(); } catch(e){}
  const draw = (msg) => {
    screen(`${etop('Sign shape', 'Trim bare metal, or add room. It never gets smaller than what’s on it.')}
      <p class="lca-hint" data-lca-dims>${esc(plateDims())}</p>
      <h4 class="lca-sub">Height</h4>
      <div class="lca-seg lca-seg2">
        <button type="button" data-act="step" data-v="y-1">− Shorter</button>
        <button type="button" data-act="step" data-v="y1">+ Taller</button></div>
      <h4 class="lca-sub">Width</h4>
      <div class="lca-seg lca-seg2">
        <button type="button" data-act="step" data-v="x-1">− Narrower</button>
        <button type="button" data-act="step" data-v="x1">+ Wider</button></div>
      <p class="lca-tellmsg" role="status" data-lca-platemsg ${msg ? '' : 'hidden'}>${esc(msg)}</p>
      <p class="lca-hint">Your size choice is the longest side, so the price stays the same.</p>
      ${efoot()}`, { tool:true });
    wire({
      step(v){
        const axis = v.charAt(0), dir = Number(v.slice(1));
        // Update in place and pin every scroller (owner, 9 Oct): a full redraw
        // jumped back to the top, so each Wider meant scrolling down again.
        const pins = _plateScrollers();
        const ok = plateStep(axis, dir);
        const msg = ok ? '' : (dir < 0 ? 'That’s as small as it goes with everything on it.' : 'That’s as big as it goes.');
        const dims = root.querySelector('[data-lca-dims]'); if(dims) dims.textContent = plateDims();
        const m = root.querySelector('[data-lca-platemsg]');
        if(m){ m.textContent = msg; m.hidden = !msg; }
        const back = () => pins.forEach(([el, y]) => { try { if(el === window){ if(Math.abs((window.scrollY||0) - y) > 1) window.scrollTo(0, y); } else if(Math.abs(el.scrollTop - y) > 1) el.scrollTop = y; } catch(e){} });
        back(); requestAnimationFrame(() => { back(); setTimeout(back, 120); });
      },
    });
  };
  draw('');
  try { renderCanvas(); } catch(e){}
};
function endPlate(){
  S.editing = null;
  try { window.CCSC_PREVIEW_FRAC = null; updateBuilderViewportOffset(); } catch(e){}
  try { evaluateAllRules(); } catch(e){}
  try { renderCanvas(); } catch(e){}
}

// A drag released just before Done is still baking; wait for it.
const LAKE_SETTLE = () => Promise.race([onSign(() => {}), sleep(50)]).then(() => sleep(250));
function endLake(){
  LAKE.on = false;
  S.editing = null;
  document.getElementById('lake-capture-app').classList.remove('lca-words');
  try { window.CCSC_PREVIEW_FRAC = null; updateBuilderViewportOffset(); } catch(e){}
  try { _selectedKey = null; } catch(e){}
  try { evaluateAllRules(); } catch(e){}
  try { renderCanvas(); } catch(e){}
}

// ── Borrowing one builder control at a time ─────────────────────────────────
// The hub opens a real step (Text, Objects, Finalize) and every way out of it --
// Done, a Next button, a pill -- comes back to the hub instead of continuing
// through the wizard. That is the "wizard as an implementation detail" rule.
const _origStepGo = window.stepGo;
window.stepGo = function(n){
  if(S.editing && n !== S.editing.step){
    // Finalize's own "back" and any Next inside an edited step land here.
    return safely(() => backToHub());
  }
  return _origStepGo.apply(this, arguments);
};

function openEditor(step, label, message){
  S.editing = { step, label };
  stage(label === 'finalize' ? 4 : 3);
  const app = document.getElementById('lake-capture-app');
  app.classList.add('lc-assist-editing');
  // Review and the full builder tools scroll as one page on phones too, and
  // hold a history entry so the phone's Back button returns to the design.
  app.classList.add('lc-flow'); app.classList.remove('lca-tool', 'lca-stk-on');
  histPush();
  showBuilder();
  _origStepGo(step);
  try { updateBuilderViewportOffset(); } catch(e){}
  { const sh = shellEl(); if(sh) sh.scrollTop = 0; }
  let bar = document.getElementById('lcaDoneBar');
  if(!bar){
    bar = document.createElement('div'); bar.id = 'lcaDoneBar'; bar.className = 'lca-donebar';
    const steps = document.getElementById('stepsContainer'); steps.parentNode.insertBefore(bar, steps);
  }
  // Review: the designer nav's "‹ Back" already returns to the design, so the
  // second back bar goes (8 Oct c) -- the price and Approve sit higher.
  bar.classList.toggle('lca-donebar-fin', label === 'finalize');
  bar.innerHTML = label === 'finalize'
    ? `<button type="button" class="lca-back" data-lca="hub">‹ Back to my design</button>`
    : `<span>${esc(message || '')}</span><button type="button" class="lc-btn lc-btn-primary" data-lca="hub">Done</button>`;
  bar.style.display = '';
  bar.onclick = (e) => { if(e.target.closest('[data-lca=hub]')) safely(() => { if(label === 'marker') T1('marker_placed'); backToHub(); }); };
  // Review: "Save for later" sits under the add-to-cart button (8 Oct c) and
  // opens the hub's email form, focused.
  if(label === 'finalize'){
    try { finSizes(); } catch(e){ try { console.warn('[assist] review sizes', e); } catch(_){} }
    const fb = document.getElementById('finalizeBtn');
    if(fb && !document.getElementById('lcaFinSave')){
      const a = document.createElement('button'); a.type = 'button'; a.id = 'lcaFinSave'; a.className = 'lca-finsave'; a.textContent = 'Save for later';
      a.onclick = () => safely(() => SAVE.open('review'));
      const after = document.querySelector('#step10 .lc-fin-edit') || document.getElementById('lcConsentLine') || fb;
      after.parentNode.insertBefore(a, after.nextSibling);
    }
  }
}

function backToHub(){
  if(WORDS.on) endWords();
  if(LAKE.on) endLake();
  if(S.editing && S.editing.label === 'plate') endPlate();
  S.editing = null;
  const app = document.getElementById('lake-capture-app');
  app.classList.remove('lc-assist-editing');
  const bar = document.getElementById('lcaDoneBar'); if(bar) bar.style.display = 'none';
  SCREENS.hub();
}

function leave(){
  // "I'll design it myself": exactly today's builder, from the Size step.
  window.CCSC_ASSIST_ACTIVE = false;
  const app = document.getElementById('lake-capture-app');
  app.classList.remove('lc-assisting', 'lc-assist-editing');
  if(root) root.style.display = 'none';
  showBuilder();
  _origStepGo(STEPS.size.n);
}

// ── "Save your design" (12 Oct 2026, owner) ──────────────────────────────────
// Email capture once a design exists: a bottom sheet on phones, a small card
// on desktop. Wording pairs saving with the launch price -- "your $50 launch
// price saved with it" -- and never says the email unlocks the discount
// (FIRST50 applies to every order; claiming otherwise would be untrue).
// Auto-shows ONCE per visit, only on Customize, on the first of: back from an
// edit, 60 s on Customize, back to the tab after leaving it, or (desktop) the
// mouse leaving the window. "Not now" ends it for the visit; never more than
// two auto-shows per browser; never after saving or adding to cart. The
// designer nav's Save, the hub's "Save for later" and Review's link open it
// any time. Uses the existing Apps Script emailDesign (Leads/<stamp>/).
const SAVE = {
  el: null, draftK: null, sending: null, trigger: null, edited: false, autoDone: false, dismissed: false, timer: 0, hiddenAt: 0, busy: false, err: '', bound: false,
  store(){ try { return JSON.parse(localStorage.getItem('lc_save_prompt') || '{}') || {}; } catch(e){ return {}; } },
  put(o){ try { localStorage.setItem('lc_save_prompt', JSON.stringify(Object.assign(this.store(), o))); } catch(e){} },
  hasDesign(){ return !!(window.CCSC_ASSIST_ACTIVE && (S.chosen >= 0 || S.fromOrder || (S.onDrafts && Number.isInteger(this.draftK) && S.images[this.draftK])) && cleanSilhouette); },
  setSaveable(on){ try { document.getElementById('lake-capture-app').classList.toggle('lca-saveable', !!on); } catch(e){} },
  onCustomize(){ const app = document.getElementById('lake-capture-app'); return !!(app && app.classList.contains('lca-hub') && !app.classList.contains('lc-assist-editing') && !isTool()); },
  carted(){ let busy = false; try { busy = _submitInProgress; } catch(e){} return busy || !!document.querySelector('#lcAddedPanel.is-open'); },
  autoOk(trigger){
    if(this.autoDone || this.dismissed || this.sending || this.el && !this.el.hidden) return false;
    // 15 Oct: Step 2 too, but only for a visitor who left for another app and
    // came back -- never a timer over cards somebody is still comparing.
    const drafts = trigger === 'return' && S.onDrafts && !S.editing && draftInView() >= 0;
    if(drafts) this.draftK = draftInView();
    if(!this.hasDesign() || S.mailed || this.carted() || !(this.onCustomize() || drafts)) return false;
    if(window._lcTestBrowser && window._lcTestBrowser() && !window.CCSC_SAVE_TEST) return false;
    const st = this.store(); if(st.saved || (st.n || 0) >= 2) return false;
    const f = document.activeElement; if(f && /^(INPUT|TEXTAREA|SELECT)$/.test(f.tagName)) return false;
    return true;
  },
  auto(trigger){ if(!this.autoOk(trigger)) return false; this.autoDone = true; this.put({ n: (this.store().n || 0) + 1 }); this.open(S.onDrafts && trigger === 'return' ? 'drafts-return' : trigger); return true; },
  // Called at the end of every Customize render.
  onHub(){
    this.setSaveable(this.hasDesign());
    this.bind();
    if(this.edited){ this.edited = false; setTimeout(() => this.auto('edit'), 700); }
    if(!this.timer && !this.autoDone){
      const ms = window.CCSC_SAVE_TIMER_MS || 60000;
      this.timer = setTimeout(() => { this.timer = 0; this.auto('timer'); }, ms);
    }
  },
  bind(){
    if(this.bound) return; this.bound = true;
    document.addEventListener('visibilitychange', () => {
      if(document.hidden){ this.hiddenAt = Date.now(); return; }
      if(this.hiddenAt && Date.now() - this.hiddenAt > 4000) setTimeout(() => this.auto('return'), 400);
      this.hiddenAt = 0;
    });
    document.addEventListener('mouseout', (e) => {
      if(e.relatedTarget || e.clientY > 0) return;
      if(!(window.matchMedia && window.matchMedia('(min-width:1025px) and (hover:hover)').matches)) return;
      this.auto('exit');
    });
    document.addEventListener('keydown', (e) => { if(e.key === 'Escape' && this.el && !this.el.hidden) this.dismiss(); });
    // The sheet is fixed to the bottom of the screen; on iPhone the keyboard
    // slides over the LAYOUT viewport, so it would cover the email field. Lift
    // the sheet by the keyboard's height (visualViewport) while it is open.
    const vv = window.visualViewport;
    if(vv){ const lift = () => this.lift(); vv.addEventListener('resize', lift); vv.addEventListener('scroll', lift); }
  },
  lift(){
    if(!this.el || this.el.hidden) return;
    const vv = window.visualViewport; if(!vv) return;
    const kb = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
    this.el.style.bottom = kb > 60 ? kb + 'px' : '';
  },
  open(trigger){
    if(!this.hasDesign()) return;
    this.trigger = trigger; this.err = '';
    this.render(); this.el.hidden = false; this.bind(); this.lift();
    document.documentElement.classList.add('lca-save-on');
    T('save_prompt_shown', { trigger });
    // A tap on Save means they want to type; an automatic sheet waits for them.
    if(['nav','hub','review','drafts'].includes(trigger)) setTimeout(() => { const i = this.el && this.el.querySelector('#lcaSaveMail'); if(i) i.focus(); }, 120);
  },
  close(){ if(this.el){ this.el.hidden = true; this.el.style.bottom = ''; } document.documentElement.classList.remove('lca-save-on'); },
  dismiss(){ if(!this.el || this.el.hidden) return; if(!S.mailed && !this.sending){ this.dismissed = true; T('save_prompt_dismissed', { trigger: this.trigger }); } this.close(); },
  render(){
    if(!this.el){
      const el = document.createElement('div');
      el.id = 'lcaSave'; el.className = 'lca-save'; el.hidden = true;
      el.setAttribute('role', 'dialog'); el.setAttribute('aria-labelledby', 'lcaSaveTitle');
      document.body.appendChild(el);
      el.addEventListener('click', (e) => {
        const b = e.target.closest('[data-save]'); if(!b) return;
        e.preventDefault();
        const a = b.getAttribute('data-save');
        if(a === 'close') return this.dismiss();
        if(a === 'send') return safely(() => this.send());
      });
      el.addEventListener('submit', (e) => { e.preventDefault(); safely(() => this.send()); });
      // Keyboard in / out: re-measure once it has finished moving.
      el.addEventListener('focusin', () => { setTimeout(() => this.lift(), 350); setTimeout(() => this.lift(), 800); });
      el.addEventListener('focusout', () => setTimeout(() => this.lift(), 350));
      this.el = el;
    }
    const off = launchOff();
    const price = off ? `, with your <b>$${off} launch price</b> saved alongside it` : '';
    const X = `<button type="button" class="lca-save-x" data-save="close" aria-label="Close"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>`;
    // 13 Oct (owner: a save took ~30 s and the email landed in spam): the
    // send runs in the background and this card says so -- nobody waits on it.
    if(this.sending){
      this.el.innerHTML = `<div class="lca-save-card">${X}
        <p class="lca-save-title" id="lcaSaveTitle">Saving your design…</p>
        <p class="lca-save-body">We’re sending it to <b>${esc(this.sending)}</b>. You can keep designing — we’ll let you know here when it’s sent.</p>
        <button type="button" class="lc-btn lc-btn-primary lca-save-go" data-save="close">Keep designing</button></div>`;
      return;
    }
    if(S.mailed){
      this.el.innerHTML = `<div class="lca-save-card">${X}
        <p class="lca-save-title" id="lcaSaveTitle">Sent!</p>
        <p class="lca-save-body">Check <b>${esc(S.mailed)}</b> for your design and a link back to it.${off ? ` Your $${off} launch discount is applied automatically when you order.` : ''}</p>
        <p class="lca-save-body lca-save-spam">Don’t see it in a few minutes? Check your <b>spam or junk</b> folder and tap <b>“Not spam”</b> (or move it to your Inbox), so our emails reach you.</p>
        <button type="button" class="lc-btn lc-btn-primary lca-save-go" data-save="close">Keep designing</button></div>`;
      return;
    }
    const title = this.trigger === 'return' ? 'Welcome back. Save your design?' : this.trigger === 'drafts' ? 'Not ready to choose?' : this.trigger === 'drafts-return' ? 'Welcome back. Save this design?' : 'Don’t lose your design';
    this.el.innerHTML = `<form class="lca-save-card" novalidate>
      <button type="button" class="lca-save-x" data-save="close" aria-label="Close"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>
      <p class="lca-save-title" id="lcaSaveTitle">${title}</p>
      <p class="lca-save-body">We’ll email you a picture and a link back to it${price}, ready whenever you are.</p>
      <label class="lca-save-lbl" for="lcaSaveMail">Email address</label>
      <input id="lcaSaveMail" type="email" inputmode="email" autocomplete="email" maxlength="200" placeholder="you@example.com" enterkeyhint="send" />
      <label class="lca-save-opt"><input type="checkbox" id="lcaSaveOpt" /> Also send me occasional ideas and offers from Clear Creek.</label>
      ${this.err ? `<p class="lca-save-err" role="alert">${esc(this.err)}</p>` : ''}
      <button type="submit" class="lc-btn lc-btn-primary lca-save-go" data-save="send"${this.busy ? ' disabled' : ''}>${this.busy ? 'Saving…' : 'Save my design'}</button>
      <p class="lca-save-fine">We’ll email your design and a few reminders. Unsubscribe anytime.</p>
      <button type="button" class="lca-save-not" data-save="close">Not now</button></form>`;
  },
  async send(){
    if(this.busy || !this.el) return;
    const box = this.el.querySelector('#lcaSaveMail');
    const email = String(box && box.value || '').trim();
    const marketing = !!(this.el.querySelector('#lcaSaveOpt') || {}).checked;
    const source = ['nav','hub','review','drafts'].includes(this.trigger) ? this.trigger : this.trigger === 'drafts-return' ? 'drafts' : 'sheet';
    if(!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)){ this.err = 'That email address doesn’t look right.'; this.render(); const i = this.el.querySelector('#lcaSaveMail'); if(i){ i.value = email; i.focus(); } return; }
    this.err = ''; this.busy = true; this.sending = email; this.render();
    // Leaving mid-send would lose it: ask first (only while it is in flight).
    const guard = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', guard);
    let r;
    try { r = await sendDesignEmail(email, marketing, source, /^drafts/.test(this.trigger) && S.onDrafts ? this.draftK : null); }
    finally { window.removeEventListener('beforeunload', guard); this.busy = false; this.sending = null; }
    if(!r.ok){
      // Reopen the form with the address kept, so a retry is one tap.
      this.err = r.err; this.render(); this.el.hidden = false; this.lift();
      document.documentElement.classList.add('lca-save-on');
      const i = this.el.querySelector('#lcaSaveMail'); if(i) i.value = email; return;
    }
    this.put({ saved: true });
    // The confirmation (with the spam note) shows whether or not they closed
    // the card while it was sending.
    this.render(); this.el.hidden = false; this.lift();
    document.documentElement.classList.add('lca-save-on');
    if(S.at === 'hub' && this.onCustomize()) SCREENS.hub({ keep:true });
  },
};
/** Email the design to the shopper (Apps Script emailDesign). {ok} or {ok:false, err}. */
async function sendDesignEmail(email, marketing, source, draftK){
  // A test browser never emails anyone or writes a Lead (lake-capture.js _lcTestBrowser).
  if(window._lcTestBrowser && window._lcTestBrowser() && !window.CCSC_SAVE_TEST) return { ok:false, err:'Test browser: nothing was sent.' };
  const t0 = Date.now();
  try {
    // From the drafts screen the builder holds whatever was built last (a
    // preview of another look), so the draft is applied and read under ONE
    // turn of the lock -- a preview build cannot slip in between.
    const draft = Number.isInteger(draftK) ? S.drafts[draftK] : S.drafts[S.chosen];
    let image, state, words;
    await onSign(async () => {
      if(Number.isInteger(draftK) && draft) await applyDraft(draft);
      image = await snapshot();
      state = serializeDesign();
      words = (textBoxes || []).map(t => String(t.value || '').trim()).filter(Boolean).join(' ');
    });
    state.originalFolderName = null;   // a saved design, not an order: buying it later makes a NEW order
    const r = await fetch(APPS_SCRIPT_URL, { method:'POST', body: JSON.stringify({
      action:'emailDesign', email, marketing, source,
      editBase: location.origin + location.pathname,
      name: (words || (window.CCSC_LAKE_NAME && window.CCSC_LAKE_NAME.name) || 'Your lake sign').slice(0, 60),
      lake: String((window.CCSC_LAKE_NAME && window.CCSC_LAKE_NAME.name) || '').slice(0, 60),
      summary: [ (LOOKS[(draft || {}).look] || {}).name, (signColor && panelColor) ? signColor.name + ' + ' + panelColor.name : '', S.size ? S.size + '″' : '' ].filter(Boolean).join(' · '),
      launchOff: launchOff(),
      state: JSON.stringify(state),
      preview: String(image || '').split(',')[1] || '',
    }) }).then(x => x.json());
    if(!(r && r.ok)){ try { CCSC_TRACK.error('save', (r && r.error === 'cap') ? 'cap' : 'send'); } catch(e){} }
    if(!(r && r.ok)) return { ok:false, err: (r && r.error === 'cap') ? 'We can’t send more emails today — please try again tomorrow.' : 'Sorry — that didn’t send. Please try again.' };
    S.mailed = email;
    T('design_emailed', { source, marketing: marketing ? 1 : 0, count: marketing ? 1 : 0, ms: Date.now() - t0 });
    try { CCSC_TRACK.meta('Lead', { identity: { email } }); } catch(e){}
    return { ok:true };
  } catch(e){ try { CCSC_TRACK.error('save', 'network'); } catch(_){} return { ok:false, err:'Sorry — that didn’t send. Please try again.' }; }
}

// Which screen is up (S.at), for the designer's Back (CCSC_ASSIST.back).
Object.keys(SCREENS).forEach(k => { const f = SCREENS[k]; SCREENS[k] = function(){ S.at = k; return f.apply(this, arguments); }; });
// Screens referenced by name from the Back buttons.
SCREENS.go = (v) => SCREENS[v]();

// ── Entry ───────────────────────────────────────────────────────────────────
// Called by lake-capture.js where it would otherwise move on to the Size step.
// Returns true to say "the assisted flow has taken over".
window.CCSC_ASSIST = {
  hasLook(k){ return !!(k && Object.prototype.hasOwnProperty.call(LOOKS, k)); },
  /** The look on the sign now (the last style tapped, else the chosen design's) -- add_to_cart's `look`. */
  currentLook(){ try { const d = S.drafts[S.chosen]; return S.styleLook || (d && d.look) || null; } catch(e){ return null; } },
  /** "Save your design" sheet (12 Oct): the designer nav's Save, or any caller. */
  savePrompt(trigger){ try { SAVE.open(trigger || 'nav'); } catch(e){} },
  onCaptured(){
    // An edit link is rebuilding a finished order: let it finish, then
    // onRestored opens the design screen with it. (The offer used to appear
    // here, on top of the half-restored wizard.)
    if(window._ccscRestoring) return false;
    try {
      // A new lake (first capture or "Change lake") starts the assisted flow
      // again. Their answers are kept; the drafts and the plate are the old
      // lake's, so they go.
      S.started = Date.now();
      S.drafts = []; S.images = []; S.chosen = -1; S.editing = null; S.words = null; S.stale = new Set();
      S.fromOrder = false; S.designOpen = false; S.directPending = false;
      S.mailed = ''; S.mailErr = ''; S.tellMsg = '';
      SAVE.close(); SAVE.setSaveable(false);
      resetLookPreviews();
      _baseRef = null;
      window.CCSC_ASSIST_ACTIVE = true;
      // Fold the map away now, not only after the first editor visit.
      try { setPreviewMode(true); } catch(e){}
      // 2026-10: designs first. No "Want us to design it for you?" -- three
      // finished designs straight away; the questions are "Personalize these".
      // Words only when the lake is reliably known (a traced lake, or a water
      // feature Google named): an address or a vague search prints nothing.
      CCSC_TRACK.setVariant('assisted');
      // 13 Oct (owner): else what they searched, cleaned (searchWords) -- never
      // anything with a digit, so an address is never printed.
      const known = (window.CCSC_LAKE_NAME && String(window.CCSC_LAKE_NAME.name || '').trim().slice(0, 32)) || searchWords();
      // A name WE put there for the previous lake goes with that lake.
      if(S.autoLake && S.brief.text.lake === S.autoLake){
        S.brief.text.lake = ''; S.brief.wants = S.brief.wants.filter(w => w !== 'lake');
      }
      S.autoLake = null;
      if(known && !S.brief.text.lake){
        S.brief.text.lake = known; S.autoLake = known;
        if(!S.brief.wants.includes('lake')) S.brief.wants = S.brief.wants.concat('lake');
      }
      generate();
      return true;
    } catch(e){ fail(e); return false; }
  },
  // "Edit your design" from the cart (lake-capture.js _finishEditRestore): the
  // order is back on the builder exactly as bought; show it on the design
  // screen, where every edit is one tap away. There are no drafts -- their words
  // are the order's words, and "Try another look" builds looks with them.
  onRestored(){
    try {
      S.started = Date.now();
      S.drafts = []; S.images = []; S.chosen = -1; S.editing = null; S.stale = new Set();
      resetLookPreviews();
      const words = (textBoxes || []).map(t => t.value).filter(v => String(v || '').trim());
      S.words = words.length ? words : null;
      S.fromOrder = true;
      S.orderIcons = (iconBoxes || []).filter(b => b.pos && b.type).map(b => b.type);
      try { S.size = parseInt(getSelectedSize(), 10) || S.size; } catch(e){}
      window.CCSC_ASSIST_ACTIVE = true;
      try { setPreviewMode(true); } catch(e){}
      T('step_viewed', { step:'reopened-order' });
      SCREENS.hub();
      return true;
    } catch(e){ fail(e); return false; }
  },
  // Keeping progress (8 Oct 2026): what the assisted flow needs to come back
  // after a reload or a Back from the cart -- their answers, their words, the
  // size, and whether they were still picking. The sign itself rides in the
  // builder's own snapshot (lake-capture.js _buildQuickRefreshSnapshot);
  // draft pictures are not stored, they are rebuilt (~3s) if needed.
  serialize(){
    try {
      if(!window.CCSC_ASSIST_ACTIVE) return null;
      return { v: 1, brief: S.brief, size: S.size, hints: S.hints || null, custOpen: !!S.custOpen,
               screen: S.onDrafts || (S.chosen < 0 && !S.fromOrder) ? 'drafts' : 'hub' };
    } catch(e){ return null; }
  },
  // The designer's "‹ Back" (lake-capture.js _lcDesignerBack): one logical step,
  // keeping the work. false = not ours (from Pick your favorite the builder
  // goes back to Check your shoreline).
  back(){
    try {
      if(!window.CCSC_ASSIST_ACTIVE) return false;
      // A Step 3 tool: Back = Done (owner, 10 Oct) -- its changes are kept.
      if(isTool() || PLACE.on || WORDS.on || LAKE.on || COLORS.on){ safely(() => commitEditor()); return true; }
      if(S.editing){ backToHub(); return true; }
      const at = S.at, Q = QSTEPS();
      if(Q.includes(at)){
        const i = Q.indexOf(at);
        if(i > 0){ SCREENS[Q[i - 1]](); return true; }
        if(S.drafts.length){ SCREENS.drafts(S.shown || 0); return true; }
        return false;
      }
      if(at === 'notes'){ SCREENS.look(); return true; }
      if(at === 'hub' && S.drafts.length && !S.fromOrder){ SCREENS.drafts(S.chosen >= 0 ? S.chosen : 0); return true; }
      if(at === 'hub' && S.fromOrder) return true;   // a reopened order has nothing behind its design
      return false;
    } catch(e){ return false; }
  },
  onSnapshotRestored(a){
    try {
      if(!a) return false;
      if(a.brief) S.brief = Object.assign(S.brief, a.brief);
      S.hints = a.hints || null;
      if(a.screen === 'drafts'){
        S.started = Date.now();
        window.CCSC_ASSIST_ACTIVE = true;
        try { setPreviewMode(true); } catch(e){}
        generate();
        return true;
      }
      // Their design is the one on the builder: open "Your design is ready"
      // with it, its words kept through any new look (as for a reopened order).
      S.custOpen = !!a.custOpen;
      // A reload put them back where they were: not a reopened order, not a
      // new visit to Customize (design_restored already said so). 10 Oct 2026.
      let ok = false;
      try { CCSC_TRACK.mute(); } catch(e){}
      try {
        ok = this.onRestored();
        if(a.size){ S.size = a.size; try { selectSize(a.size); } catch(e){} }
        if(a.size || S.custOpen) SCREENS.hub();
      } finally { try { CCSC_TRACK.unmute(); } catch(e){} }
      return ok;
    } catch(e){ fail(e); return false; }
  },
  // "Change lake" (lake-capture.js resetCapture): put the assisted panels down
  // so the map and search are what the customer sees. The next capture calls
  // onCaptured again and the flow restarts, answers kept.
  // The background look previews draw on the one real builder: lake-capture
  // waits for the one in flight before it restores the bare lake.
  idle(){ return LP.lock; },
  /** Lake & layout → Advanced settings, applied (lake-capture _lcLakeEditFinish):
   * the lake was re-traced; lay the SAME design on it -- look, words, font,
   * colours, markers, size. Hand-dragged positions are re-fitted. */
  async onLakeEdited(o){
    o = o || {};
    try {
      window.CCSC_ASSIST_ACTIVE = true;
      try { setPreviewMode(true); } catch(e){}
      _baseRef = null; applyLook._px = null;
      resetLookPreviews();
      // The cards were drawn on the old lake: redraw them if they are visited again.
      S.stale = new Set(S.images.map((x, k) => x ? k : -1).filter(k => k >= 0));
      const d0 = S.drafts[S.chosen] || S.drafts[0];
      if(!d0){ SCREENS.hub(); return; }
      const d = Object.assign({}, d0, {
        lines: (o.words && o.words.length) ? o.words.slice() : d0.lines,
        lineMode: 'asis', icons: (o.icons || []).slice(),
        signColorId: o.sc || d0.signColorId, panelColorId: o.pc || d0.panelColorId,
        fontOverride: o.font || null,
      });
      S.drafts[S.chosen >= 0 ? S.chosen : 0] = d;
      await onSign(() => applyDraft(d));
      if(S.size){ try { selectSize(S.size); } catch(e){} }
      SCREENS.hub();
    } catch(e){ fail(e); }
  },
  /** Advanced settings cancelled: everything is back on the builder; show the design. */
  onLakeEditCancelled(){
    try {
      window.CCSC_ASSIST_ACTIVE = true;
      try { setPreviewMode(true); } catch(e){}
      SCREENS.hub();
    } catch(e){ fail(e); }
  },
  // After the engine's Undo (lake-capture.js _undoLast): the size, the words and
  // the draft's colours follow what came back, and the screen redraws.
  /** A click off the sign cleared the selection (lake-capture.js): unmark the words field. */
  onDeselect(){ try { if(WORDS.on) markWordsField(); } catch(e){} },
  onUndo(){
    try {
      if(!window.CCSC_ASSIST_ACTIVE) return;
      S.size = parseInt(_widgetSize, 10) || S.size;
      const w = (textBoxes || []).map(t => t.value).filter(v => String(v || '').trim());
      if(w.length && S.words) S.words = w;
      const d = S.drafts[S.chosen];
      if(d && signColor && panelColor){ d.signColorId = signColor.id; d.panelColorId = panelColor.id; }
      if(COLORS.on) return SCREENS.colors({ keep:true });
      if(S.at === 'hub' && !S.editing && !isTool()) SCREENS.hub({ keep:true });
    } catch(e){}
  },
  // The room preview reads the live canvas; it queues behind any look preview
  // so it can only ever capture the customer's actual design.
  withSign(fn){ return onSign(fn); },
  purpose(){ return (S.brief && S.brief.purpose) || null; },
  onReset(){
    S.onDrafts = false;          // stops previewLooks after its current build
    try { if(PLACE.on) endPlace(); } catch(e){}
    try { if(WORDS.on) endWords(); } catch(e){}
    try { if(LAKE.on) endLake(); } catch(e){}
    try { if(COLORS.on) endColors(); } catch(e){}
    SESSION.mark = null;
    histDrop();
    S.editing = null;
    window.CCSC_ASSIST_ACTIVE = false;
    window.CCSC_PREVIEW_FRAC = null;
    document.getElementById('lake-capture-app').classList.remove('lc-flow', 'lca-tool', 'lca-stk-on');
    if(root) root.style.display = 'none';
    const bar = document.getElementById('lcaDoneBar'); if(bar) bar.style.display = 'none';
    const app = document.getElementById('lake-capture-app');
    if(app) app.classList.remove('lc-assisting', 'lc-assist-editing', 'lca-placing', 'lca-picking', 'lca-generating');
  },
  core: CORE,
};

const CSS = `
#lake-capture-app.lc-assisting #stepNavBar{display:none}
#lake-capture-app.lc-assist-editing #stepNavBar{display:none}
#lake-capture-app.lc-assisting [id^="panelBoost"]{display:none!important}
#lake-capture-app.lca-generating #rightPanel{opacity:.15;transition:opacity .2s}
.lc-assist{display:block;padding:8px 4px 24px;box-sizing:border-box;color:var(--lc-text);font-size:1rem}
.lca-head h3{font-family:var(--lc-heading-font);font-weight:600;font-size:clamp(1.35rem,2.4vw,1.65rem);line-height:1.25;margin:6px 0 8px;color:var(--navy)}
.lca-head p{margin:0 0 18px;color:#4c5a63;font-size:1.02rem;line-height:1.5}
.lca-wide{display:block;width:100%;margin:18px 0 6px;box-sizing:border-box}
.lca-link{display:block;margin:10px auto 0;background:none;border:0;color:#4c5a63;text-decoration:underline;cursor:pointer;font:inherit;font-size:1rem;padding:10px}
.lca-link.lca-left{margin:6px 0 0;padding:6px 0}
.lca-back{display:block;text-align:left;background:none;border:0;color:#4c5a63;cursor:pointer;font:inherit;font-size:1rem;padding:8px 0;margin-bottom:2px;min-height:44px}
.lca-sub{font-family:var(--lc-heading-font);font-size:1.08rem;font-weight:600;color:var(--navy);margin:22px 0 10px}
.lca-sub small{font-weight:400;color:#5d6b74;font-size:.95rem;margin-left:6px}
/* Tiles: the one control for every choice. */
.lca-tiles{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
@media (max-width:420px){.lca-tiles{grid-template-columns:1fr}.lca-tiles.lca-icons{grid-template-columns:repeat(2,minmax(0,1fr))}}
.lca-tile{position:relative;display:flex;align-items:center;gap:12px;min-height:64px;padding:14px 16px;text-align:left;border:1.5px solid #d5dee3;border-radius:14px;background:#fff;color:var(--navy);font:inherit;cursor:pointer;box-shadow:0 1px 2px rgba(1,35,53,.05);transition:border-color .15s,box-shadow .15s,transform .1s}
@media (hover:hover){.lca-tile:hover{border-color:#9fb3be;box-shadow:0 3px 12px rgba(1,35,53,.1)}}
/* The theme fades every hovered button/link to 60%; on a phone that "hover"
   sticks after a tap and the tile just tapped looks disabled. */
#lcAssist button:not(:disabled):hover,#lcAssist a:hover,#lcAssist label:hover,#lcAssist input:hover,#lcAssist textarea:hover{opacity:1}
.lca-tile.on{border-color:var(--mint);box-shadow:0 0 0 3px var(--mint)}
.lca-tile-emoji{font-size:1.6rem;line-height:1;flex:0 0 auto}
.lca-tile-text{display:flex;flex-direction:column;gap:2px;min-width:0}
.lca-tile-text strong{font-size:1.05rem;font-weight:600}
.lca-tile-text small{font-size:.92rem;color:#5d6b74;line-height:1.35}
.lca-check{position:absolute;top:8px;right:10px;width:24px;height:24px;border-radius:50%;border:2px solid #c9d3da;color:transparent;display:flex;align-items:center;justify-content:center;font-size:.85rem;font-weight:700}
.lca-tile.on .lca-check{background:var(--mint);border-color:var(--mint);color:var(--navy)}
.lca-look{flex-direction:column;align-items:center;justify-content:center;gap:10px;text-align:center}
.lca-look .lca-tile-text{align-items:center;text-align:center}
.lca-look > .lca-pair{justify-content:center}
.lca-pair{display:flex;width:100%}
.lca-pair img,.lca-pair i{display:block;flex:0 0 auto;width:48px;height:48px;border-radius:50%;object-fit:cover;box-shadow:0 0 0 2px #fff,0 0 0 3px rgba(1,35,53,.15)}
.lca-pair > :nth-child(n+2){margin-left:-12px}
.lca-swatches img,.lca-swatches i{width:44px;height:44px}
.lca-lookprev{display:flex;align-items:center;justify-content:center;flex-direction:column;gap:8px;width:100%;aspect-ratio:1;border-radius:10px;background:#f3f6f8;overflow:hidden}
.lca-lookprev img.lca-lookimg{display:block;width:100%;height:100%;object-fit:contain;background:#fff}
.lca-lookprev .lca-pair{justify-content:center}
.lca-lookprev em{font-style:normal;font-size:.82rem;color:#5d6b74}
.lca-icontile{flex-direction:column;justify-content:center;text-align:center;min-height:120px}
.lca-icontile .lca-tile-text{align-items:center}
.lca-ico{width:56px;height:56px}
.lca-fields{display:grid;gap:12px;margin-top:16px}
.lca-field span{display:block;font-size:.98rem;font-weight:600;color:var(--navy);margin-bottom:6px}
.lca-field input,.lca-notes{width:100%;box-sizing:border-box;padding:14px;border:1.5px solid #c9d3da;border-radius:12px;font:inherit;font-size:17px;background:#fff;color:var(--lc-text)}
.lca-field input:focus,.lca-notes:focus{outline:none;border-color:var(--navy);box-shadow:0 0 0 3px rgba(45,255,179,.45)}
.lca-field small{display:block;color:#5d6b74;font-size:.9rem;margin-top:6px}
.lca-notes{min-height:150px;line-height:1.5;resize:vertical}
.lca-fine{color:#6b7880;font-size:.85rem;line-height:1.45;margin:10px 0 0}
.lca-qstep{color:#6b7880;font-size:.9rem;font-weight:600;letter-spacing:.02em;margin:0 0 4px}
/* Creating */
.lca-busy{text-align:center;padding:36px 0}
.lca-busy h3{font-family:var(--lc-heading-font);font-size:1.4rem;color:var(--navy);margin:0 0 16px}
.lca-spin{width:44px;height:44px;margin:0 auto 18px;border-radius:50%;border:4px solid #dfe6ea;border-top-color:var(--navy);animation:lcaspin 1s linear infinite}
@keyframes lcaspin{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){.lca-spin{animation:none}}
.lca-progress{list-style:none;margin:0 auto;padding:0;display:inline-flex;flex-direction:column;gap:10px;text-align:left}
.lca-progress li{font-size:1.05rem;color:#8a969d;padding-left:30px;position:relative}
.lca-progress li:before{content:"";position:absolute;left:0;top:2px;width:18px;height:18px;border-radius:50%;border:2px solid #c9d3da}
.lca-progress li.now{color:var(--navy);font-weight:600}
.lca-progress li.now:before{border-color:var(--navy);border-top-color:transparent;animation:lcaspin 1s linear infinite}
.lca-progress li.done{color:var(--navy)}
.lca-progress li.done:before{content:"✓";background:var(--mint);border-color:var(--mint);font-size:.75rem;line-height:18px;text-align:center;color:var(--navy)}
/* Drafts */
.lca-note{background:#f1f6f8;border-radius:12px;padding:12px 14px;font-size:.98rem;margin:0 0 12px}
.lca-dcard{border:1.5px solid #d5dee3;border-radius:18px;overflow:hidden;background:#fff;box-shadow:0 4px 18px rgba(1,35,53,.08)}
.lca-dimg{position:relative;background:#fff;padding-bottom:26px}
.lca-dimg img{display:block;width:100%;height:auto;max-height:62vh;object-fit:contain}
.lca-arrow{position:absolute;top:50%;transform:translateY(-50%);width:52px;height:52px;border-radius:50%;border:none;background:rgba(1,35,53,.85);color:#fff;font-size:1.9rem;line-height:1;cursor:pointer;box-shadow:0 2px 10px rgba(0,0,0,.2);display:flex;align-items:center;justify-content:center}
.lca-prev{left:10px}.lca-next{right:10px}
.lca-arrow:disabled{opacity:0;pointer-events:none}
.lca-count{position:absolute;left:50%;bottom:10px;transform:translateX(-50%);background:rgba(1,35,53,.85);color:#fff;font-size:1rem;font-weight:600;padding:5px 14px;border-radius:999px}
.lca-dmeta{display:flex;justify-content:space-between;gap:14px;padding:16px 18px 4px}
.lca-dmeta strong{font-family:var(--lc-heading-font);font-size:1.35rem;color:var(--navy)}
.lca-dmeta p{margin:4px 0 2px;font-size:1rem;color:#3d4a53}
.lca-dmeta small{font-size:.92rem;color:#5d6b74}
.lca-dmeta small.lca-dnote{display:block;margin-top:6px;color:#0b5f45}
.lca-dprice{text-align:right;flex:0 0 auto}
.lca-dprice b{display:block;font-size:1.45rem;color:var(--navy)}
#lake-capture-app #lcAssist .lca-choose{display:block;width:calc(100% - 36px);max-width:none;margin:12px 18px 18px;box-sizing:border-box}
.lca-dots{display:flex;justify-content:center;gap:8px;margin:14px 0 2px}
.lca-dots i{width:10px;height:10px;border-radius:50%;background:#c9d3da}
.lca-dots i.on{background:var(--navy)}
/* Browse every look */
.lca-browse{margin-top:26px;padding-top:18px;border-top:1px solid #e1e8ec}
.lca-browse .lca-sub{margin-top:0}
.lca-browse .lca-fine{margin:0 0 12px}
.lca-browse-grid{grid-template-columns:repeat(3,minmax(0,1fr))}
@media (max-width:520px){.lca-browse-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
.lca-browse-grid .lca-tile{min-height:0;padding:10px}
.lca-browse-grid .lca-pair img,.lca-browse-grid .lca-pair i{height:40px}
.lca-browse-grid .lca-tile-text strong{font-size:.98rem}
.lca-browse-grid .lca-tile-text small{font-size:.82rem}
.lca-tile.busy{opacity:.7;cursor:progress}
/* Hub */
.lca-facts{list-style:none;padding:0;margin:0 0 18px;display:flex;flex-wrap:wrap;gap:8px}
.lca-facts li{background:#f1f6f8;border-radius:999px;padding:8px 14px;font-size:.98rem;color:var(--navy)}
.lca-edits{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
.lca-edits > :last-child:nth-child(odd){grid-column:1/-1}
.lca-sizes{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px}
@media (max-width:520px){.lca-sizes{grid-template-columns:repeat(2,minmax(0,1fr))}}
.lca-size{position:relative;display:flex;flex-direction:column;align-items:center;gap:4px;min-height:112px;padding:22px 8px 12px;border:1.5px solid #d5dee3;border-radius:14px;background:#fff;color:var(--navy);font:inherit;cursor:pointer;text-align:center}
.lca-size strong{font-size:1.45rem}
.lca-size span{font-size:.88rem;color:#4c5a63;line-height:1.25}
.lca-size b{font-size:1rem;margin-top:auto}
.lca-size.on{border-color:var(--mint);box-shadow:0 0 0 3px var(--mint)}
.lca-badge{position:absolute;top:-11px;left:50%;transform:translateX(-50%);background:var(--mint);color:var(--navy);font-size:.75rem;font-weight:700;padding:3px 10px;border-radius:999px;white-space:nowrap}
.lca-sizehelp{color:#4c5a63;font-size:.95rem;line-height:1.45;margin:10px 0 0}
/* Colours */
#lcAssist img{opacity:1!important}
.lca-finishes{display:grid;grid-template-columns:repeat(auto-fill,minmax(76px,1fr));gap:12px 6px;margin-bottom:6px}
.lca-finish{display:flex;flex-direction:column;align-items:center;gap:6px;padding:4px 2px;border:0;background:none;color:var(--navy);font:inherit;font-size:.8rem;line-height:1.2;cursor:pointer;text-align:center}
.lca-finish-img{display:block;width:56px;height:56px;border-radius:50%;overflow:hidden;box-shadow:0 0 0 1px rgba(1,35,53,.18),0 1px 3px rgba(1,35,53,.15)}
.lca-finish-img img,.lca-finish-img i{display:block;width:100%;height:100%;object-fit:cover}
@media (hover:hover){.lca-finish:hover .lca-finish-img{box-shadow:0 0 0 2px #9fb0ba}}
.lca-finish.on .lca-finish-img{box-shadow:0 0 0 3px #fff,0 0 0 6px var(--mint)}
.lca-finish.on .lca-finish-name{font-weight:700}
/* Text editor */
.lca-fonts{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}
.lca-font{display:flex;flex-direction:column;gap:4px;padding:12px;border:1.5px solid #d5dee3;border-radius:12px;background:#fff;color:var(--navy);cursor:pointer;text-align:left;font:inherit}
.lca-font span{font-size:1.3rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.lca-font small{font-size:.82rem;color:#5d6b74}
.lca-font.on{border-color:var(--mint);box-shadow:0 0 0 3px var(--mint)}
.lca-font:disabled{opacity:.45;cursor:default}
.lca-seg{display:grid;grid-template-columns:repeat(3,1fr);border:1.5px solid #c9d3da;border-radius:12px;overflow:hidden}
.lca-seg button{display:flex;align-items:center;justify-content:center;min-height:50px;margin:0;padding:0 8px;border:0;background:#fff;color:var(--navy);font:inherit;font-size:1rem;text-align:center;cursor:pointer}
.lca-seg.lca-seg2{grid-template-columns:repeat(2,1fr)}
.lca-seg button+button{border-left:1.5px solid #c9d3da}
.lca-seg button.on{background:var(--navy);color:#fff;font-weight:600}
/* Picking a draft: the card is the preview. */
#lake-capture-app.lca-picking #previewArea{display:none!important}
/* No preview while picking: nothing to resize or recapture. */
#lake-capture-app.lca-picking.lc-past-capture #recaptureBtn,#lake-capture-app.lca-picking.lc-past-capture #silControls{display:none!important}
/* Full screen is the sign alone -- the builder's own panels already hide. */
#lake-capture-app.ccsc-preview-fs #lcAssist,#lake-capture-app.ccsc-preview-fs #lcStages,#lake-capture-app.ccsc-preview-fs #lcLakeBar{display:none!important}
@media (min-width:1025px){
  #lake-capture-app.lca-picking.lc-past-capture .ccsc-builder-shell{grid-template-columns:minmax(0,1fr)}
  #lake-capture-app.lca-picking.lc-past-capture .ccsc-builder-shell > *{grid-column:1;width:100%;max-width:720px;margin-left:auto;margin-right:auto}
  #lake-capture-app.lca-picking.lc-past-capture .ccsc-builder-shell > .lc-stages{max-width:760px}
}
/* Tap to place: a focused mode. The sign gets the room; one instruction. */
.lca-placebar{display:flex;align-items:flex-start;gap:14px}
.lca-placebar .lca-head p{margin-bottom:6px}
#lake-capture-app.lca-placing #silhouetteCanvas{cursor:crosshair}
#lake-capture-app.lca-placing .sil-wrap,#lake-capture-app.lca-words .sil-wrap{box-shadow:0 0 0 4px var(--mint) inset;border-radius:16px}
#lake-capture-app.lca-placing #lcLakeBar,#lake-capture-app.lca-words #lcLakeBar{display:none!important}
@media (min-width:1025px){
  #lake-capture-app.lca-placing.lc-past-capture .ccsc-builder-shell,#lake-capture-app.lca-words.lc-past-capture .ccsc-builder-shell{grid-template-columns:minmax(0,1.7fr) minmax(320px,.8fr)}
  #lake-capture-app.lca-placing.lc-past-capture #previewArea .sil-wrap,#lake-capture-app.lca-words.lc-past-capture #previewArea .sil-wrap{max-width:780px}
}
.lca-notfinal{margin:12px 0 0;padding:10px 14px;border-radius:10px;background:#eefcf6;color:var(--navy);font-size:.95rem;line-height:1.4;text-align:center}
.lca-field.lca-sel input{border-color:var(--mint);box-shadow:0 0 0 3px rgba(45,255,179,.35)}
.lca-hint{margin:-4px 0 14px;padding:10px 12px;border-radius:10px;background:#eefcf6;color:var(--navy);font-size:.95rem;line-height:1.4}
.lca-tapcue{flex:0 0 auto;display:inline-flex;margin-top:10px}
.lca-tapcue span{width:34px;height:34px;border-radius:50%;border:2px solid var(--navy);position:relative;animation:lcapulse 1.4s ease-out infinite}
.lca-tapcue span:before,.lca-tapcue span:after{content:"";position:absolute;background:var(--navy)}
.lca-tapcue span:before{left:50%;top:-8px;bottom:-8px;width:2px;margin-left:-1px}
.lca-tapcue span:after{top:50%;left:-8px;right:-8px;height:2px;margin-top:-1px}
@keyframes lcapulse{0%{box-shadow:0 0 0 0 rgba(45,255,179,.7)}100%{box-shadow:0 0 0 16px rgba(45,255,179,0)}}
@media (prefers-reduced-motion:reduce){.lca-tapcue span{animation:none}}
.lca-wide:disabled{cursor:default}
.lca-donebar{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 16px;margin:0 0 10px;background:#f1f6f8;border-radius:12px;font-size:1rem;color:var(--navy)}
.lca-donebar .lc-btn{padding:12px 24px;min-height:48px}
/* 2026-10 draft picker: every draft at once. A swipe row on phones/tablets,
   three across on desktop. */
.lca-grid{display:flex;gap:14px;overflow-x:auto;scroll-snap-type:x mandatory;-webkit-overflow-scrolling:touch;margin:0 -16px;padding:4px 16px 10px;scrollbar-width:none}
.lca-grid::-webkit-scrollbar{display:none}
.lca-gcard{flex:0 0 84%;scroll-snap-align:center;display:flex;flex-direction:column;border:1.5px solid #d5dee3;border-radius:18px;overflow:hidden;background:#fff;box-shadow:0 4px 18px rgba(1,35,53,.08)}
.lca-gimg{background:#fff;padding:10px 10px 0}
.lca-gimg img{display:block;width:100%;height:auto;max-height:52vh;object-fit:contain}
@media (max-width:899px){.lca-gcard{flex:0 0 68%}.lca-gimg{padding:8px 8px 0}.lca-gimg img{max-height:min(34vh,300px)}.lca-gmeta{padding:8px 12px 0}#lake-capture-app #lcAssist .lca-gcard .lca-choose{margin:10px 12px 12px;min-height:46px}}
@media (max-width:360px){.lca-gcard{flex:0 0 74%}}
.lca-gmeta{padding:12px 16px 2px;display:flex;flex-direction:column;gap:3px;flex:1}
.lca-gmeta strong{font-family:var(--lc-heading-font);font-size:1.3rem;color:var(--navy)}
.lca-gmeta small{font-size:.92rem;color:#5d6b74}
.lca-gmeta small.lca-dnote{color:#0b5f45}
.lca-wordsrow{display:flex;align-items:center;gap:8px;max-width:520px;margin:10px auto 6px;padding:0 4px}
.lca-wordsrow input{flex:1;min-width:0;min-height:50px;padding:0 14px;border:1.5px solid #c9d3da;border-radius:12px;background:#fff;color:var(--navy);font:inherit;font-size:17px;font-weight:600}
.lca-wordsrow input:focus{outline:none;border-color:var(--navy);box-shadow:0 0 0 3px rgba(45,255,179,.45)}
#lake-capture-app #lcAssist .lca-words-ed{flex:none;display:inline-flex;align-items:center;justify-content:center;width:50px;height:50px;border:1.5px solid #c9d3da;border-radius:12px;background:#f2fffa;color:#0a6b4c;cursor:pointer;opacity:1}
.lca-words-ed .lca-pencil{width:20px;height:20px}
.lca-gcard.is-pending .lca-gwait{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;aspect-ratio:1/1;max-height:52vh;margin:0 auto;border-radius:10px;background:#f3f6f8;color:#4a5a66;font-weight:600;font-size:.95rem}
.lca-gwait i{width:26px;height:26px;border-radius:50%;border:3px solid #2dffb3;border-right-color:transparent;animation:lcaGwait .8s linear infinite}
@keyframes lcaGwait{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){.lca-gwait i{animation:none;border-right-color:#2dffb3;width:12px;height:12px}}
@media (max-width:899px){.lca-gcard.is-pending .lca-gwait{max-height:min(34vh,300px)}}
#lake-capture-app #lcAssist .lca-gcard .lca-choose:disabled{opacity:.5;cursor:default}
.lca-gimg.is-updating{position:relative}
.lca-gimg.is-updating img{opacity:.35;transition:opacity .2s}
.lca-gimg.is-updating::after{content:'Updating…';position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);padding:4px 12px;border-radius:999px;background:rgba(1,35,53,.86);color:#fff;font-size:.85rem;font-weight:600}
.lca-gmeta small.lca-gwords{display:inline-flex;align-items:center;gap:5px;font-size:.86rem;color:#0a6b4c;line-height:1.3}
.lca-pencil{flex:0 0 auto}
.lca-gfrom{font-size:.95rem;font-weight:600;color:var(--navy);margin-top:4px}
#lake-capture-app #lcAssist .lca-gcard .lca-choose{display:block;width:calc(100% - 28px);max-width:none;margin:12px 14px 14px;box-sizing:border-box}
.lca-swipehint{text-align:center;font-size:.92rem;color:#5d6b74;margin:2px 0 6px}
.lca-pickrow{display:flex;flex-wrap:wrap;gap:10px;justify-content:center;margin:10px 0 4px}
.lca-wordslbl{margin:10px 0 4px;font-size:.92rem;font-weight:600;color:var(--navy)}
.lca-wordslbl small{font-weight:400;color:#5b6b76}
#lake-capture-app #lcAssist .lca-pill.lca-swap3{display:flex;align-items:center;justify-content:center;text-align:center;flex:0 0 auto;height:auto;min-height:50px;padding:12px 22px;line-height:1.25;margin:12px auto 0}
#lake-capture-app #lcAssist .lca-pickrow .lca-pill{display:inline-flex;align-items:center;justify-content:center;text-align:center}
.lca-gfrom s{font-weight:400;color:#6b7a84;margin-left:4px}
.lca-dots{display:flex;justify-content:center;gap:7px;margin:8px 0 2px}
.lca-dots i{width:7px;height:7px;border-radius:50%;background:#c8d3da;transition:background .2s,transform .2s}
.lca-dots i.on{background:var(--navy);transform:scale(1.15)}
.lca-pickrow{flex-direction:column;align-items:center;gap:4px}
/* 15 Oct: Step 2's email link -- a visible second choice, never louder than Choose. */
#lake-capture-app #lcAssist .lca-mailcards{display:inline-flex;align-items:center;gap:10px;min-height:48px;padding:10px 18px;border:1.5px solid #c9d3da;border-radius:12px;background:#fff;color:var(--navy);font:inherit;font-size:16px;cursor:pointer;touch-action:manipulation;text-align:left}
#lake-capture-app #lcAssist .lca-mailcards b{font-weight:700}
#lake-capture-app #lcAssist .lca-mailcards svg{flex:0 0 auto;color:#0e7c66}
#lake-capture-app #lcAssist .lca-gimg[data-act]{cursor:pointer}
.lca-pickrow .lca-pill{flex:0 0 auto !important;min-width:220px;height:auto}
.lca-pickalt{display:flex;flex-wrap:wrap;justify-content:center;gap:2px 18px}
#lake-capture-app #lcAssist .lca-pickalt .lca-link{font-size:.95rem;min-height:40px}
@media (max-width:899px){.lca-grid{align-items:flex-start}.lca-gcard{flex:0 0 80%}.lca-gimg img{max-height:min(38vh,320px)}.lca-gmeta{gap:1px;padding:8px 14px 0;flex:0 0 auto}.lca-gmeta strong{font-size:1.15rem}#lake-capture-app #lcAssist .lca-gcard .lca-choose{margin:10px 12px 12px;min-height:48px}}
@media (max-width:360px){.lca-gcard{flex:0 0 84%}}
@media (min-width:1025px){.lca-dots{display:none}}
@media (prefers-reduced-motion:reduce){.lca-dots i{transition:none}}
.lca-fams{display:flex;flex-wrap:wrap;gap:8px;margin:2px 0 14px}
#lake-capture-app #lcAssist .lca-fam{min-height:40px;padding:0 14px;border-radius:999px;border:1.5px solid #c9d3da;background:#fff;color:var(--navy);font:inherit;font-size:.92rem;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;opacity:1}
#lake-capture-app #lcAssist .lca-fam.on{background:var(--navy);border-color:var(--navy);color:#fff}
@media (max-width:599px){.lca-fams{flex-wrap:nowrap;overflow-x:auto;scrollbar-width:none;margin-right:-16px;padding-right:16px}.lca-fams::-webkit-scrollbar{display:none}#lake-capture-app #lcAssist .lca-fam{flex:0 0 auto}}
#lake-capture-app #lcAssist .lca-pill{flex:1 1 200px;min-height:50px;border-radius:999px;border:1.5px solid var(--navy);background:#fff;color:var(--navy);font:inherit;font-size:1rem;font-weight:600;cursor:pointer;padding:0 18px}
#lake-capture-app #lcAssist .lca-pill:disabled{opacity:.6;cursor:default}
.lca-myself{font-size:.95rem}
.lca-launch{margin:8px 0 0;font-size:.95rem;color:#0a6b4c;font-weight:600;text-align:center}
.lca-size b s{font-weight:400;color:#6b7a85;margin-right:4px}
@media (min-width:1025px){
  .lca-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));overflow:visible;margin:0;padding:4px 0 10px}
  .lca-gcard{flex:none}
  .lca-swipehint{display:none}
  #lake-capture-app.lca-picking.lc-past-capture .ccsc-builder-shell > *{max-width:1180px}
  #lake-capture-app.lca-picking.lc-past-capture .ccsc-builder-shell > .lc-stages{max-width:1180px}
}
.lca-tell{margin:6px 0 18px;padding:14px;border:1.5px solid var(--mint);border-radius:14px;background:#f2fffa}
.lca-tell label{display:block;margin-bottom:8px;color:var(--navy);font-size:1.05rem}
.lca-tell textarea{min-height:64px}
.lca-tellmsg{margin:10px 0 0;font-size:.95rem;color:var(--navy);line-height:1.45}
.lca-tell label small{display:block;font-size:.92rem;font-weight:400;color:#4a5a66;margin-top:2px}
.lca-tellideas{display:flex;flex-wrap:wrap;gap:6px;margin:8px 0 10px}
#lake-capture-app #lcAssist .lca-idea{min-height:36px;padding:4px 12px;border:1px solid #b9e8d6;border-radius:999px;background:#fff;color:var(--navy);font:inherit;font-size:.88rem;cursor:pointer;opacity:1}
#lake-capture-app #lcAssist .lca-moretools{display:block;margin:6px auto 14px;font-size:.95rem;text-align:center}
.lca-mail.is-closed{border:0;padding:0;text-align:center;margin:10px 0 4px}
#lake-capture-app #lcAssist .lca-saveopen{font-size:1rem;min-height:44px}
.lca-mail{margin:18px 0 6px;padding:14px;border:1.5px dashed #c9d3da;border-radius:14px}
.lca-mail label{display:block;color:var(--navy)}
.lca-mail label small{display:block;font-size:.92rem;color:#5d6b74;margin-top:2px}
.lca-mailrow{display:flex;flex-wrap:wrap;gap:8px;margin-top:10px}
.lca-mailrow input{flex:1 1 220px;min-height:48px;border:1.5px solid #c9d3da;border-radius:12px;padding:0 12px;font:inherit;font-size:16px;color:var(--navy);box-sizing:border-box}
#lake-capture-app #lcAssist .lca-mailrow .lca-pill{flex:0 1 auto}
.lca-mail .lca-mailopt{display:flex;gap:8px;align-items:flex-start;margin-top:10px;font-size:.92rem;color:#3d4a53}
.lca-mail .lca-mailopt input{margin-top:3px;width:18px;height:18px;flex:none}
.lca-mailmsg{margin:8px 0 0;font-size:.95rem;color:var(--navy)}
.lca-mailmsg.lca-err{color:#a3281f}
/* ── STEP 3 — 2026-10-08e: size -> price -> Review; changes optional ── */
.lca-buy{margin:0 0 6px}
#lake-capture-app #lcAssist .lca-buyh{margin:0 0 8px;font-size:1.05rem;font-weight:700;color:var(--navy);letter-spacing:0}
.lca-sizes2{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px}
/* Review's size row + "Make changes" (26 Oct, quickReview) */
#lake-capture-app .lca-finsizes{margin:0 0 12px}
#lake-capture-app .lca-finsizes-h{margin:0 0 8px;font-weight:700;color:var(--navy);font-size:1rem}
#lake-capture-app .lca-finsizes-row{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px}
#lake-capture-app .lca-fsz{display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:52px;padding:6px 2px;border:1.5px solid #d5dee3;border-radius:12px;background:#fff;color:var(--navy);font:inherit;font-size:1.1rem;font-weight:700;line-height:1.1;cursor:pointer;opacity:1;touch-action:manipulation}
#lake-capture-app .lca-fsz small{display:block;font-size:.58rem;font-weight:600;color:#0a6b4c;margin-top:3px;white-space:nowrap}
#lake-capture-app .lca-fsz.on{border-color:var(--mint);box-shadow:0 0 0 2px var(--mint);background:#f2fffa}
#lake-capture-app .lca-fsz:hover{opacity:1}
#lake-capture-app .lca-finmake{display:block;width:100%;margin:6px 0 12px;padding:10px 4px;background:none;border:0;color:#4a5a66;font:inherit;font-size:.95rem;text-align:center;cursor:pointer;touch-action:manipulation}
#lake-capture-app .lca-finmake b{color:var(--navy);text-decoration:underline}
#lake-capture-app .lca-finmake:hover{opacity:1}
#lcAssist button,#lcAssist input,#lcAssist textarea,#lcAssist a{touch-action:manipulation}
#lake-capture-app #lcAssist .lca-sz{position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:52px;padding:6px 2px;border:1.5px solid #d5dee3;border-radius:12px;background:#fff;color:var(--navy);font:inherit;font-size:1.15rem;font-weight:700;line-height:1.1;cursor:pointer;opacity:1}
#lake-capture-app #lcAssist .lca-sz small{display:block;font-size:.62rem;font-weight:600;color:#0a6b4c;letter-spacing:.02em;margin-top:3px;white-space:nowrap}
#lake-capture-app #lcAssist .lca-sz.on{border-color:var(--mint);box-shadow:0 0 0 2px var(--mint);background:#f2fffa}
.lca-pricerow{display:flex;align-items:flex-end;justify-content:space-between;gap:10px;margin:12px 0 12px}
.lca-price{display:flex;flex-wrap:wrap;align-items:baseline;gap:2px 8px;color:var(--navy)}
.lca-pnow{font-size:1.75rem;font-weight:800;line-height:1}
.lca-pwas{font-size:1.05rem;color:#6b7a85}
.lca-psave{flex-basis:100%;font-size:.9rem;font-weight:600;color:#0a6b4c;margin-top:2px}
.lca-why{position:relative;flex:none;font-size:.88rem;color:#4c5a63;text-align:right}
.lca-why summary{list-style:none;cursor:pointer;min-height:44px;display:flex;align-items:center;gap:4px;text-decoration:underline;text-underline-offset:2px}
.lca-why summary::-webkit-details-marker{display:none}
.lca-why summary:before{content:"ⓘ";text-decoration:none;font-size:1rem}
.lca-pricerow{flex-wrap:wrap}
.lca-why[open]{flex-basis:100%;text-align:left}
.lca-why[open] summary{min-height:32px}
.lca-why p{margin:0;padding:8px 12px;background:#f1f6f8;border-radius:10px;text-align:left;line-height:1.4;color:var(--navy)}
#lake-capture-app #lcAssist .lca-review{margin:0}
.lca-buy .lca-mail.is-closed{margin:4px 0 0}
.lca-cust{margin:12px 0 0;border-top:1px solid #e3e9ec}
/* 9 Oct c (owner): "Make it yours" must catch the eye -- a teal card that
   pulses on arrival (once per design), then keeps a soft teal ring. */
.lca-cust.is-cue{border-top:0;margin-top:16px}
#lake-capture-app #lcAssist .lca-cust.is-cue .lca-custbtn{padding:0 16px;border:2px solid rgba(45,255,179,.85);border-radius:14px;background:#effdf8;box-shadow:0 0 0 3px rgba(45,255,179,.18)}
#lake-capture-app #lcAssist .lca-cust.is-glow .lca-custbtn{animation:lcaCustGlow 2.4s ease-out .5s 1 both}
#lake-capture-app #lcAssist .lca-pill.lca-browse.is-cue{background:#e6fff5;border:2px solid #2DFFB3;color:var(--navy);font-weight:700;box-shadow:0 0 0 3px rgba(45,255,179,.18)}
#lake-capture-app #lcAssist .lca-pill.lca-browse.is-cue:hover{background:#d4fdee}
#lake-capture-app #lcAssist .lca-pill.lca-browse.is-glow{animation:lcaCustGlow 2.4s ease-out .8s 2 both}
@media (prefers-reduced-motion:reduce){#lake-capture-app #lcAssist .lca-pill.lca-browse.is-glow{animation:none}}
@keyframes lcaCustGlow{0%{box-shadow:0 0 0 0 rgba(45,255,179,0)}17%{box-shadow:0 0 0 8px rgba(45,255,179,.7)}33%{box-shadow:0 0 0 3px rgba(45,255,179,.2)}50%{box-shadow:0 0 0 8px rgba(45,255,179,.6)}67%{box-shadow:0 0 0 3px rgba(45,255,179,.2)}83%{box-shadow:0 0 0 8px rgba(45,255,179,.5)}100%{box-shadow:0 0 0 3px rgba(45,255,179,.18)}}
@media (prefers-reduced-motion:reduce){#lake-capture-app #lcAssist .lca-cust.is-glow .lca-custbtn{animation:none}}
#lake-capture-app #lcAssist .lca-custbtn{display:flex;width:100%;align-items:center;justify-content:space-between;min-height:52px;padding:0 2px;border:0;background:none;color:var(--navy);font:inherit;font-size:1.05rem;font-weight:700;text-align:left;cursor:pointer;opacity:1}
.lca-custbtn .lca-custpm{font-size:1.5rem;font-weight:400;line-height:1;color:#0a6b4c;padding-left:10px}
.lca-custtxt{display:flex;flex-direction:column;gap:2px;padding:10px 0}
.lca-custtxt small{font-size:.9rem;font-weight:400;color:#4a5a66;line-height:1.35}
.lca-custbody{padding:2px 0 6px}
.lca-custbody .lca-tell{margin:0 0 14px}
/* 9 Oct: wrap, so both word chips (the personalisation cue) are always visible */
.lca-custbody .lca-tellideas{flex-wrap:wrap;margin:8px 0 10px}
.lca-custbody .lca-tellideas::-webkit-scrollbar{display:none}
#lake-capture-app #lcAssist .lca-custbody .lca-idea{flex:none;white-space:nowrap;min-height:40px}
#lake-capture-app #lcAssist .lca-custbody .lca-sub{margin:4px 0 8px;font-size:1rem}
.lca-tools{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}
#lake-capture-app #lcAssist .lca-tool{display:flex;align-items:center;gap:8px;min-height:52px;padding:8px 12px;border:1.5px solid #d5dee3;border-radius:12px;background:#fff;color:var(--navy);font:inherit;font-size:.95rem;font-weight:600;line-height:1.2;text-align:left;cursor:pointer;opacity:1}
.lca-tool span{font-size:1.15rem;flex:none}
.lca-details{border-top:1px solid #e3e9ec;margin:0;color:var(--navy)}
.lca-details summary{cursor:pointer;min-height:48px;display:flex;align-items:center;font-size:.95rem;font-weight:600;color:#4c5a63}
.lca-details dl{display:grid;grid-template-columns:auto 1fr;gap:4px 14px;margin:0 0 12px;font-size:.92rem}
.lca-details dt{color:#5d6b74}
.lca-details dd{margin:0;overflow-wrap:anywhere}
.lca-sticky{display:none}
@media (max-width:900px){
  #lake-capture-app.lca-stk-on #lcAssist{scroll-padding-bottom:84px}
  .lca-sticky{position:sticky;bottom:0;z-index:20;display:flex;align-items:center;justify-content:space-between;gap:10px;margin:10px -16px 0;padding:8px 16px calc(8px + env(safe-area-inset-bottom));background:#fff;border-top:1px solid #e3e9ec;box-shadow:0 -4px 14px rgba(1,35,53,.08);visibility:hidden;opacity:0;transform:translateY(8px);transition:opacity .15s,transform .15s,visibility .15s}
  .lca-sticky.on{visibility:visible;opacity:1;transform:none}
  .lca-stkp{color:var(--navy);font-size:.95rem;white-space:nowrap}
  .lca-stkp b{font-size:1.2rem}
  .lca-stkp s{color:#6b7a85;font-size:.85rem}
  #lake-capture-app #lcAssist .lca-sticky .lc-btn{min-height:46px;padding:0 18px;width:auto;flex:none}
}
@media (max-width:360px){
  #lake-capture-app #lcAssist .lca-sz{font-size:1.02rem}
  #lake-capture-app #lcAssist .lca-sz small{font-size:.56rem}
  .lca-tools{grid-template-columns:1fr}
}
/* ── MOBILE UX REFACTOR — 2026-10-10 (owner's spec) ──
   Step 3 overview: size, four optional categories, "Or describe your changes",
   one Review. Focused editors: back link, title, common choices, one "More"
   disclosure, Done + Cancel. Colors & finishes: tiles in a grid. */
.lca-vh{position:absolute!important;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
.lca-pers{margin:18px 0 0}
#lake-capture-app #lcAssist .lca-pers .lca-buyh small{display:block;margin-top:3px;font-size:.92rem;font-weight:400;color:#4a5a66}
.lca-cats{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin:10px 0 0}
#lake-capture-app #lcAssist .lca-cat{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;min-height:84px;padding:12px 8px;border:1.5px solid #d5dee3;border-radius:12px;background:#fff;color:var(--navy);font:inherit;font-size:.98rem;font-weight:600;line-height:1.2;text-align:center;cursor:pointer;opacity:1}
#lake-capture-app #lcAssist .lca-cat:hover{border-color:#9fb3bf}
.lca-catico{display:block;flex:none;color:var(--navy)}
.lca-ai{margin:12px 0 0;border:1.5px solid #d5dee3;border-radius:12px;background:#fff}
.lca-ai>summary{list-style:none;display:flex;align-items:center;justify-content:space-between;gap:10px;min-height:56px;padding:10px 14px;cursor:pointer;color:var(--navy)}
.lca-ai>summary::-webkit-details-marker{display:none}
.lca-aitxt{display:flex;flex-direction:column;gap:3px;font-weight:700;font-size:1rem}
.lca-aitxt small{font-weight:400;font-size:.88rem;color:#4a5a66}
.lca-aichev{flex:none;width:10px;height:10px;border-right:2px solid #4a5a66;border-bottom:2px solid #4a5a66;transform:rotate(45deg);margin:-4px 4px 0 0;transition:transform .15s}
.lca-ai[open] .lca-aichev{transform:rotate(-135deg);margin-top:4px}
.lca-ai .lca-tell{padding:0 14px 14px;margin:0}
.lca-ai .lca-tellideas{flex-wrap:wrap;margin:8px 0 10px}
#lake-capture-app #lcAssist .lca-ai .lca-idea{flex:none;white-space:nowrap;min-height:40px}
.lca-ai.is-glow{animation:lcaCustGlow 2.4s ease-out .5s 1 both}
@media (prefers-reduced-motion:reduce){.lca-ai.is-glow{animation:none}}
.lca-buyend{margin:18px 0 6px}
#lake-capture-app #lcAssist .lca-eback{margin:0 0 4px;padding-left:0}
.lca-efoot{margin:18px 0 4px;display:flex;flex-direction:column;align-items:stretch;gap:2px}
.lca-efoot .lca-link{align-self:center}
.lca-more{margin:12px 0 0;border-top:1px solid #e3e9ec}
.lca-more>summary{cursor:pointer;min-height:48px;display:flex;align-items:center;font-size:1rem;font-weight:600;color:var(--navy);text-decoration:underline;text-underline-offset:3px}
.lca-more[open]>summary{text-decoration:none}
#lake-capture-app #lcAssist .lca-row{display:flex;flex-direction:column;align-items:flex-start;gap:2px;width:100%;min-height:56px;margin:0 0 8px;padding:10px 14px;border:1.5px solid #d5dee3;border-radius:12px;background:#fff;color:var(--navy);font:inherit;text-align:left;cursor:pointer;opacity:1}
.lca-row b{font-size:.98rem}
.lca-row small{font-size:.86rem;color:#4a5a66;font-weight:400}
#lake-capture-app #lcAssist .lca-morebtn{margin:8px 0 0;font-weight:600}
.lca-cgrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin:8px 0 0}
.lca-cgrid-all{margin-top:10px}
#lake-capture-app #lcAssist .lca-cgrid .lca-srow-tile{flex:none;min-width:0;width:auto}
#lake-capture-app #lcAssist .lca-cgrid .lca-srow-img{height:auto;aspect-ratio:4/3}
@media (min-width:1025px){.lca-cgrid{grid-template-columns:repeat(3,minmax(0,1fr))}}
@media (max-width:1024px){
  /* One-scroll Step 3: the sticky price bar is fixed to the screen's foot and
     takes no room while hidden; the page gets padding while it shows. */
  #lake-capture-app.lc-flow #lcAssist .lca-sticky{position:fixed;left:0;right:0;bottom:0;margin:0}
  #lake-capture-app.lc-flow.lca-stk-on .ccsc-builder-shell{padding-bottom:calc(76px + env(safe-area-inset-bottom))}
}
/* ── SAVE YOUR DESIGN — 2026-10-12 (owner) ── a bottom sheet on phones, a card
   bottom-right on desktop; the sign stays visible behind it. On body, above
   the fixed shell. */
.lca-save{position:fixed;left:0;right:0;bottom:0;z-index:2147483000;display:flex;justify-content:center;pointer-events:none}
.lca-save[hidden]{display:none}
.lca-save-card{position:relative;pointer-events:auto;width:100%;max-width:520px;margin:0;padding:22px 20px calc(16px + env(safe-area-inset-bottom));background:#fff;color:#012335;border-radius:18px 18px 0 0;box-shadow:0 -8px 30px rgba(1,35,53,.22);font-family:var(--lc-body-font,inherit);animation:lcaSaveUp .28s ease-out}
@keyframes lcaSaveUp{from{transform:translateY(24px);opacity:0}to{transform:none;opacity:1}}
@media (prefers-reduced-motion:reduce){.lca-save-card{animation:none}}
.lca-save-x{position:absolute;top:10px;right:10px;width:40px;height:40px;display:flex;align-items:center;justify-content:center;border:0;border-radius:50%;background:#f1f5f7;color:#012335;cursor:pointer;opacity:1}
.lca-save-title{margin:0 44px 6px 0;font-size:1.3rem;font-weight:800;line-height:1.25}
.lca-save-body{margin:0 0 14px;font-size:1rem;line-height:1.45;color:#33444f}
.lca-save-lbl{display:block;margin:0 0 6px;font-size:.92rem;font-weight:700}
.lca-save input[type=email]{display:block;width:100%;box-sizing:border-box;min-height:50px;padding:10px 14px;border:1.5px solid #c9d4da;border-radius:12px;font:inherit;font-size:16px;color:#012335;background:#fff}
.lca-save input[type=email]:focus{outline:none;border-color:#012335;box-shadow:0 0 0 3px rgba(87,246,177,.45);background:#fff !important;color:#012335 !important}
.lca-save-opt{display:flex;gap:10px;align-items:flex-start;margin:12px 0 0;font-size:.9rem;line-height:1.35;color:#33444f}
.lca-save-opt input{width:20px;height:20px;margin:1px 0 0;flex:none}
.lca-save-err{margin:10px 0 0;color:#b42318;font-size:.92rem}
.lca-save .lca-save-go{display:flex;align-items:center;justify-content:center;width:100%;min-height:52px;margin:14px 0 0;border-radius:12px;font-size:1.05rem;font-weight:700;touch-action:manipulation}
.lca-save-spam{margin-top:-4px;font-size:.92rem;color:#5b6b76}
.lca-mailspam{display:block;margin-top:6px;font-size:.92rem;color:#5b6b76}
.lca-save-fine{margin:10px 0 0;font-size:.82rem;color:#5b6b76;text-align:center}
.lca-save-not{display:block;margin:6px auto 0;min-height:44px;padding:0 14px;border:0;background:none;color:#5b6b76;font:inherit;font-size:.95rem;text-decoration:underline;text-underline-offset:3px;cursor:pointer;opacity:1}
@media (min-width:1025px){
  .lca-save{left:auto;right:24px;bottom:24px;justify-content:flex-end}
  .lca-save-card{width:380px;border-radius:16px;padding:22px 20px 16px;box-shadow:0 12px 40px rgba(1,35,53,.25)}
}
/* The designer nav's Save (lake-capture.js #lcDNavSave): only once a design exists. */
#lake-capture-app .lc-dnav .lc-dnav-save{display:none}
#lake-capture-app.lca-saveable .lc-dnav .lc-dnav-save{display:inline-flex;align-items:center;gap:6px;font-size:.95rem;font-weight:700}
/* ── DESIGNS SCREEN — 2026-10-13 (owner): words above the cards; the last card
   opens every style in the same row; "N of M" once it has. ── */
.lca-wordsrow.lca-wordstop{margin:4px auto 12px}
.lca-browsecard{align-self:stretch;border-style:dashed;border-color:#9fe9cf;background:#f3fdf9;box-shadow:none}
.lca-browsego{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;width:100%;min-height:260px;padding:22px 16px;border:0;background:none;color:var(--navy);font:inherit;text-align:center;cursor:pointer;touch-action:manipulation}
.lca-browsego strong{font-size:1.2rem;line-height:1.25}
.lca-browsego small{font-size:.95rem;color:#4c5d66}
.lca-bswatch{display:grid;grid-template-columns:repeat(3,44px);gap:6px;margin-bottom:6px}
.lca-bswatch img{width:44px;height:44px;border-radius:10px;object-fit:cover;display:block;box-shadow:0 1px 3px rgba(1,35,53,.15)}
.lca-bgo{margin-top:4px;padding:10px 18px;border-radius:999px;background:var(--navy);color:#fff;font-weight:700;font-size:.98rem}
#lake-capture-app #lcAssist .lca-browsego:hover{opacity:1}
.lca-dots.lca-cardcount{display:block;text-align:center;font-size:.9rem;color:#5b6a73;font-weight:600}
@media (min-width:1025px){
  .lca-grid.has-browse{grid-template-columns:repeat(3,minmax(0,1fr)) minmax(0,.62fr)}
  .lca-browsego{min-height:100%}
  .lca-bswatch{grid-template-columns:repeat(2,40px)}.lca-bswatch img{width:40px;height:40px}
  .lca-dots.lca-cardcount{display:none}
}
`;
})();
